import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import SalesAnalyticsView from './SalesAnalyticsView';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';
import {
  HeroKpiRow,
  RevenuePerformanceSection,
  FinancialSnapshotTable,
  RoomsAccommodationSection,
  OutletPulseTable,
  UtilitiesEnergySection,
} from '../components/dashboard/ExecutiveOverviewV2';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, ComposedChart, Area, BarChart, Bar, ReferenceLine, Legend
} from 'recharts';
import {
  CalendarIcon, TrendingUp, TrendingDown, AlertTriangle, DollarSign, ChefHat, Wine,
  Target, Minus, Download, Printer, Filter, BedDouble, ChevronDown, ChevronRight, Info,
  Sparkles, Flame, Award, Zap, Users, Activity, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { downloadCSV, formatDailyExport, formatMTDExport } from '../utils/export';
import { OutletFilter } from '../components/OutletFilter';
import ReportExportModal from '../components/ReportExportModal';
import { addDays, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const fmt = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;

/* ============================================================
   PHASE 2 — Unified Premium Section Header (Feb 2026, iter158)
   Single design language for the executive control center. Used by
   every reporting band on the dashboard so the UI feels designed as
   ONE cohesive system, not assembled widgets.
   • Green band background (var(--color-primary)) with white text.
   • Optional subtitle, right-side meta slot (test counts, scope, etc.).
   • Top + side rounded; child content sits flush below.
   ============================================================ */
function SectionBand({ title, subtitle, right = null, testId, tone = 'primary' }) {
  const bg = tone === 'primary' ? 'bg-[color:var(--color-primary)]'
    : tone === 'soft' ? 'bg-[var(--color-primary-hover)]'
    : 'bg-[color:var(--color-primary)]';
  return (
    <div
      className={`${bg} text-white rounded-t-xl px-4 py-2.5 flex flex-wrap items-center justify-between gap-2`}
      data-testid={testId}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <h3 className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.08em] text-white truncate">
          {title}
        </h3>
        {subtitle && (
          <span className="text-[10px] text-white/70 truncate hidden sm:inline">
            {subtitle}
          </span>
        )}
      </div>
      {right && (
        <div className="text-[10px] text-white/80 flex items-center gap-2 shrink-0">
          {right}
        </div>
      )}
    </div>
  );
}

// Premium section wrapper — green band header + white card body.
// Use this anywhere we want the UNIFIED design language. Avoids ad-hoc styling.
function SectionCard({ title, subtitle, right, testId, children, bandTestId, bodyClassName = '' }) {
  return (
    <div className="mb-3" data-testid={testId}>
      <SectionBand
        title={title}
        subtitle={subtitle}
        right={right}
        testId={bandTestId || `${testId}-band`}
      />
      <div className={`bg-white rounded-b-xl shadow-sm border-x border-b border-[#E2E8DF]/60 ${bodyClassName}`}>
        {children}
      </div>
    </div>
  );
}


/* Single source of truth for portfolio aggregation. Mirrors the backend
   `counts_toward_portfolio` helper: an outlet is EXCLUDED when either
     • hierarchy === 'sub'              (sub-outlet, already rolled up)
     • include_in_totals === false      (explicit opt-out)
   Otherwise it counts. Prevents zero-total and double-count regressions
   even when one of the two flags is missing on legacy records. */
const countsTowardPortfolio = (o) =>
  o && o.hierarchy !== 'sub' && o.include_in_totals !== false;
const CHART_COLORS = ['var(--color-primary)', '#4A7059', '#829C7A', '#C45A41', '#E6A176'];

/* ============================================================
   REDESIGN — Mobile-first Daily/MTD overview components (Feb 2026)
   Shared by both tabs so switching feels identical; only numbers change.
   ============================================================ */

// Smart percentage formatter — 1 decimal max, trailing zero stripped.
//   pctFmt(86.27) → "86.3%"
//   pctFmt(86.0)  → "86%"
//   pctFmt(null)  → "—"
// Optional leading sign for signed variances/YoY (prefixes '+' when positive).
const pctFmt = (v, { signed = false, fallback = '—' } = {}) => {
  if (v == null || isNaN(v)) return fallback;
  const n = Number(v);
  const rounded = Math.round(n * 10) / 10;
  const s = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  const sign = signed && rounded >= 0 ? '+' : '';
  return `${sign}${s}%`;
};

// Insight tags — driven by configurable thresholds (sales variance, NPS, checklist).
// Pure presentation; data calc happens at the row level (caller passes ready vars).
function InsightTags({ tags = [], testId }) {
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {tags.map(t => (
        <span key={t.label}
          className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${t.cls}`}
          title={t.title || ''}
          data-testid={`${testId}-${t.key}`}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

// Compute insight tags for a single row given variance%, NPS, checklist% and thresholds.
function computeInsightTags({ variancePct, nps, checklistPct }, thresholds) {
  const t = thresholds || { sales_variance: { green_above: 5, red_below: -5 }, nps: { green_above: 9, amber_above: 7 }, checklist: { green_above: 95, amber_above: 90 } };
  const tags = [];
  if (variancePct != null) {
    if (variancePct > t.sales_variance.green_above) tags.push({ key: 'top', label: 'Top Performer', cls: 'text-[var(--color-primary-hover)] bg-[#EAF4EE]', title: `Sales +${variancePct.toFixed(1)}% vs budget` });
    else if (variancePct < t.sales_variance.red_below) tags.push({ key: 'low', label: 'Needs Attention', cls: 'text-[#9A3D27] bg-[#FBEBE5]', title: `Sales ${variancePct.toFixed(1)}% vs budget` });
    else tags.push({ key: 'ontrack', label: 'On Track', cls: 'text-[color:var(--color-text-muted)] bg-[#F0F2F0]', title: `Within ±${Math.max(t.sales_variance.green_above, -t.sales_variance.red_below)}% of budget` });
  }
  if (nps != null) {
    if (nps > t.nps.green_above) tags.push({ key: 'nps-strong', label: 'NPS Strong', cls: 'text-[var(--color-primary-hover)] bg-[#EAF4EE]', title: `NPS ${nps}` });
    else if (nps < t.nps.amber_above) tags.push({ key: 'nps-weak', label: 'NPS Weak', cls: 'text-[#9A3D27] bg-[#FBEBE5]', title: `NPS ${nps}` });
  }
  if (checklistPct != null) {
    if (checklistPct < t.checklist.amber_above) tags.push({ key: 'checklist', label: 'Checklist Risk', cls: 'text-[#B88718] bg-[#FCF4E1]', title: `Checklist ${checklistPct}%` });
  }
  return tags;
}

// Hero: horizontal Sales-vs-Budget comparison bar.
// • Budget rendered as full-width grey track.
// • Sales rendered as overlay (green if >= budget, red if below).
// • If sales overflow budget, extension continues past 100% up to 150%.
function HeroSalesVsBudget({ sales, budget, testId = 'hero-sales-budget' }) {
  const s = Number(sales) || 0;
  const b = Number(budget) || 0;
  const above = s >= b;
  const diff = s - b;
  // Scale: 0..max(sales, budget). Budget always fills its % of the scale, sales likewise.
  const scale = Math.max(s, b, 1);
  const budgetPct = (b / scale) * 100;
  const salesPct = (s / scale) * 100;
  const hasData = b > 0 || s > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-2" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)] font-medium mb-1">Sales vs Budget</p>
          <p className="text-[24px] sm:text-[28px] font-semibold text-[color:var(--color-text-primary)] tabular-nums leading-none" data-testid={`${testId}-sales`}>
            {fmt(s)}
          </p>
          <p className="text-xs text-[color:var(--color-text-muted)] mt-1">Budget {fmt(b)}</p>
        </div>
        {hasData && (
          <div className={`text-right ${above ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`} data-testid={`${testId}-delta`}>
            <p className="text-[11px] uppercase tracking-[0.08em] font-medium opacity-80">
              {above ? 'Above Budget' : 'Below Budget'}
            </p>
            <p className="text-xl font-semibold tabular-nums leading-tight">
              {above ? '+' : '−'}{fmt(Math.abs(diff))}
            </p>
          </div>
        )}
      </div>

      {/* Bar: two stacked rows — budget track, sales overlay */}
      <div className="relative h-3 bg-[#EEF1EE] rounded-full overflow-hidden">
        {/* Budget fill (neutral) */}
        <div
          className="absolute inset-y-0 left-0 bg-[#D1D8D3] rounded-full transition-all duration-500"
          style={{ width: `${Math.min(budgetPct, 100)}%` }}
          aria-label="Budget bar"
        />
        {/* Sales overlay */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${above ? 'bg-[#2D6A4F]' : 'bg-[#C45A41]'}`}
          style={{ width: `${Math.min(salesPct, 100)}%` }}
          aria-label="Sales bar"
          data-testid={`${testId}-fill`}
        />
      </div>

      {/* Tick labels */}
      <div className="flex justify-between text-[10px] text-[#9CA3AF] mt-2 tabular-nums">
        <span>₹0</span>
        <span>{fmt(Math.round(scale))}</span>
      </div>
    </div>
  );
}

// 2×2 KPI grid. Uses the same MetricCard but laid out per spec.
function KpiGrid4({ totalSales, totalBudget, variance, reporting, total, testIdPrefix = 'overview' }) {
  const above = variance >= 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3" data-testid={`${testIdPrefix}-kpi-grid`}>
      <div className="bg-white rounded-xl shadow-sm px-3.5 py-2.5" data-testid={`${testIdPrefix}-kpi-sales`}>
        <p className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)] font-medium">Total Net Sales</p>
        <p className="text-[18px] sm:text-[20px] font-semibold text-[color:var(--color-text-primary)] tabular-nums mt-0.5">{fmt(totalSales)}</p>
      </div>
      <div className={`rounded-xl shadow-sm px-3.5 py-2.5 ${above ? 'bg-[#EAF4EE]' : 'bg-[#FBEBE5]'}`} data-testid={`${testIdPrefix}-kpi-variance`}>
        <p className={`text-[10px] uppercase tracking-[0.08em] font-medium ${above ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>Variance</p>
        <p className={`text-[18px] sm:text-[20px] font-semibold tabular-nums mt-0.5 ${above ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
          {above ? '+' : '−'}{fmt(Math.abs(variance))}
        </p>
      </div>
      <div className="bg-white rounded-xl shadow-sm px-3.5 py-2.5" data-testid={`${testIdPrefix}-kpi-budget`}>
        <p className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)] font-medium">Total Budget</p>
        <p className="text-[18px] sm:text-[20px] font-semibold text-[color:var(--color-text-primary)] tabular-nums mt-0.5">{fmt(totalBudget)}</p>
      </div>
      <div className="bg-white rounded-xl shadow-sm px-3.5 py-2.5" data-testid={`${testIdPrefix}-kpi-reporting`}>
        <p className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)] font-medium">Outlets Reporting</p>
        <p className="text-[18px] sm:text-[20px] font-semibold text-[color:var(--color-text-primary)] tabular-nums mt-0.5">{reporting} <span className="text-[#9CA3AF] font-normal">/ {total}</span></p>
      </div>
    </div>
  );
}

// Outlet Performance table — full KPI column set:
// Outlet | F&B Sales | Room Revenue | Total Revenue | LY MTD | YOY % | Budget |
//   FC % | BC % | Covers | Total APC | Days
// Expand arrow reveals ROOMS detail (Sold/Available, Occupancy, ARR).
// `kind` = 'daily' | 'mtd' — used to pick the right field names from the row object.
function OutletPerformanceTable({ outlets, kind = 'daily', testId = 'outlet-performance-table', thresholds = null }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const rows = outlets || [];
  if (rows.length === 0) return null;

  // Field-picker so the same table handles daily and MTD shapes.
  const pick = (o) => kind === 'mtd'
    ? {
        fbSales: o.total_sales,
        roomRev: o.total_room_revenue,
        totalRev: o.total_revenue != null ? o.total_revenue : ((o.total_sales || 0) + (o.total_room_revenue || 0)),
        lyMtd: o.ly_mtd_excel,          // Prior-year MTD elapsed baseline
        lyCompareBase: o.ly_mtd_excel,  // YoY denominator
        budget: (o.total_budget || 0) + (o.total_room_budget || 0),
        fcPct: o.avg_food_cost_pct,
        bcPct: o.avg_beverage_cost_pct,
        covers: o.total_covers,
        apc: o.total_apc,
        days: o.days_reported,
        roomsSold: o.total_rooms_sold,
        roomsAvail: o.total_rooms_available,
        occupancy: o.avg_occupancy_pct,
        arr: o.avg_arr,
        reported: (o.days_reported || 0) > 0,
      }
    : {
        fbSales: o.net_sales,
        roomRev: o.room_revenue,
        totalRev: o.total_revenue != null ? o.total_revenue : ((o.net_sales || 0) + (o.room_revenue || 0)),
        lyMtd: o.ly_mtd_total,                // LY MTD through same elapsed date
        lyCompareBase: o.ly_same_day_sales,   // Daily YoY compares vs LY same-day (meaningful for a single day)
        budget: (o.budget || 0) + (o.room_revenue_budget || 0),
        fcPct: o.food_cost_pct,
        bcPct: o.beverage_cost_pct,
        covers: o.number_of_covers,
        apc: o.total_apc,
        days: o.has_entry ? 1 : 0,
        roomsSold: o.rooms_sold,
        roomsAvail: o.rooms_available,
        occupancy: o.occupancy_pct,
        arr: o.arr,
        reported: !!o.has_entry,
      };

  // Aggregation pool (sub-outlets + opted-out excluded, entry-less excluded).
  const totalsRows = rows.filter(o => {
    if (!countsTowardPortfolio(o)) return false;
    return pick(o).reported;
  });
  const totals = totalsRows.reduce((acc, o) => {
    const p = pick(o);
    acc.fbSales += Number(p.fbSales) || 0;
    acc.roomRev += Number(p.roomRev) || 0;
    acc.totalRev += Number(p.totalRev) || 0;
    acc.lyMtd += Number(p.lyMtd) || 0;
    acc.lyCompareBase += Number(p.lyCompareBase) || 0;
    acc.budget += Number(p.budget) || 0;
    acc.covers += Number(p.covers) || 0;
    acc.days = Math.max(acc.days, Number(p.days) || 0);
    return acc;
  }, { fbSales: 0, roomRev: 0, totalRev: 0, lyMtd: 0, lyCompareBase: 0, budget: 0, covers: 0, days: 0 });
  const totalYoyPct = totals.lyCompareBase > 0
    ? (((totals.totalRev - totals.lyCompareBase) / totals.lyCompareBase) * 100)
    : null;
  const totalApc = totals.covers > 0 ? (totals.fbSales / totals.covers) : null;
  const totalVarAmt = totals.totalRev - totals.budget;
  const totalVarPct = totals.budget > 0
    ? (((totals.totalRev - totals.budget) / totals.budget) * 100)
    : null;

  const em = <span className="text-[#9CA3AF]">—</span>;
  const pending = <span className="text-[#9CA3AF] text-[11px] italic">Pending</span>;

  // (Per-cell sticky helper was an earlier approach; kept here only for
  // documentation. The current pattern uses a constrained-height scroll
  // container with sticky `top-0` on `<thead>` — see below.)
  const th = 'px-2.5 py-2.5 font-semibold whitespace-nowrap';
  const td = 'px-2.5 py-2.5 whitespace-nowrap';

  return (
    <SectionCard
      title="Outlet Performance"
      subtitle="Primary operational table · sub-units roll up to parent"
      right={<span className="hidden sm:inline">Scroll horizontally to see all KPIs →</span>}
      testId={testId}
      bandTestId={`${testId}-header`}
      bodyClassName="p-0"
    >
      <div className="overflow-auto max-h-[calc(100vh-160px)]">
        <table className="w-full text-sm text-left text-[color:var(--color-text-primary)]">
          {/* Sticky thead — pins to TOP of the scrollable table container.
              The wrapper has a viewport-relative max-height so it becomes
              its OWN scroll context; sticky inside that container works
              reliably across browsers (Chrome / Firefox / Safari) and
              survives both horizontal and vertical scrolling. */}
          <thead className="sticky top-0 z-30 shadow-[0_1px_0_rgba(20,61,48,0.15)]">
            <tr className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)] bg-[#F0F4F1] border-b-2 border-[color:var(--color-primary)]/15">
              <th className={`${th} w-6 bg-[#F0F4F1]`}></th>
              <th className={`${th} sticky left-0 bg-[#F0F4F1] z-10`}>Outlet</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>F&B Sales</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Room Revenue</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Total Revenue</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Budget</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Variance ₹</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Variance %</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>LY MTD</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>YOY %</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>FC %</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>BC %</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Covers</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>APC</th>
              <th className={`${th} text-right bg-[#F0F4F1]`}>Days</th>
              <th className={`${th} bg-[#F0F4F1]`}>Insights</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o, i) => {
              const p = pick(o);
              const yoyPct = p.lyCompareBase > 0
                ? (((p.totalRev - p.lyCompareBase) / p.lyCompareBase) * 100)
                : null;
              const yoyPositive = yoyPct == null ? null : yoyPct >= 0;
              const variancePct = p.budget > 0 ? (((p.totalRev - p.budget) / p.budget) * 100) : null;
              const tags = p.reported ? computeInsightTags({ variancePct, nps: o.nps_score, checklistPct: o.checklist_pct }, thresholds) : [];
              return (
                <Fragment key={o.outlet_id}>
                  <tr className={`border-b border-[#EEF1EE] transition-colors hover:bg-[#EAF4EE]/60 ${!p.reported ? 'opacity-60' : ''} ${o.hierarchy === 'sub' ? 'bg-[#FAFBFA]' : ''}`}
                    data-testid={`${testId}-row-${o.outlet_id}`}>
                    <td className={td}>
                      <button onClick={() => toggle(o.outlet_id)}
                        className="text-[#9CA3AF] hover:text-[color:var(--color-text-primary)]"
                        aria-label="Toggle details"
                        data-testid={`${testId}-expand-${o.outlet_id}`}>
                        {expanded[o.outlet_id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className={`${td} font-semibold sticky left-0 z-10 ${o.hierarchy === 'sub' ? 'bg-[#FAFBFA]' : 'bg-white'}`}>
                      <span className={`flex items-center gap-1.5 flex-wrap ${o.hierarchy === 'sub' ? 'pl-5 relative' : ''}`}>
                        {o.hierarchy === 'sub' && (
                          <span className="absolute left-1 top-0 bottom-0 flex items-center text-[#9CA3AF]" aria-hidden>
                            <span className="w-2 border-t border-[#C4D0CB] mr-0.5" />
                          </span>
                        )}
                        <span className={o.hierarchy === 'sub' ? 'text-[color:var(--color-text-muted)] text-[13px]' : 'text-[color:var(--color-primary)]'}>{o.outlet_name}</span>
                        {o.hierarchy === 'sub' && <span className="text-[9px] font-semibold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F0F2F0] px-1.5 py-0.5 rounded" data-testid={`${testId}-sub-${o.outlet_id}`}>Sub</span>}
                        {o.include_in_totals === false && <span className="text-[9px] font-semibold uppercase tracking-wider text-[#B88718] bg-[#FCF4E1] px-1.5 py-0.5 rounded" title="Excluded from portfolio totals">Excl</span>}
                        {!p.reported && <span className="text-[9px] font-semibold uppercase tracking-wider text-[#9CA3AF] bg-[#F0F2F0] px-1.5 py-0.5 rounded">Not reported</span>}
                      </span>
                    </td>
                    <td className={`${td} text-right tabular-nums`}>{p.reported ? fmt(p.fbSales) : em}</td>
                    <td className={`${td} text-right tabular-nums`}>{p.reported && p.roomRev ? fmt(p.roomRev) : em}</td>
                    <td className={`${td} text-right font-bold text-[color:var(--color-primary)] tabular-nums`}>{p.reported ? fmt(p.totalRev) : em}</td>
                    <td className={`${td} text-right tabular-nums text-[color:var(--color-text-muted)]`}>{p.reported ? fmt(p.budget) : em}</td>
                    <td className={`${td} text-right`}>
                      {!p.reported || p.budget <= 0 ? em : (
                        <span className={`font-semibold tabular-nums ${(p.totalRev - p.budget) >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                          data-testid={`${testId}-var-amt-${o.outlet_id}`}>
                          {(p.totalRev - p.budget) >= 0 ? '+' : ''}{fmt(p.totalRev - p.budget)}
                        </span>
                      )}
                    </td>
                    <td className={`${td} text-right`}>
                      {variancePct != null ? (
                        <span className={`font-semibold tabular-nums ${variancePct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                          data-testid={`${testId}-var-pct-${o.outlet_id}`}>
                          {pctFmt(variancePct, { signed: true })}
                        </span>
                      ) : em}
                    </td>
                    <td className={`${td} text-right tabular-nums text-[color:var(--color-text-muted)]`}>{p.lyMtd ? fmt(p.lyMtd) : em}</td>
                    <td className={`${td} text-right`}>
                      {yoyPct != null ? (
                        <span className={`font-semibold tabular-nums ${yoyPositive ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                          data-testid={`${testId}-yoy-${o.outlet_id}`}>
                          {pctFmt(yoyPct, { signed: true })}
                        </span>
                      ) : em}
                    </td>
                    <td className={`${td} text-right`}>
                      {!p.reported ? em : p.fcPct != null ? (
                        <span className={`tabular-nums ${p.fcPct > 35 ? 'text-[#C45A41] font-medium' : ''}`}>{pctFmt(p.fcPct)}</span>
                      ) : pending}
                    </td>
                    <td className={`${td} text-right`}>
                      {!p.reported ? em : p.bcPct != null ? (
                        <span className={`tabular-nums ${p.bcPct > 30 ? 'text-[#C45A41] font-medium' : ''}`}>{pctFmt(p.bcPct)}</span>
                      ) : pending}
                    </td>
                    <td className={`${td} text-right tabular-nums`}>{p.reported ? (p.covers || em) : em}</td>
                    <td className={`${td} text-right tabular-nums`}>{p.reported && p.apc ? fmt(p.apc) : em}</td>
                    <td className={`${td} text-right tabular-nums text-[color:var(--color-text-muted)]`}>{p.days || em}</td>
                    <td className={td}>
                      <InsightTags tags={tags} testId={`${testId}-tags-${o.outlet_id}`} />
                    </td>
                  </tr>
                  {expanded[o.outlet_id] && (
                    <tr className="bg-[#FAFBFA]" data-testid={`${testId}-detail-${o.outlet_id}`}>
                      <td colSpan={16} className="px-6 py-3">
                        {(p.roomsSold > 0 || p.roomsAvail > 0 || p.roomRev > 0) ? (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)]">Rooms Sold / Available</p>
                              <p className="font-semibold mt-0.5">{p.roomsSold || 0} / {p.roomsAvail || 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)]">Occupancy</p>
                              <p className="font-semibold mt-0.5">{p.occupancy != null ? `${p.occupancy}%` : em}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)]">ARR</p>
                              <p className="font-semibold mt-0.5">{p.arr ? fmt(p.arr) : em}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)]">Room Revenue</p>
                              <p className="font-semibold mt-0.5">{p.roomRev ? fmt(p.roomRev) : em}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-[#9CA3AF] italic">No room data for this outlet.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gradient-to-b from-[var(--color-primary)]/[0.06] to-[var(--color-primary)]/[0.10] border-t-[3px] border-[color:var(--color-primary)] font-semibold"
              data-testid={`${testId}-totals`}>
              <td className={td}></td>
              <td className={`${td} font-bold text-[color:var(--color-primary)] sticky left-0 bg-[#EEF4EF] z-10 text-[12px] uppercase tracking-[0.05em]`}>Portfolio Total</td>
              <td className={`${td} text-right tabular-nums font-bold`}>{fmt(totals.fbSales)}</td>
              <td className={`${td} text-right tabular-nums font-bold`}>{totals.roomRev ? fmt(totals.roomRev) : em}</td>
              <td className={`${td} text-right tabular-nums font-bold text-[14px] text-[color:var(--color-primary)]`}>{fmt(totals.totalRev)}</td>
              <td className={`${td} text-right tabular-nums font-semibold text-[color:var(--color-text-muted)]`}>{fmt(totals.budget)}</td>
              <td className={`${td} text-right`}>
                {totals.budget > 0 ? (
                  <span className={`font-bold tabular-nums ${totalVarAmt >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                    data-testid={`${testId}-totals-var-amt`}>
                    {totalVarAmt >= 0 ? '+' : ''}{fmt(totalVarAmt)}
                  </span>
                ) : em}
              </td>
              <td className={`${td} text-right`}>
                {totalVarPct != null ? (
                  <span className={`font-bold tabular-nums ${totalVarPct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                    data-testid={`${testId}-totals-var-pct`}>
                    {pctFmt(totalVarPct, { signed: true })}
                  </span>
                ) : em}
              </td>
              <td className={`${td} text-right tabular-nums text-[color:var(--color-text-muted)]`}>{totals.lyMtd ? fmt(totals.lyMtd) : em}</td>
              <td className={`${td} text-right`}>
                {totalYoyPct != null ? (
                  <span className={`font-bold tabular-nums ${totalYoyPct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                    {pctFmt(totalYoyPct, { signed: true })}
                  </span>
                ) : em}
              </td>
              <td className={td}></td>
              <td className={td}></td>
              <td className={`${td} text-right tabular-nums font-bold`}>{totals.covers || em}</td>
              <td className={`${td} text-right tabular-nums font-bold`}>{totalApc ? fmt(totalApc) : em}</td>
              <td className={`${td} text-right tabular-nums text-[color:var(--color-text-muted)] font-bold`}>{totals.days || em}</td>
              <td className={td}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </SectionCard>
  );
}

// Product Mix (Food vs Bev) doughnut — secondary section.
// Lunch vs Dinner shown as compact stat text on the right.
// Two-column doughnut: Product Mix (Food vs Bev) | Service Split (Lunch vs Dinner).
// Same card, two equally sized doughnuts side-by-side; stacks on mobile.
function MixDoughnutSection({
  foodPct, bevPct, foodSale, bevSale,
  lunchPct, dinnerPct, lunchSales, dinnerSales,
  channelDineIn = 0, channelSwiggy = 0, channelZomato = 0, channelOther = 0,
  testId = 'mix-doughnut-section',
}) {
  const hasMix = (Number(foodSale) || 0) + (Number(bevSale) || 0) > 0;
  const hasSplit = (Number(lunchSales) || 0) + (Number(dinnerSales) || 0) > 0;
  const channelTotal = (Number(channelDineIn) || 0) + (Number(channelSwiggy) || 0) + (Number(channelZomato) || 0) + (Number(channelOther) || 0);
  const hasChannel = channelTotal > 0;
  if (!hasMix && !hasSplit && !hasChannel) return null;

  const mixData = hasMix ? [
    { name: 'Food', value: Number(foodSale) || 0, color: '#2D6A4F' },
    { name: 'Beverage', value: Number(bevSale) || 0, color: '#C89455' },
  ].filter(d => d.value > 0) : [];
  const splitData = hasSplit ? [
    { name: 'Lunch', value: Number(lunchSales) || 0, color: 'var(--color-primary-hover)' },
    { name: 'Dinner', value: Number(dinnerSales) || 0, color: '#B88718' },
  ].filter(d => d.value > 0) : [];
  // Channel palette — chosen to differ from Product/Service while staying
  // within the dashboard's restrained earthy/forest tone. Order is fixed so
  // legend reading is consistent across outlets.
  const channelData = hasChannel ? [
    { name: 'Dine-in', value: Number(channelDineIn) || 0, color: 'var(--color-primary)' },
    { name: 'Swiggy', value: Number(channelSwiggy) || 0, color: '#C45A41' },
    { name: 'Zomato', value: Number(channelZomato) || 0, color: '#E6A176' },
    { name: 'Other', value: Number(channelOther) || 0, color: '#829C7A' },
  ].filter(d => d.value > 0) : [];
  const dominantChannel = channelData.length
    ? channelData.reduce((a, b) => (b.value > a.value ? b : a))
    : null;
  const dominantPct = (hasChannel && dominantChannel)
    ? (dominantChannel.value / channelTotal) * 100
    : null;

  const Doughnut = ({ title, centerLabel, centerValue, data, legend, testSuffix }) => (
    <div className="flex flex-col items-center text-center" data-testid={`${testId}-${testSuffix}`}>
      <p className="text-[11px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)] font-medium mb-3">{title}</p>
      <div className="w-[144px] h-[144px] relative">
        {data.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name"
                  innerRadius={44} outerRadius={66} paddingAngle={2} stroke="none">
                  {data.map(d => <Cell key={d.name} fill={d.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-[9px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)]">{centerLabel}</p>
              <p className="text-xl font-semibold text-[color:var(--color-text-primary)] tabular-nums">{centerValue}</p>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-[#9CA3AF]">No data</div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center mt-4">
        {legend.map(l => (
          <div key={l.label} className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: l.color }} />
            <span className="text-[color:var(--color-text-primary)] font-medium">{l.label}</span>
            <span className="text-[color:var(--color-text-muted)] tabular-nums">{l.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <SectionCard
      title="Distribution & Mix"
      subtitle="Product · Service · Channel"
      testId={testId}
      bandTestId={`${testId}-header`}
      bodyClassName="p-4"
    >
      <div className={`grid grid-cols-1 ${hasChannel ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-5 items-start`}>
        <Doughnut
          title="Product Mix"
          centerLabel="Food"
          centerValue={pctFmt(foodPct)}
          data={mixData}
          legend={[
            { label: `Food ${pctFmt(foodPct)}`, value: fmt(foodSale), color: '#2D6A4F' },
            { label: `Bev ${pctFmt(bevPct)}`, value: fmt(bevSale), color: '#C89455' },
          ]}
          testSuffix="product-mix"
        />
        <Doughnut
          title="Service Split"
          centerLabel="Lunch"
          centerValue={pctFmt(lunchPct)}
          data={splitData}
          legend={[
            { label: `Lunch ${pctFmt(lunchPct)}`, value: fmt(lunchSales), color: 'var(--color-primary-hover)' },
            { label: `Dinner ${pctFmt(dinnerPct)}`, value: fmt(dinnerSales), color: '#B88718' },
          ]}
          testSuffix="service-split"
        />
        {hasChannel && (
          <Doughnut
            title="Channel Split"
            centerLabel={dominantChannel ? dominantChannel.name : '—'}
            centerValue={dominantPct != null ? pctFmt(dominantPct) : '—'}
            data={channelData}
            legend={channelData.map(c => ({
              label: `${c.name} ${pctFmt(channelTotal > 0 ? (c.value / channelTotal) * 100 : 0)}`,
              value: fmt(c.value),
              color: c.color,
            }))}
            testSuffix="channel-split"
          />
        )}
      </div>
    </SectionCard>
  );
}

// MTD Daily Sales Trend chart — full-width line chart shown above the
// Outlet Performance table. Reuses the `daily_trend` array already provided
// by /api/dashboard/mtd. Each daily_trend entry carries the canonical
// `cumulative_actual` + `cumulative_budget` values (computed server-side
// in /dashboard/mtd) so frontend never re-aggregates. Cumulative pacing
// stops at `data_through` — matching elapsed-only reality.
function MtdSalesTrendChart({
  dailyTrend,
  totalBudget,
  totalActual,
  daysInMonth,
  daysElapsed,
  testId = 'mtd-sales-trend-chart',
}) {
  if (!dailyTrend || dailyTrend.length === 0) return null;

  const reportedDays = dailyTrend.filter(d => Number(d.total_net_sales) > 0);
  const variancePct = totalBudget > 0 ? (((totalActual - totalBudget) / totalBudget) * 100) : null;
  const avgDaily = reportedDays.length > 0
    ? reportedDays.reduce((s, d) => s + Number(d.total_net_sales || 0), 0) / reportedDays.length
    : 0;
  const best = reportedDays.length
    ? reportedDays.reduce((a, b) => (Number(b.total_net_sales) > Number(a.total_net_sales) ? b : a))
    : null;
  const lowest = reportedDays.length
    ? reportedDays.reduce((a, b) => (Number(b.total_net_sales) < Number(a.total_net_sales) ? b : a))
    : null;

  // CUMULATIVE chart series — Actual sales running total vs Budget pace
  // running total. Both stop at `data_through` (the last day with reported
  // data), so we never paint a fake budget expectation beyond elapsed time.
  const chartData = dailyTrend.map(d => ({
    day: d.date.slice(8),
    date: d.date,
    cumulativeActual: Number(d.cumulative_actual) || 0,
    cumulativeBudget: Number(d.cumulative_budget) || 0,
  }));

  const fmtD = (v) => v == null ? '—' : `₹${Math.round(Number(v)).toLocaleString('en-IN')}`;
  const dayLabel = (d) => d ? d.date.slice(5) : '—';

  // Custom tooltip: shows cumulative actual, cumulative budget pace,
  // variance % vs pace for that day. Premium boardroom styling.
  const TooltipRender = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const a = payload.find(p => p.dataKey === 'cumulativeActual')?.value;
    const b = payload.find(p => p.dataKey === 'cumulativeBudget')?.value;
    const vPct = (a != null && b > 0) ? (((a - b) / b) * 100) : null;
    return (
      <div className="bg-white border border-[#E2E8DF] rounded-md shadow-lg px-3.5 py-2.5 text-[11px] min-w-[170px]"
        data-testid={`${testId}-tooltip`}>
        <p className="font-semibold text-white bg-[color:var(--color-primary)] -mx-3.5 -mt-2.5 px-3.5 py-1.5 mb-2 rounded-t-md text-[10.5px] uppercase tracking-[0.06em]">
          Day {label} · Cumulative
        </p>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[color:var(--color-text-muted)]">Actual</span>
            <span className="text-[color:var(--color-primary)] font-semibold tabular-nums">{fmtD(a)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[color:var(--color-text-muted)]">Budget pace</span>
            <span className="text-[color:var(--color-text-muted)] font-medium tabular-nums">{fmtD(b)}</span>
          </div>
          {vPct != null && (
            <div className="flex items-center justify-between gap-3 pt-1 mt-1 border-t border-[#EEF1EE]">
              <span className="text-[color:var(--color-text-muted)]">vs pace</span>
              <span className={`font-semibold tabular-nums ${vPct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                {pctFmt(vPct, { signed: true })}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const chip = (label, value, sub, key) => (
    <div key={key} className="flex flex-col rounded-md border border-[#E2E8DF] bg-white px-3 py-1.5 min-w-[110px]"
      data-testid={`${testId}-chip-${key}`}>
      <span className="text-[9px] uppercase tracking-[0.08em] text-[color:var(--color-text-muted)]">{label}</span>
      <span className="text-xs font-semibold text-[color:var(--color-text-primary)] tabular-nums leading-tight mt-0.5">{value}</span>
      {sub ? <span className="text-[9px] text-[#9CA3AF] leading-tight">{sub}</span> : null}
    </div>
  );

  // Pace status — for conditional band tone (green if ahead, red if behind).
  const paceStatus = variancePct == null ? null : (variancePct >= 0 ? 'ahead' : 'behind');
  const paceColor = paceStatus === 'ahead' ? '#2D6A4F' : paceStatus === 'behind' ? '#C45A41' : 'var(--color-primary)';
  // Subtle area fill under the actual line for visual weight without flashiness.
  const fillId = `${testId}-area-fill`;

  return (
    <SectionCard
      title="Cumulative Sales vs Budget Pace"
      subtitle="Running totals through reported days only"
      testId={testId}
      bandTestId={`${testId}-header`}
      bodyClassName="p-0"
    >
      <div className="px-4 pt-3 pb-1 flex flex-wrap items-stretch gap-1.5">
        {chip('MTD Actual', fmtD(totalActual), null, 'actual')}
        {chip('MTD Budget', fmtD(totalBudget), null, 'budget')}
        {chip(
          'Variance',
          variancePct == null
            ? '—'
            : <span className={variancePct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}>{pctFmt(variancePct, { signed: true })}</span>,
          paceStatus === 'ahead' ? 'Ahead of pace' : paceStatus === 'behind' ? 'Behind pace' : null,
          'variance',
        )}
        {chip('Best Day', best ? fmtD(best.total_net_sales) : '—', best ? dayLabel(best) : null, 'best')}
        {chip('Lowest Day', lowest ? fmtD(lowest.total_net_sales) : '—', lowest ? dayLabel(lowest) : null, 'lowest')}
        {chip('Avg / Day', fmtD(avgDaily), daysElapsed ? `${reportedDays.length}d reported` : null, 'avg')}
      </div>
      <div className="px-3 pb-3 pt-1">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 12, right: 24, left: 0, bottom: 4 }}>
            <defs>
              <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={paceColor} stopOpacity={0.16} />
                <stop offset="100%" stopColor={paceColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAEAEA" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#5C6B64' }}
              axisLine={{ stroke: '#E2E8DF' }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#5C6B64' }}
              axisLine={false} tickLine={false}
              tickFormatter={(v) => v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : v} />
            <Tooltip content={<TooltipRender />} cursor={{ stroke: 'var(--color-primary)', strokeWidth: 1, strokeOpacity: 0.15 }} />
            <Line type="monotone" dataKey="cumulativeBudget" name="Cumulative Budget Pace"
              stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="5 4"
              dot={false} activeDot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="cumulativeActual" name="Cumulative Actual"
              stroke={paceColor} strokeWidth={2.25}
              dot={{ r: 2.5, fill: paceColor, strokeWidth: 0 }}
              activeDot={{ r: 5, stroke: paceColor, strokeWidth: 1.5, fill: '#fff' }}
              connectNulls={false}
              fill={`url(#${fillId})`}
              isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 px-1 mt-1.5 flex-wrap">
          <span className="flex items-center gap-1.5 text-[11px] text-[color:var(--color-text-muted)]">
            <span className="inline-block w-3.5 h-[2.25px] rounded-full" style={{ background: paceColor }} />
            Cumulative actual
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-[color:var(--color-text-muted)]">
            <span className="inline-block w-3.5 border-t-[1.5px] border-dashed border-[#9CA3AF]" />
            Cumulative budget pace
          </span>
          {paceStatus && (
            <span className={`ml-auto text-[10px] uppercase tracking-[0.08em] font-semibold px-2 py-0.5 rounded-full ${
              paceStatus === 'ahead'
                ? 'bg-[#EAF4EE] text-[var(--color-primary-hover)]'
                : 'bg-[#FBEBE5] text-[#9A3D27]'
            }`} data-testid={`${testId}-pace-badge`}>
              {paceStatus === 'ahead' ? 'Ahead of pace' : 'Behind pace'}
            </span>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────
// InsightStripCards — compact 4-card strip replacing DataHealthCard.
// Tells the executive story in 2 seconds: vs LY benchmark + winners +
// laggards. Reads from per-outlet rows + portfolio_totals; NEVER
// re-aggregates revenue (those come from backend canonical totals).
// ─────────────────────────────────────────────────────────────────────
function InsightStripCards({
  kind,               // 'daily' | 'mtd'
  outlets = [],
  portfolioTotals = null,
  testId = 'insight-strip',
}) {
  if (!outlets || outlets.length === 0) return null;
  // Pool: only outlets that count toward portfolio AND have data we care about.
  const pool = outlets.filter(o => {
    if (!countsTowardPortfolio(o)) return false;
    return kind === 'daily' ? o.has_entry : (o.days_reported || 0) > 0;
  });
  if (pool.length === 0) return null;

  // Field accessors per `kind`.
  const f = (o) => kind === 'daily'
    ? {
        revenue: (Number(o.net_sales) || 0) + (Number(o.room_revenue) || 0),
        budget: (Number(o.budget) || 0) + (Number(o.room_revenue_budget) || 0),
        ly: o.ly_same_day_sales,
      }
    : {
        revenue: (Number(o.total_sales) || 0) + (Number(o.total_room_revenue) || 0),
        budget: (Number(o.total_budget) || 0) + (Number(o.total_room_budget) || 0),
        ly: o.ly_mtd_excel,
      };

  // Per-outlet variance vs budget → rank for top / weakest.
  const ranked = pool
    .map(o => {
      const x = f(o);
      const varPct = x.budget > 0 ? ((x.revenue - x.budget) / x.budget * 100) : null;
      return { name: o.outlet_name, revenue: x.revenue, budget: x.budget, varPct };
    })
    .filter(r => r.varPct != null)
    .sort((a, b) => b.varPct - a.varPct);
  const top = ranked[0] || null;
  const weakest = ranked.length > 1 ? ranked[ranked.length - 1] : null;

  // LY comparison — uses portfolio totals first, falls back to summing per row.
  const totalRev = portfolioTotals
    ? (portfolioTotals.total_revenue || 0)
    : pool.reduce((s, o) => s + f(o).revenue, 0);
  const totalLy = pool.reduce((s, o) => {
    const v = f(o).ly;
    return s + (v == null ? 0 : Number(v));
  }, 0);
  const lyDelta = totalLy > 0 ? ((totalRev - totalLy) / totalLy * 100) : null;
  const lyAbs = totalRev - totalLy;

  // Variance vs budget (portfolio).
  const portBudget = portfolioTotals ? (portfolioTotals.budget || 0) : 0;
  const portVarPct = portBudget > 0 ? ((totalRev - portBudget) / portBudget * 100) : null;
  const portVarAmt = portBudget > 0 ? (totalRev - portBudget) : null;

  // Highest APC per outlet — uses canonical APC if present, else derived.
  const apcRanked = pool
    .map(o => {
      const apc = o.total_apc != null
        ? Number(o.total_apc)
        : (kind === 'daily'
            ? (Number(o.number_of_covers) > 0 ? Number(o.net_sales) / Number(o.number_of_covers) : null)
            : (Number(o.total_covers) > 0 ? Number(o.total_sales) / Number(o.total_covers) : null));
      return { name: o.outlet_name, apc };
    })
    .filter(r => r.apc != null && r.apc > 0)
    .sort((a, b) => b.apc - a.apc);
  const topApc = apcRanked[0] || null;

  // Best Food Cost % — lowest FC % wins (only outlets with FC% reported).
  const fcKey = kind === 'daily' ? 'food_cost_pct' : 'avg_food_cost_pct';
  const fcRanked = pool
    .map(o => ({ name: o.outlet_name, fc: o[fcKey] != null ? Number(o[fcKey]) : null }))
    .filter(r => r.fc != null && r.fc > 0)
    .sort((a, b) => a.fc - b.fc);
  const bestFc = fcRanked[0] || null;

  // Card UI — richer with contextual insight line + value highlight.
  const Card = ({ label, value, sub, insight, tone = 'neutral', tid }) => {
    const toneClass = tone === 'up' ? 'text-[#2D6A4F]'
      : tone === 'down' ? 'text-[#C45A41]'
      : 'text-[color:var(--color-text-primary)]';
    const insightBg = tone === 'up' ? 'bg-[#EAF4EE] text-[var(--color-primary-hover)]'
      : tone === 'down' ? 'bg-[#FBEBE5] text-[#9A3D27]'
      : 'bg-[#F0F2F0] text-[color:var(--color-text-muted)]';
    return (
      <div
        className="rounded-lg border border-[#E2E8DF] bg-white px-3.5 py-3 flex flex-col gap-1 min-w-0 hover:shadow-md hover:border-[color:var(--color-primary)]/20 transition-all"
        data-testid={tid}
      >
        <span className="text-[9.5px] uppercase tracking-[0.12em] text-[color:var(--color-text-muted)] font-semibold">{label}</span>
        <span className={`text-[15px] font-bold tabular-nums truncate ${toneClass}`}>{value}</span>
        {sub && <span className="text-[10.5px] text-[#9CA3AF] truncate">{sub}</span>}
        {insight && (
          <span className={`mt-0.5 inline-flex self-start text-[9.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded ${insightBg}`}>
            {insight}
          </span>
        )}
      </div>
    );
  };

  const lyLabel = kind === 'daily' ? 'Today vs LY Same Day' : 'MTD vs LY MTD';
  const periodLabel = kind === 'daily' ? 'Today' : 'MTD';

  // Contextual insights — short, premium, narrative.
  const lyInsight = lyDelta == null ? null
    : lyDelta > 10 ? 'Strong YoY growth'
    : lyDelta > 0 ? 'Positive YoY'
    : lyDelta > -10 ? 'Slight YoY dip'
    : 'YoY decline';
  const budgetInsight = portVarPct == null ? null
    : portVarPct > 10 ? 'Comfortably ahead'
    : portVarPct > 0 ? 'On target'
    : portVarPct > -10 ? 'Close to target'
    : 'Behind plan';

  return (
    <SectionCard
      title="Executive Insights"
      subtitle="Story behind the numbers"
      testId={testId}
      bandTestId={`${testId}-header`}
      bodyClassName="p-3"
    >
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2.5">
        <Card
          label={lyLabel}
          value={lyDelta == null
            ? '—'
            : `${pctFmt(lyDelta, { signed: true })}`}
          sub={lyDelta == null
            ? 'No LY benchmark'
            : `${fmt(totalRev)} vs ${fmt(totalLy)}`}
          insight={lyInsight}
          tone={lyDelta == null ? 'neutral' : (lyDelta >= 0 ? 'up' : 'down')}
          tid={`${testId}-ly-compare`}
        />
        <Card
          label={`${periodLabel} vs Budget`}
          value={portVarPct == null
            ? '—'
            : `${pctFmt(portVarPct, { signed: true })}`}
          sub={portVarAmt == null
            ? 'No budget set'
            : `${portVarAmt >= 0 ? '+' : ''}${fmt(portVarAmt)}`}
          insight={budgetInsight}
          tone={portVarPct == null ? 'neutral' : (portVarPct >= 0 ? 'up' : 'down')}
          tid={`${testId}-budget-variance`}
        />
        <Card
          label="Top Performer"
          value={top ? top.name : '—'}
          sub={top ? `${pctFmt(top.varPct, { signed: true })} vs budget` : 'No data'}
          insight={top && top.varPct >= 20 ? 'Leading the portfolio' : top && top.varPct >= 0 ? 'Above plan' : null}
          tone={top && top.varPct >= 0 ? 'up' : 'neutral'}
          tid={`${testId}-top-performer`}
        />
        <Card
          label="Weakest Outlet"
          value={weakest ? weakest.name : '—'}
          sub={weakest ? `${pctFmt(weakest.varPct, { signed: true })} vs budget` : 'No data'}
          insight={weakest && weakest.varPct < -10 ? 'Needs attention' : weakest && weakest.varPct < 0 ? 'Trailing pack' : null}
          tone={weakest && weakest.varPct < 0 ? 'down' : 'neutral'}
          tid={`${testId}-weakest-outlet`}
        />
        <Card
          label="Highest APC"
          value={topApc ? topApc.name : '—'}
          sub={topApc ? `${fmt(Math.round(topApc.apc))} avg per cover` : 'No covers data'}
          insight={topApc && topApc.apc >= 500 ? 'Premium spend' : topApc ? 'Best ticket size' : null}
          tone={topApc ? 'up' : 'neutral'}
          tid={`${testId}-highest-apc`}
        />
        <Card
          label="Best Food Cost"
          value={bestFc ? bestFc.name : '—'}
          sub={bestFc ? `${pctFmt(bestFc.fc)} food cost` : 'No FC data'}
          insight={bestFc && bestFc.fc <= 25 ? 'Excellent control' : bestFc && bestFc.fc <= 32 ? 'Healthy margin' : null}
          tone={bestFc ? 'up' : 'neutral'}
          tid={`${testId}-best-fc`}
        />
      </div>
    </SectionCard>
  );
}



// Compact one-line context strip — shown above the Sales-vs-Budget hero.
// Replaces the older 3-line header (title + subtitle + updated). Active tab
// already conveys the period type (Daily/MTD/YTD), so we drop the redundant
// title and keep just the operational meta: "{period} • {n}/{total} outlets
// reported • Updated h:mm a".
function OverviewHeader({ title, subtitle, testId = 'overview-header' }) {
  const [now] = useState(() => new Date());
  const updated = format(now, "h:mm a");
  return (
    <div
      className="flex items-center gap-2 flex-wrap text-[11px] text-[color:var(--color-text-muted)] mb-2"
      data-testid={testId}
    >
      {subtitle && <span data-testid={`${testId}-subtitle`}>{subtitle}</span>}
      {subtitle && <span className="text-[#D1D8D3]">·</span>}
      <span className="text-[#9CA3AF]" data-testid={`${testId}-updated`}>
        Updated {updated}
      </span>
      {/* Title kept as a screen-reader-only label so existing tests querying
          `${testId}-title` still resolve. */}
      <span className="sr-only" data-testid={`${testId}-title`}>{title}</span>
    </div>
  );
}



function MetricCard({ title, value, subtitle, icon: Icon, trend, testId }) {
  return (
    <Card className="border-[#E2E8DF] shadow-sm rounded-xl metric-card" data-testid={testId}>
      <CardContent className="p-3 lg:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)] font-medium">{title}</p>
            <p className="text-[20px] sm:text-[22px] lg:text-[24px] font-semibold text-[color:var(--color-text-primary)] mt-0.5 leading-tight tabular-nums">{value}</p>
            {subtitle && <p className="text-[11px] text-[color:var(--color-text-muted)] mt-1 truncate">{subtitle}</p>}
          </div>
          <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
            trend === 'up' ? 'bg-[#2D6A4F]/10' : trend === 'down' ? 'bg-[#C45A41]/10' : 'bg-[#F3F5F2]'
          }`}>
            <Icon className={`w-4 h-4 ${
              trend === 'up' ? 'text-[#2D6A4F]' : trend === 'down' ? 'text-[#C45A41]' : 'text-[color:var(--color-primary)]'
            }`} strokeWidth={1.75} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Side-by-side table: Product Mix (Food vs Bev) + Service Split (Lunch vs Dinner)
function MixSplitTable({
  foodPct, bevPct, foodSale, bevSale, foodApc, bevApc,
  lunchPct, dinnerPct, lunchSales, dinnerSales, lunchCovers, dinnerCovers,
  testId = 'mix-split-table',
}) {
  const hasMix = (foodSale || 0) + (bevSale || 0) > 0;
  const hasSplit = (lunchSales || 0) + (dinnerSales || 0) > 0;
  if (!hasMix && !hasSplit) return null;

  const pct = (v) => pctFmt(v);
  const num = (v) => (v != null && v !== '' ? `₹${Math.round(Number(v)).toLocaleString('en-IN')}` : '—');

  // Rows: each row pairs a Product Mix metric and a Service Split metric.
  const rows = [
    { mixLabel: 'Food %',     mixValue: hasMix   ? pct(foodPct)        : '—',
      splitLabel: 'Lunch %',   splitValue: hasSplit ? pct(lunchPct)     : '—',
      mixTestId: 'mix-food-pct', splitTestId: 'split-lunch-pct' },
    { mixLabel: 'Beverage %', mixValue: hasMix   ? pct(bevPct)         : '—',
      splitLabel: 'Dinner %',  splitValue: hasSplit ? pct(dinnerPct)    : '—',
      mixTestId: 'mix-bev-pct', splitTestId: 'split-dinner-pct' },
    { mixLabel: 'Food Sales',  mixValue: hasMix  ? num(foodSale)        : '—',
      splitLabel: 'Lunch Sales', splitValue: hasSplit ? num(lunchSales) : '—',
      mixTestId: 'mix-food-sales', splitTestId: 'split-lunch-sales' },
    { mixLabel: 'Bev Sales',   mixValue: hasMix  ? num(bevSale)         : '—',
      splitLabel: 'Dinner Sales', splitValue: hasSplit ? num(dinnerSales) : '—',
      mixTestId: 'mix-bev-sales', splitTestId: 'split-dinner-sales' },
    { mixLabel: 'Food APC',    mixValue: foodApc != null && foodApc > 0 ? num(foodApc) : '—',
      splitLabel: 'Lunch Covers', splitValue: hasSplit ? String(lunchCovers || 0) : '—',
      mixTestId: 'mix-food-apc', splitTestId: 'split-lunch-covers' },
    { mixLabel: 'Bev APC',     mixValue: bevApc  != null && bevApc  > 0 ? num(bevApc)  : '—',
      splitLabel: 'Dinner Covers', splitValue: hasSplit ? String(dinnerCovers || 0) : '—',
      mixTestId: 'mix-bev-apc', splitTestId: 'split-dinner-covers' },
  ];

  return (
    <Card className="border-[#E2E8DF] shadow-sm mb-4 overflow-hidden" data-testid={testId}>
      <CardHeader className="py-3 px-4 bg-[#F9F9F8] border-b border-[#E2E8DF]">
        <CardTitle className="text-base text-[color:var(--color-text-primary)]">Product Mix &amp; Service Split</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-[color:var(--color-text-primary)]">
            <thead>
              <tr className="text-xs uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                <th colSpan={2} className="px-4 py-2 font-medium border-r border-[#E2E8DF] text-left">Product Mix</th>
                <th colSpan={2} className="px-4 py-2 font-medium text-left">Service Split</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.mixLabel} className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}>
                  <td className="px-4 py-2 text-[color:var(--color-text-muted)] w-1/4">{r.mixLabel}</td>
                  <td className="px-4 py-2 font-semibold border-r border-[#E2E8DF] w-1/4" data-testid={r.mixTestId}>
                    {r.mixValue}
                  </td>
                  <td className="px-4 py-2 text-[color:var(--color-text-muted)] w-1/4">{r.splitLabel}</td>
                  <td className="px-4 py-2 font-semibold w-1/4" data-testid={r.splitTestId}>{r.splitValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function AlertCard({ alert }) {
  const iconMap = {
    below_budget: TrendingDown,
    high_food_cost: ChefHat,
    high_beverage_cost: Wine,
    missing_entry: Minus,
  };
  const Icon = iconMap[alert.type] || AlertTriangle;
  return (
    <div className="flex items-start gap-3 p-3 bg-[#C45A41]/5 border border-[#C45A41]/20 rounded-lg" data-testid="variance-alert-card">
      <Icon className="w-4 h-4 text-[#C45A41] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <p className="text-sm font-medium text-[#C45A41]">{alert.outlet}</p>
        <p className="text-xs text-[#C45A41]/80">{alert.message}</p>
      </div>
    </div>
  );
}

/* ============================================================
   OutletComparisonTable — dense ops-control table
   ------------------------------------------------------------
   Replaces the previous outlet-card grid (Daily + MTD tabs) with a single
   horizontally-scrollable comparison table. Pure presentation: every
   column reads a field already present on the daily/mtd payload (no extra
   API calls, no extra aggregation logic). Two adapters (`pickDaily` /
   `pickMtd`) normalise the field names so the same render pipeline serves
   both tabs.
   ============================================================ */
function OutletComparisonTable({ outlets, kind = 'daily', testId }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(s => ({ ...s, [id]: !s[id] }));

  // Normalise per-tab field shapes into a single record schema for the row.
  const pick = (o) => kind === 'mtd' ? {
    has_entry: (o.days_reported || 0) > 0,
    // Canonical: Sales = F&B + Rooms; Budget = F&B budget + Room budget.
    sales: (o.total_revenue != null) ? o.total_revenue : ((o.total_sales || 0) + (o.total_room_revenue || 0)),
    budget: (o.total_budget || 0) + (o.total_room_budget || 0),
    foodSale: o.total_food_sale || 0,
    bevSale: o.total_bev_sale || 0,
    fcPct: o.avg_food_cost_pct,
    bcPct: o.avg_beverage_cost_pct,
    apc: o.total_apc,
    foodApc: o.food_apc,
    bevApc: o.beverage_apc,
    covers: o.total_covers,
    nps: o.avg_nps_score,
    checklistPct: o.avg_checklist_pct,
    chDineIn: o.channel_dine_in || 0,
    chSwiggy: o.channel_swiggy || 0,
    chZomato: o.channel_zomato || 0,
    chOther: o.channel_other || 0,
    lunchSales: o.total_lunch_sales || 0,
    dinnerSales: o.total_dinner_sales || 0,
    elecKwh: o.electricity_consumption || 0,
    elecCost: o.electricity_cost || 0,
    gasUnits: o.gas_consumption || 0,
    gasCost: o.gas_cost || 0,
    days: o.days_reported,
    sectionSubs: null,
    roomsEnabled: !!o.rooms_enabled,
    roomRev: o.total_room_revenue || 0,
  } : {
    has_entry: !!o.has_entry,
    // Canonical totals include Room Revenue + Room Budget so Sales/Budget/Var%
    // match the Overview Hero, KPI cards, and Outlet Performance table.
    sales: (o.total_revenue != null) ? o.total_revenue : ((o.net_sales || 0) + (o.room_revenue || 0)),
    budget: (o.total_budget != null) ? o.total_budget : ((o.budget || 0) + (o.room_revenue_budget || 0)),
    foodSale: o.food_sale || 0,
    bevSale: o.beverage_sale || 0,
    fcPct: o.food_cost_pct,
    bcPct: o.beverage_cost_pct,
    apc: o.total_apc,
    foodApc: o.food_apc,
    bevApc: o.beverage_apc,
    covers: o.number_of_covers,
    nps: o.nps_day,
    checklistPct: o.checklist_pct,
    chDineIn: o.channel_dine_in || 0,
    chSwiggy: o.channel_swiggy || 0,
    chZomato: o.channel_zomato || 0,
    chOther: o.channel_other || 0,
    lunchSales: o.lunch_sales || 0,
    dinnerSales: o.dinner_sales || 0,
    elecKwh: o.electricity_consumption || 0,
    elecCost: o.electricity_cost || 0,
    gasUnits: o.gas_consumption || 0,
    gasCost: o.gas_cost || 0,
    days: null,
    sectionSubs: o.section_submissions,
    roomsEnabled: !!o.rooms_enabled,
    roomRev: o.room_revenue || 0,
  };

  const em = <span className="text-[#9CA3AF]">—</span>;
  const pending = <span className="text-[#9CA3AF] italic">…</span>;
  const th = 'px-2 py-1.5 font-medium whitespace-nowrap text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)]';
  const td = 'px-2 py-1.5 whitespace-nowrap';
  const numCell = (v, opts = {}) => v === null || v === undefined || v === 0
    ? em
    : <span className={`tabular-nums ${opts.className || ''}`}>{opts.fmt ? opts.fmt(v) : fmt(v)}</span>;

  const COLS = 17;  // column count for expand-row colSpan

  if (!outlets || outlets.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm mb-3 p-4 text-center text-[#9CA3AF] text-sm"
        data-testid={testId}>No outlet data.</div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm mb-3 overflow-hidden" data-testid={testId}>
      <div className="px-4 py-2.5 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[color:var(--color-text-primary)]">Outlet Comparison</h3>
        <p className="text-[10px] text-[color:var(--color-text-muted)]">Click outlet name to see channel split, sections & rooms →</p>
      </div>
      <div className="overflow-auto max-h-[calc(100vh-160px)]">
        <table className="w-full text-xs text-left text-[color:var(--color-text-primary)]">
          {/* Sticky thead — pins to top of this scrollable container. */}
          <thead className="sticky top-0 z-30 shadow-[0_1px_0_rgba(20,61,48,0.10)]">
            <tr className="bg-[#F7F8F9] border-b-2 border-[color:var(--color-primary)]/10">
              <th className={`${th} sticky left-0 bg-[#F7F8F9] z-30`}>Outlet</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Sales</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Budget</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Var %</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Food</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Bev</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>FC %</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>BC %</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Covers</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>APC</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>NPS</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Checklist</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Elec kWh</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Elec ₹</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Gas Units</th>
              <th className={`${th} text-right bg-[#F7F8F9]`}>Gas ₹</th>
              {kind === 'mtd' && <th className={`${th} text-right bg-[#F7F8F9]`}>Days</th>}
            </tr>
          </thead>
          <tbody>
            {outlets.map(o => {
              const p = pick(o);
              const variance = p.sales - p.budget;
              const variancePct = p.budget > 0 ? (variance / p.budget * 100) : null;
              const isExpanded = !!expanded[o.outlet_id];
              const channelTotal = p.chDineIn + p.chSwiggy + p.chZomato + p.chOther;
              const fcOver = p.fcPct != null && p.fcPct > 35;
              const bcOver = p.bcPct != null && p.bcPct > 30;
              return (
                <Fragment key={o.outlet_id}>
                  <tr className={`border-b border-[#EAEAEA] hover:bg-[#EAF4EE]/30 ${!p.has_entry ? 'opacity-60' : ''}`}
                    data-testid={`${testId}-row-${o.outlet_id}`}>
                    <td className={`${td} sticky left-0 bg-white z-10 font-medium`}>
                      <button type="button" onClick={() => toggle(o.outlet_id)}
                        className="flex items-center gap-1.5 w-full text-left hover:text-[color:var(--color-primary)]"
                        data-testid={`${testId}-toggle-${o.outlet_id}`}>
                        {isExpanded
                          ? <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />
                          : <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />}
                        <span className={`${o.hierarchy === 'sub' ? 'pl-2' : ''} text-[color:var(--color-primary)]`}>
                          {o.hierarchy === 'sub' && <span className="text-[#9CA3AF] -ml-2">↳ </span>}
                          {o.outlet_name}
                        </span>
                        {p.roomsEnabled && p.roomRev > 0 && <BedDouble className="w-3 h-3 text-[color:var(--color-primary)]/50" strokeWidth={1.5} />}
                        {!p.has_entry && <span className="text-[9px] font-semibold uppercase tracking-wider text-[#9CA3AF] bg-[#F0F2F0] px-1.5 py-0.5 rounded">No Entry</span>}
                      </button>
                    </td>
                    <td className={`${td} text-right font-bold text-[color:var(--color-primary)]`}>{numCell(p.sales)}</td>
                    <td className={`${td} text-right text-[color:var(--color-text-muted)]`}>{numCell(p.budget)}</td>
                    <td className={`${td} text-right`}>
                      {variancePct == null ? em : (
                        <span className={`tabular-nums font-semibold ${variancePct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                          data-testid={`${testId}-var-${o.outlet_id}`}>
                          {pctFmt(variancePct, { signed: true })}
                        </span>
                      )}
                    </td>
                    <td className={`${td} text-right`}>{numCell(p.foodSale)}</td>
                    <td className={`${td} text-right`}>{numCell(p.bevSale)}</td>
                    <td className={`${td} text-right`}>
                      {p.fcPct == null ? (p.has_entry ? pending : em)
                        : <span className={`tabular-nums ${fcOver ? 'text-[#C45A41] font-semibold' : ''}`}>{pctFmt(p.fcPct)}</span>}
                    </td>
                    <td className={`${td} text-right`}>
                      {p.bcPct == null ? (p.has_entry ? pending : em)
                        : <span className={`tabular-nums ${bcOver ? 'text-[#C45A41] font-semibold' : ''}`}>{pctFmt(p.bcPct)}</span>}
                    </td>
                    <td className={`${td} text-right`}>{p.covers ? <span className="tabular-nums">{Number(p.covers).toLocaleString('en-IN')}</span> : em}</td>
                    <td className={`${td} text-right`}>{numCell(p.apc)}</td>
                    <td className={`${td} text-right`}>{p.nps && p.nps > 0 ? <span className="tabular-nums">{p.nps}</span> : em}</td>
                    <td className={`${td} text-right`}>{p.checklistPct != null ? <span className="tabular-nums">{pctFmt(p.checklistPct)}</span> : em}</td>
                    <td className={`${td} text-right text-[color:var(--color-text-muted)]`}>{p.elecKwh ? <span className="tabular-nums">{Math.round(p.elecKwh).toLocaleString('en-IN')}</span> : em}</td>
                    <td className={`${td} text-right`}>{numCell(p.elecCost)}</td>
                    <td className={`${td} text-right text-[color:var(--color-text-muted)]`}>{p.gasUnits ? <span className="tabular-nums">{Math.round(p.gasUnits).toLocaleString('en-IN')}</span> : em}</td>
                    <td className={`${td} text-right`}>{numCell(p.gasCost)}</td>
                    {kind === 'mtd' && <td className={`${td} text-right text-[color:var(--color-text-muted)]`}>{p.days || em}</td>}
                  </tr>
                  {isExpanded && (
                    <tr className="bg-[#FAFBFA]" data-testid={`${testId}-detail-${o.outlet_id}`}>
                      <td colSpan={kind === 'mtd' ? COLS : COLS - 1} className="px-4 py-3">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Channel Split breakdown */}
                          {channelTotal > 0 && (
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)] font-medium mb-1.5">Channel Split</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                {[
                                  { label: 'Dine-in', value: p.chDineIn },
                                  { label: 'Swiggy', value: p.chSwiggy },
                                  { label: 'Zomato', value: p.chZomato },
                                  { label: 'Other', value: p.chOther },
                                ].map(c => (
                                  <div key={c.label} className="flex justify-between">
                                    <span className="text-[color:var(--color-text-muted)]">{c.label}</span>
                                    <span className="font-semibold tabular-nums text-[color:var(--color-text-primary)]">
                                      {c.value > 0 ? fmt(c.value) : '—'}
                                      {c.value > 0 && channelTotal > 0 && (
                                        <span className="text-[#9CA3AF] ml-1.5 text-[10px]">{pctFmt(c.value / channelTotal * 100)}</span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Service split + APC detail */}
                          {(p.lunchSales > 0 || p.dinnerSales > 0 || p.foodApc || p.bevApc) && (
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)] font-medium mb-1.5">Service & APC</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Lunch</span><span className="tabular-nums font-semibold">{p.lunchSales ? fmt(p.lunchSales) : '—'}</span></div>
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Dinner</span><span className="tabular-nums font-semibold">{p.dinnerSales ? fmt(p.dinnerSales) : '—'}</span></div>
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Food APC</span><span className="tabular-nums font-semibold">{p.foodApc ? fmt(p.foodApc) : '—'}</span></div>
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Bev APC</span><span className="tabular-nums font-semibold">{p.bevApc ? fmt(p.bevApc) : '—'}</span></div>
                              </div>
                            </div>
                          )}
                          {/* Section status — only on Daily tab where the data exists */}
                          {p.sectionSubs && Object.keys(p.sectionSubs).length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)] font-medium mb-1.5">Section Status</p>
                              <div className="grid grid-cols-1 gap-y-0.5 text-[11px]">
                                {[
                                  { key: 'sales_data', label: 'Sales' },
                                  { key: 'kitchen_inventory', label: 'Kitchen' },
                                  ...(o.alcohol_enabled === false ? [] : [{ key: 'bar_inventory', label: 'Bar' }]),
                                  { key: 'other_issues', label: 'Issues' },
                                  { key: 'customer_experience', label: 'Cust. Exp' },
                                  { key: 'notes', label: 'Notes' },
                                ].map(sec => {
                                  const ss = p.sectionSubs[sec.key];
                                  return (
                                    <div key={sec.key} className="flex justify-between"
                                      data-testid={`section-status-${sec.key}-${o.outlet_id}`}>
                                      <span className="text-[color:var(--color-text-muted)]">{sec.label}</span>
                                      {ss?.locked
                                        ? <span className="text-[#2D6A4F] font-medium">Submitted{ss.edit_count > 0 ? ` · ${ss.edit_count}x edit` : ''}</span>
                                        : <span className="text-[#C45A41]">Pending</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {/* Rooms (when enabled & has data) */}
                          {p.roomsEnabled && p.roomRev > 0 && (
                            <div className="col-span-1 lg:col-span-3 pt-2 border-t border-[#EAEAEA]">
                              <p className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--color-text-muted)] font-medium mb-1.5 flex items-center gap-1">
                                <BedDouble className="w-3 h-3" /> Rooms
                              </p>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Sold / Avail</span><span className="tabular-nums font-semibold">{(o.rooms_sold || o.total_rooms_sold || 0)} / {(o.rooms_available || o.total_rooms_available || 0)}</span></div>
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Occupancy</span><span className="tabular-nums font-semibold">{(o.occupancy_pct || o.avg_occupancy_pct) ? `${o.occupancy_pct || o.avg_occupancy_pct}%` : '—'}</span></div>
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">Revenue</span><span className="tabular-nums font-semibold">{fmt(p.roomRev)}</span></div>
                                <div className="flex justify-between"><span className="text-[color:var(--color-text-muted)]">ARR</span><span className="tabular-nums font-semibold">{(o.arr || o.avg_arr) ? fmt(o.arr || o.avg_arr) : '—'}</span></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyOutletCard({ outlet }) {
  const [open, setOpen] = useState(false);
  const hasLyData = outlet.last_year_net_sales != null || outlet.ly_same_day_sales != null;
  return (
    <Card className="border-[#E2E8DF] shadow-sm opacity-60" data-testid={`outlet-card-${outlet.outlet_id}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F9F9F8]/50 transition-colors"
        data-testid={`empty-toggle-${outlet.outlet_id}`}>
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-[color:var(--color-text-primary)] text-sm">{outlet.outlet_name}</h4>
          <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[color:var(--color-text-muted)]">No Entry</Badge>
        </div>
        {open
          ? <ChevronDown className="w-3.5 h-3.5 text-[color:var(--color-text-muted)]" />
          : <ChevronRight className="w-3.5 h-3.5 text-[color:var(--color-text-muted)]" />
        }
      </button>
      {open && hasLyData && (
        <CardContent className="pt-0 pb-3 px-4 border-t border-[#E2E8DF]">
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg">
              <p className="text-[10px] uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] font-medium">
                LY Same Date{outlet.ly_same_date_day ? ` (${outlet.ly_same_date_day.slice(0,3)})` : ''}
              </p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)] mt-0.5">{outlet.last_year_net_sales != null ? fmt(outlet.last_year_net_sales) : '—'}</p>
            </div>
            <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg">
              <p className="text-[10px] uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] font-medium">
                LY Same Day{outlet.ly_same_day_name ? ` (${outlet.ly_same_day_name.slice(0,3)})` : ''}
              </p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)] mt-0.5">{outlet.ly_same_day_sales != null ? fmt(outlet.ly_same_day_sales) : '—'}</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// YTD outlets table — merges Rooms revenue inline with expandable rooms-detail row.
// Portfolio total row shows weighted Cost % (sum food cost / sum food sale).
function YtdOutletTable({ ytdData }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const os = ytdData?.outlets || [];
  if (os.length === 0) return null;

  // Subset that contributes to portfolio totals (primary + opted-in only).
  const totalsPool = os.filter(countsTowardPortfolio);
  const sum = (k) => totalsPool.reduce((s, o) => s + (Number(o[k]) || 0), 0);
  const totSales = sum('ytd_sales');
  const totBudget = sum('ytd_budget');
  const totRoomRev = sum('total_room_revenue');
  const totRoomBudget = sum('total_room_budget');
  const totFood = sum('total_food_sale');
  const totBev = sum('total_bev_sale');
  const totFc = totalsPool.reduce((s, o) => s + (o.total_food_cost != null ? Number(o.total_food_cost) : 0), 0);
  const totBc = totalsPool.reduce((s, o) => s + (o.total_beverage_cost != null ? Number(o.total_beverage_cost) : 0), 0);
  const fcPct = totFood > 0 ? (totFc / totFood * 100) : null;
  const bcPct = totBev > 0 ? (totBc / totBev * 100) : null;
  const totCovers = sum('total_covers');
  const totApc = totCovers > 0 ? Math.round(totSales / totCovers) : null;
  const maxDays = Math.max(...totalsPool.map(o => o.days_reported || 0), 1);
  const avgDaily = maxDays > 0 ? Math.round((totSales + totRoomRev) / maxDays) : 0;
  const totVariance = (totSales + totRoomRev) - (totBudget + totRoomBudget);

  return (
    <div className="overflow-auto max-h-[calc(100vh-160px)]">
      <table className="w-full text-sm text-left text-[color:var(--color-text-primary)]">
        <thead className="sticky top-0 z-30 shadow-[0_1px_0_rgba(20,61,48,0.10)]">
          <tr className="text-xs uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] bg-[#F9F9F8] border-b border-[#E2E8DF]">
            <th className="px-3 py-3 font-medium w-8 bg-[#F9F9F8]"></th>
            <th className="px-3 py-3 font-medium bg-[#F9F9F8]">Outlet</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">F&B Sales</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Room Rev</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Total Revenue</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Budget</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Variance</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Avg Daily</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">FC %</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">BC %</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Covers</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Total APC</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Days</th>
          </tr>
        </thead>
        <tbody>
          {os.map((o, i) => {
            const totalRev = (o.ytd_sales || 0) + (o.total_room_revenue || 0);
            const totalBudget = (o.ytd_budget || 0) + (o.total_room_budget || 0);
            const variance = totalRev - totalBudget;
            const variancePct = totalBudget > 0 ? (variance / totalBudget * 100).toFixed(1) : '0.0';
            const isRoomRow = o.rooms_enabled && (o.total_room_revenue > 0 || o.total_rooms_sold > 0);
            return (
              <Fragment key={o.outlet_id}>
                <tr className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                  data-testid={`ytd-row-${o.outlet_id}`}>
                  <td className="px-3 py-3">
                    {isRoomRow ? (
                      <button onClick={() => toggle(o.outlet_id)} className="text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-primary)]"
                        data-testid={`ytd-rooms-expand-${o.outlet_id}`}>
                        {expanded[o.outlet_id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 font-medium">
                    <span className={`flex items-center gap-1.5 ${o.hierarchy === 'sub' ? 'pl-4' : ''}`}>
                      {o.hierarchy === 'sub' && <span className="text-[#9CA3AF] -ml-3" aria-hidden>↳</span>}
                      {o.outlet_name}
                      {isRoomRow && <BedDouble className="w-3 h-3 text-[color:var(--color-primary)]/60" strokeWidth={1.5} />}
                      {o.hierarchy === 'sub' && <span className="text-[8px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F3F5F2] px-1 py-px rounded" data-testid={`ytd-sub-badge-${o.outlet_id}`}>Sub</span>}
                      {o.include_in_totals === false && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D4A017] bg-[#D4A017]/10 px-1 py-px rounded" title="Excluded from portfolio totals">Excl</span>}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">{fmt(o.ytd_sales)}</td>
                  <td className="px-3 py-3 text-right">{isRoomRow ? fmt(o.total_room_revenue) : <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className="px-3 py-3 text-right font-semibold">{fmt(totalRev)}</td>
                  <td className="px-3 py-3 text-right">{fmt(totalBudget)}</td>
                  <td className="px-3 py-3 text-right">
                    <span className={`font-medium ${variance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                      {variance >= 0 ? '+' : ''}{fmt(variance)} ({variancePct}%)
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">{fmt(o.avg_daily_sales)}</td>
                  <td className="px-3 py-3 text-right">
                    {o.avg_food_cost_pct != null ? (
                      <span className={o.avg_food_cost_pct > 35 ? 'text-[#C45A41] font-medium' : ''}>{o.avg_food_cost_pct}%</span>
                    ) : <span className="text-[#9CA3AF] italic text-xs">Pending</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {o.avg_beverage_cost_pct != null ? (
                      <span className={o.avg_beverage_cost_pct > 30 ? 'text-[#C45A41] font-medium' : ''}>{o.avg_beverage_cost_pct}%</span>
                    ) : <span className="text-[#9CA3AF] italic text-xs">Pending</span>}
                  </td>
                  <td className="px-3 py-3 text-right">{o.total_covers > 0 ? o.total_covers : '—'}</td>
                  <td className="px-3 py-3 text-right">{o.total_apc != null ? fmt(o.total_apc) : '—'}</td>
                  <td className="px-3 py-3 text-right">{o.days_reported}</td>
                </tr>
                {isRoomRow && expanded[o.outlet_id] && (
                  <tr className="bg-[color:var(--color-primary)]/5 border-b border-[#E2E8DF]" data-testid={`ytd-rooms-detail-${o.outlet_id}`}>
                    <td colSpan={13} className="px-6 py-3">
                      <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs">
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Rooms Sold:</span>{' '}
                          <span className="font-semibold">{o.total_rooms_sold} / {o.total_rooms_available || 0}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Avg Occupancy:</span>{' '}
                          <span className="font-semibold">{o.avg_occupancy_pct > 0 ? `${o.avg_occupancy_pct}%` : '—'}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Avg ARR:</span>{' '}
                          <span className="font-semibold">{o.total_rooms_sold > 0 ? fmt(o.avg_arr) : '—'}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">RevPAR:</span>{' '}
                          <span className="font-semibold">{o.avg_revpar > 0 ? fmt(o.avg_revpar) : '—'}</span>
                        </div>
                        {o.total_room_budget > 0 && (
                          <div>
                            <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Budget:</span>{' '}
                            <span className="font-semibold">{fmt(o.total_room_budget)}</span>
                          </div>
                        )}
                        {o.total_room_ly > 0 && (
                          <div>
                            <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Last Year:</span>{' '}
                            <span className="font-semibold">{fmt(o.total_room_ly)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[color:var(--color-primary)]/20 bg-[color:var(--color-primary)]/5 font-medium" data-testid="ytd-total-row">
            <td className="px-3 py-3"></td>
            <td className="px-3 py-3 font-semibold text-[color:var(--color-primary)]">Portfolio Total</td>
            <td className="px-3 py-3 text-right font-semibold">{fmt(totSales)}</td>
            <td className="px-3 py-3 text-right">{totRoomRev > 0 ? fmt(totRoomRev) : <span className="text-[#9CA3AF]">—</span>}</td>
            <td className="px-3 py-3 text-right font-semibold text-[color:var(--color-primary)]">{fmt(totSales + totRoomRev)}</td>
            <td className="px-3 py-3 text-right">{fmt(totBudget + totRoomBudget)}</td>
            <td className="px-3 py-3 text-right">
              <span className={`font-medium ${totVariance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                {totVariance >= 0 ? '+' : ''}{fmt(totVariance)}
              </span>
            </td>
            <td className="px-3 py-3 text-right">{fmt(avgDaily)}</td>
            <td className="px-3 py-3 text-right">
              {fcPct != null ? (
                <span className={fcPct > 35 ? 'text-[#C45A41] font-medium' : 'font-medium'}
                  data-testid="ytd-portfolio-fc-pct">
                  {fcPct.toFixed(1)}%
                </span>
              ) : <span className="text-[#9CA3AF] text-xs">—</span>}
            </td>
            <td className="px-3 py-3 text-right">
              {bcPct != null ? (
                <span className={bcPct > 30 ? 'text-[#C45A41] font-medium' : 'font-medium'}
                  data-testid="ytd-portfolio-bc-pct">
                  {bcPct.toFixed(1)}%
                </span>
              ) : <span className="text-[#9CA3AF] text-xs">—</span>}
            </td>
            <td className="px-3 py-3 text-right">{totCovers || '—'}</td>
            <td className="px-3 py-3 text-right">{totApc !== null ? fmt(totApc) : '—'}</td>
            <td className="px-3 py-3 text-right">{maxDays}</td>
          </tr>
          <tr className="text-[10px] text-[color:var(--color-text-muted)] bg-[color:var(--color-primary)]/5">
            <td colSpan={13} className="px-3 py-1.5 italic">
              Total Revenue includes F&B sales + Room revenue • Cost % is sales-weighted
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// MTD outlets table — merges Rooms revenue inline with expandable rooms-detail row.
// Portfolio total row shows weighted Cost % (sum food cost / sum food sale).
function MtdOutletTable({ mtdData }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const os = mtdData?.outlets || [];
  if (os.length === 0) return null;

  // Subset that contributes to portfolio totals (primary + opted-in only).
  const totalsPool = os.filter(countsTowardPortfolio);
  const sum = (k) => totalsPool.reduce((s, o) => s + (Number(o[k]) || 0), 0);
  const totSales = sum('total_sales');
  const totRoomRev = sum('total_room_revenue');
  const totLY = sum('ly_mtd_excel');
  const totBudget = sum('total_budget');
  const totRoomBudget = sum('total_room_budget');
  const totFood = sum('total_food_sale');
  const totBev = sum('total_bev_sale');
  const totFc = totalsPool.reduce((s, o) => s + (o.total_food_cost != null ? Number(o.total_food_cost) : 0), 0);
  const totBc = totalsPool.reduce((s, o) => s + (o.total_beverage_cost != null ? Number(o.total_beverage_cost) : 0), 0);
  const fcPct = totFood > 0 ? (totFc / totFood * 100) : null;
  const bcPct = totBev > 0 ? (totBc / totBev * 100) : null;
  const yoyTotal = (totLY + totRoomRev > 0)
    ? ((totSales + totRoomRev - totLY) / totLY * 100).toFixed(1)
    : null;
  const totCovers = totalsPool.reduce((s, o) => s + (o.total_covers || 0), 0);
  const totApc = totCovers > 0 ? Math.round(totSales / totCovers) : null;
  const maxDays = Math.max(...totalsPool.map(o => o.days_reported || 0), 1);
  const avgDaily = maxDays > 0 ? Math.round((totSales + totRoomRev) / maxDays) : 0;

  return (
    <div className="overflow-auto max-h-[calc(100vh-160px)]">
      <table className="w-full text-sm text-left text-[color:var(--color-text-primary)]">
        <thead className="sticky top-0 z-30 shadow-[0_1px_0_rgba(20,61,48,0.10)]">
          <tr className="text-xs uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] bg-[#F9F9F8] border-b border-[#E2E8DF]">
            <th className="px-3 py-3 font-medium w-8 bg-[#F9F9F8]"></th>
            <th className="px-3 py-3 font-medium bg-[#F9F9F8]">Outlet</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">F&B Sales</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Room Rev</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Total Revenue</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">LY MTD</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">YoY %</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Budget</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">FC %</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">BC %</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Covers</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Total APC</th>
            <th className="px-3 py-3 font-medium text-right bg-[#F9F9F8]">Days</th>
          </tr>
        </thead>
        <tbody>
          {os.map((o, i) => {
            const lyVal = o.ly_mtd_excel;
            const totalRev = (o.total_sales || 0) + (o.total_room_revenue || 0);
            const yoy = lyVal != null && lyVal > 0
              ? ((totalRev - lyVal) / lyVal * 100).toFixed(1)
              : null;
            const isRoomRow = o.rooms_enabled && (o.total_room_revenue > 0 || o.total_rooms_sold > 0);
            return (
              <Fragment key={o.outlet_id}>
                <tr className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                  data-testid={`mtd-row-${o.outlet_id}`}>
                  <td className="px-3 py-3">
                    {isRoomRow ? (
                      <button onClick={() => toggle(o.outlet_id)} className="text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-primary)]"
                        data-testid={`mtd-rooms-expand-${o.outlet_id}`}>
                        {expanded[o.outlet_id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 font-medium">
                    <span className={`flex items-center gap-1.5 ${o.hierarchy === 'sub' ? 'pl-4' : ''}`}>
                      {o.hierarchy === 'sub' && <span className="text-[#9CA3AF] -ml-3" aria-hidden>↳</span>}
                      {o.outlet_name}
                      {isRoomRow && <BedDouble className="w-3 h-3 text-[color:var(--color-primary)]/60" strokeWidth={1.5} />}
                      {o.hierarchy === 'sub' && <span className="text-[8px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F3F5F2] px-1 py-px rounded" data-testid={`mtd-sub-badge-${o.outlet_id}`}>Sub</span>}
                      {o.include_in_totals === false && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D4A017] bg-[#D4A017]/10 px-1 py-px rounded" title="Excluded from portfolio totals">Excl</span>}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">{fmt(o.total_sales)}</td>
                  <td className="px-3 py-3 text-right">{isRoomRow ? fmt(o.total_room_revenue) : <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className="px-3 py-3 text-right font-semibold">{fmt(totalRev)}</td>
                  <td className="px-3 py-3 text-right text-[color:var(--color-text-muted)]">
                    {lyVal != null ? fmt(lyVal) : <span className="text-[#9CA3AF] italic text-xs">N/A</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {yoy !== null ? (
                      <span className={`font-medium ${parseFloat(yoy) >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                        data-testid={`mtd-yoy-${o.outlet_id}`}>
                        {parseFloat(yoy) >= 0 ? '+' : ''}{yoy}%
                      </span>
                    ) : <span className="text-[#9CA3AF] italic text-xs">N/A</span>}
                  </td>
                  <td className="px-3 py-3 text-right">{fmt(o.total_budget)}</td>
                  <td className="px-3 py-3 text-right">
                    {o.avg_food_cost_pct != null ? (
                      <span className={o.avg_food_cost_pct > 35 ? 'text-[#C45A41] font-medium' : ''}>
                        {o.avg_food_cost_pct}%
                      </span>
                    ) : <span className="text-[#9CA3AF] italic text-xs">Pending</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {o.avg_beverage_cost_pct != null ? (
                      <span className={o.avg_beverage_cost_pct > 30 ? 'text-[#C45A41] font-medium' : ''}>
                        {o.avg_beverage_cost_pct}%
                      </span>
                    ) : <span className="text-[#9CA3AF] italic text-xs">Pending</span>}
                  </td>
                  <td className="px-3 py-3 text-right">{o.total_covers != null ? o.total_covers : '—'}</td>
                  <td className="px-3 py-3 text-right">{o.total_apc != null ? fmt(o.total_apc) : '—'}</td>
                  <td className="px-3 py-3 text-right">{o.days_reported}</td>
                </tr>
                {isRoomRow && expanded[o.outlet_id] && (
                  <tr className="bg-[color:var(--color-primary)]/5 border-b border-[#E2E8DF]" data-testid={`mtd-rooms-detail-${o.outlet_id}`}>
                    <td colSpan={13} className="px-6 py-3">
                      <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs">
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Rooms Sold:</span>{' '}
                          <span className="font-semibold">{o.total_rooms_sold} / {o.total_rooms_available || 0}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Avg Occupancy:</span>{' '}
                          <span className="font-semibold">{o.avg_occupancy_pct > 0 ? `${o.avg_occupancy_pct}%` : '—'}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Avg ARR:</span>{' '}
                          <span className="font-semibold">{o.total_rooms_sold > 0 ? fmt(o.avg_arr) : '—'}</span>
                        </div>
                        <div>
                          <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">RevPAR:</span>{' '}
                          <span className="font-semibold">{o.avg_revpar > 0 ? fmt(o.avg_revpar) : '—'}</span>
                        </div>
                        {o.total_room_budget > 0 && (
                          <div>
                            <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Budget:</span>{' '}
                            <span className="font-semibold">{fmt(o.total_room_budget)}</span>
                          </div>
                        )}
                        {o.total_room_ly > 0 && (
                          <div>
                            <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Last Year:</span>{' '}
                            <span className="font-semibold">{fmt(o.total_room_ly)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[color:var(--color-primary)]/20 bg-[color:var(--color-primary)]/5 font-medium" data-testid="sales-total-row">
            <td className="px-3 py-3"></td>
            <td className="px-3 py-3 font-semibold text-[color:var(--color-primary)]">Portfolio Total</td>
            <td className="px-3 py-3 text-right font-semibold">{fmt(totSales)}</td>
            <td className="px-3 py-3 text-right">{totRoomRev > 0 ? fmt(totRoomRev) : <span className="text-[#9CA3AF]">—</span>}</td>
            <td className="px-3 py-3 text-right font-semibold text-[color:var(--color-primary)]">{fmt(totSales + totRoomRev)}</td>
            <td className="px-3 py-3 text-right">{totLY > 0 ? fmt(totLY) : <span className="text-[#9CA3AF] text-xs">N/A</span>}</td>
            <td className="px-3 py-3 text-right">
              {yoyTotal !== null ? (
                <span className={parseFloat(yoyTotal) >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}>
                  {parseFloat(yoyTotal) >= 0 ? '+' : ''}{yoyTotal}%
                </span>
              ) : <span className="text-[#9CA3AF] text-xs">N/A</span>}
            </td>
            <td className="px-3 py-3 text-right">{fmt(totBudget + totRoomBudget)}</td>
            <td className="px-3 py-3 text-right">
              {fcPct != null ? (
                <span className={fcPct > 35 ? 'text-[#C45A41] font-medium' : 'font-medium'}
                  data-testid="mtd-portfolio-fc-pct">
                  {fcPct.toFixed(1)}%
                </span>
              ) : <span className="text-[#9CA3AF] text-xs">—</span>}
            </td>
            <td className="px-3 py-3 text-right">
              {bcPct != null ? (
                <span className={bcPct > 30 ? 'text-[#C45A41] font-medium' : 'font-medium'}
                  data-testid="mtd-portfolio-bc-pct">
                  {bcPct.toFixed(1)}%
                </span>
              ) : <span className="text-[#9CA3AF] text-xs">—</span>}
            </td>
            <td className="px-3 py-3 text-right">{totCovers || '—'}</td>
            <td className="px-3 py-3 text-right">{totApc !== null ? fmt(totApc) : '—'}</td>
            <td className="px-3 py-3 text-right">{maxDays}</td>
          </tr>
          <tr className="text-[10px] text-[color:var(--color-text-muted)] bg-[color:var(--color-primary)]/5">
            <td colSpan={13} className="px-3 py-1.5 italic">
              Total Revenue includes F&B sales + Room revenue • Cost % is sales-weighted • Avg Daily = Total Revenue / Days
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Compact unified outlet summary table (merges rooms inline as expandable row)
// SEPARATION OF CONCERNS:
//   • Display (`rows`)        = ALL outlets (primary + sub, reported or not)
//   • Aggregation (`totalsRows`) = only primaries with include_in_totals=true
// This guarantees parent outlets (e.g. Woodland) stay visible even when they
// have no entry yet, and sub-outlets never contribute to portfolio totals.
function DailyOutletSummaryTable({ outlets, agg, hasRooms }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const rows = outlets || [];
  if (rows.length === 0) return null;
  // Subset that contributes to portfolio totals (primary + opted-in only).
  // Must ALSO have an entry — otherwise we'd sum `null` and pollute KPIs.
  const totalsRows = rows.filter(o => o.has_entry && countsTowardPortfolio(o));
  return (
    <Card className="border-[#E2E8DF] shadow-sm mb-4 overflow-hidden" data-testid="daily-outlets-summary-table">
      <CardHeader className="py-3 px-4 bg-[#F9F9F8] border-b border-[#E2E8DF]">
        <CardTitle className="text-base text-[color:var(--color-text-primary)]">All Outlets at a Glance</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-auto max-h-[calc(100vh-160px)]">
          <table className="w-full text-sm text-left text-[color:var(--color-text-primary)]">
            <thead className="sticky top-0 z-30 shadow-[0_1px_0_rgba(20,61,48,0.10)]">
              <tr className="text-xs uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                <th className="px-3 py-2 font-medium w-8 bg-[#F9F9F8]"></th>
                <th className="px-3 py-2 font-medium bg-[#F9F9F8]">Outlet</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Net Sales</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Room Rev</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Total Revenue</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Budget</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Var %</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">FC %</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">BC %</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Covers</th>
                <th className="px-3 py-2 font-medium text-right bg-[#F9F9F8]">Total APC</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o, i) => {
                const totalRev = (o.net_sales || 0) + (o.room_revenue || 0);
                const isRoomRow = o.has_entry && o.rooms_enabled && (o.room_revenue > 0 || o.rooms_sold > 0);
                const em = <span className="text-[#9CA3AF]">—</span>;
                const pending = <span className="text-[#9CA3AF] text-xs italic">Pending</span>;
                return (
                  <Fragment key={o.outlet_id}>
                    <tr className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'} ${!o.has_entry ? 'opacity-70' : ''}`}
                      data-testid={`daily-summary-row-${o.outlet_id}`}>
                      <td className="px-3 py-2">
                        {isRoomRow ? (
                          <button onClick={() => toggle(o.outlet_id)} className="text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-primary)]"
                            data-testid={`daily-rooms-expand-${o.outlet_id}`}>
                            {expanded[o.outlet_id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 font-medium">
                        <span className={`flex items-center gap-1.5 ${o.hierarchy === 'sub' ? 'pl-4' : ''}`}>
                          {o.hierarchy === 'sub' && <span className="text-[#9CA3AF] -ml-3" aria-hidden>↳</span>}
                          {o.outlet_name}
                          {isRoomRow && <BedDouble className="w-3 h-3 text-[color:var(--color-primary)]/60" strokeWidth={1.5} />}
                          {o.hierarchy === 'sub' && <span className="text-[8px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F3F5F2] px-1 py-px rounded" data-testid={`daily-sub-badge-${o.outlet_id}`}>Sub</span>}
                          {o.include_in_totals === false && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D4A017] bg-[#D4A017]/10 px-1 py-px rounded" title="Excluded from portfolio totals">Excl</span>}
                          {!o.has_entry && <span className="text-[8px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F3F5F2] px-1 py-px rounded" data-testid={`daily-not-reported-${o.outlet_id}`}>Not reported</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{o.has_entry ? fmt(o.net_sales) : em}</td>
                      <td className="px-3 py-2 text-right">{isRoomRow ? fmt(o.room_revenue) : em}</td>
                      <td className="px-3 py-2 text-right font-semibold">{o.has_entry ? fmt(totalRev) : em}</td>
                      <td className="px-3 py-2 text-right">{o.has_entry ? fmt(o.budget) : em}</td>
                      <td className={`px-3 py-2 text-right font-medium ${!o.has_entry ? 'text-[#9CA3AF]' : o.variance_pct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                        {o.has_entry
                          ? `${o.variance_pct >= 0 ? '+' : ''}${(o.variance_pct ?? 0).toFixed ? (o.variance_pct).toFixed(1) : o.variance_pct}%`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!o.has_entry ? em : o.food_cost_pct != null ? (
                          <span className={o.food_cost_pct > 35 ? 'text-[#C45A41] font-medium' : ''}>{o.food_cost_pct}%</span>
                        ) : pending}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!o.has_entry ? em : o.beverage_cost_pct != null ? (
                          <span className={o.beverage_cost_pct > 30 ? 'text-[#C45A41] font-medium' : ''}>{o.beverage_cost_pct}%</span>
                        ) : pending}
                      </td>
                      <td className="px-3 py-2 text-right">{o.has_entry ? (o.number_of_covers || em) : em}</td>
                      <td className="px-3 py-2 text-right">{o.has_entry && o.total_apc ? fmt(o.total_apc) : em}</td>
                    </tr>
                    {isRoomRow && expanded[o.outlet_id] && (
                      <tr className="bg-[color:var(--color-primary)]/5 border-b border-[#E2E8DF]" data-testid={`daily-rooms-detail-${o.outlet_id}`}>
                        <td colSpan={11} className="px-6 py-3">
                          <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs">
                            <div>
                              <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Rooms Sold:</span>{' '}
                              <span className="font-semibold">{o.rooms_sold} / {o.rooms_available}</span>
                            </div>
                            <div>
                              <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Occupancy:</span>{' '}
                              <span className="font-semibold">{o.rooms_available > 0 ? `${o.occupancy_pct}%` : '—'}</span>
                            </div>
                            <div>
                              <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">ARR:</span>{' '}
                              <span className="font-semibold">{o.rooms_sold > 0 ? fmt(o.arr) : '—'}</span>
                            </div>
                            <div>
                              <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">RevPAR:</span>{' '}
                              <span className="font-semibold">
                                {o.rooms_available > 0 ? fmt((o.room_revenue || 0) / o.rooms_available) : '—'}
                              </span>
                            </div>
                            {o.room_revenue_budget > 0 && (
                              <div>
                                <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Budget:</span>{' '}
                                <span className="font-semibold">{fmt(o.room_revenue_budget)}</span>
                              </div>
                            )}
                            {o.room_revenue_last_year > 0 && (
                              <div>
                                <span className="text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Last Year:</span>{' '}
                                <span className="font-semibold">{fmt(o.room_revenue_last_year)}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[color:var(--color-primary)]/20 bg-[color:var(--color-primary)]/5 font-medium" data-testid="daily-summary-total-row">
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 font-semibold text-[color:var(--color-primary)]">Portfolio Total</td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(totalsRows.reduce((s, o) => s + (o.net_sales || 0), 0))}</td>
                <td className="px-3 py-2 text-right">{hasRooms ? fmt(agg.totalRoomRev) : <span className="text-[#9CA3AF]">—</span>}</td>
                <td className="px-3 py-2 text-right font-semibold text-[color:var(--color-primary)]">{fmt(agg.grandRevenue)}</td>
                <td className="px-3 py-2 text-right">{fmt(totalsRows.reduce((s, o) => s + (o.budget || 0), 0))}</td>
                <td className="px-3 py-2 text-right text-[#9CA3AF] text-xs">—</td>
                <td className="px-3 py-2 text-right">
                  {agg.foodCostPct != null ? (
                    <span className={agg.foodCostPct > 35 ? 'text-[#C45A41] font-medium' : 'font-medium'}
                      data-testid="daily-portfolio-fc-pct">
                      {agg.foodCostPct.toFixed(1)}%
                    </span>
                  ) : <span className="text-[#9CA3AF] text-xs">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {agg.bevCostPct != null ? (
                    <span className={agg.bevCostPct > 30 ? 'text-[#C45A41] font-medium' : 'font-medium'}
                      data-testid="daily-portfolio-bc-pct">
                      {agg.bevCostPct.toFixed(1)}%
                    </span>
                  ) : <span className="text-[#9CA3AF] text-xs">—</span>}
                </td>
                <td className="px-3 py-2 text-right">{agg.totalCovers || <span className="text-[#9CA3AF]">—</span>}</td>
                <td className="px-3 py-2 text-right">{agg.apc != null ? fmt(agg.apc) : <span className="text-[#9CA3AF]">—</span>}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact, uniform per-outlet summary card used on MTD & YTD tabs
function AggregateOutletCard({ outlet, sales, budget, foodCostPct, bevCostPct, covers, totalApc, roomRevenue, occupancy, arr, daysReported, label, testId }) {
  const variance = (sales || 0) - (budget || 0);
  const variancePct = budget > 0 ? (variance / budget * 100).toFixed(1) : null;
  const totalRev = (sales || 0) + (roomRevenue || 0);
  const isRoomOutlet = outlet.rooms_enabled && (roomRevenue > 0);
  return (
    <Card className="border-[#E2E8DF] shadow-sm metric-card" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-[color:var(--color-text-primary)] text-sm flex items-center gap-1.5">
            {outlet.outlet_name}
            {isRoomOutlet && <BedDouble className="w-3 h-3 text-[color:var(--color-primary)]/60" strokeWidth={1.5} />}
          </h4>
          {variancePct !== null && (
            <Badge className={variance >= 0
              ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20'
              : 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20'}>
              {variance >= 0 ? '+' : ''}{variancePct}%
            </Badge>
          )}
        </div>
        <p className="text-2xl font-bold text-[color:var(--color-text-primary)]">{fmt(totalRev)}</p>
        <p className="text-xs text-[color:var(--color-text-muted)] mt-0.5">
          {label}: F&amp;B {fmt(sales)}{isRoomOutlet ? ` • Rooms ${fmt(roomRevenue)}` : ''}
        </p>

        <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">FC %</p>
              <p className={`text-sm font-semibold ${foodCostPct != null && foodCostPct > 35 ? 'text-[#C45A41]' : 'text-[color:var(--color-text-primary)]'}`}>
                {foodCostPct != null ? `${foodCostPct}%` : <span className="text-[color:var(--color-text-muted)] text-xs font-normal">Pending</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">BC %</p>
              <p className={`text-sm font-semibold ${bevCostPct != null && bevCostPct > 30 ? 'text-[#C45A41]' : 'text-[color:var(--color-text-primary)]'}`}>
                {bevCostPct != null ? `${bevCostPct}%` : <span className="text-[color:var(--color-text-muted)] text-xs font-normal">Pending</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Days</p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)]">{daysReported || 0}</p>
            </div>
          </div>
        </div>

        {covers > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
            <p className="text-xs text-[color:var(--color-text-muted)] font-medium mb-2">APC (Covers: {covers.toLocaleString('en-IN')})</p>
            <div className="grid grid-cols-1 gap-1 text-center">
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Total APC</p>
                <p className="text-sm font-semibold text-[color:var(--color-text-primary)]">{totalApc != null ? fmt(totalApc) : '—'}</p>
              </div>
            </div>
          </div>
        )}

        {isRoomOutlet && (
          <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
            <p className="text-xs text-[color:var(--color-text-muted)] font-medium mb-2 flex items-center gap-1">
              <BedDouble className="w-3.5 h-3.5" /> Rooms
            </p>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Avg Occupancy</p>
                <p className="text-sm font-semibold text-[color:var(--color-text-primary)]">{occupancy > 0 ? `${occupancy}%` : '—'}</p>
              </div>
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-[0.05em]">Avg ARR</p>
                <p className="text-sm font-semibold text-[color:var(--color-text-primary)]">{arr > 0 ? fmt(arr) : '—'}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OutletCard({ outlet }) {
  if (!outlet.has_entry) {
    return <EmptyOutletCard outlet={outlet} />;
  }

  // Canonical per-outlet totals — must match the Overview Hero + KPI cards.
  // `outlet.variance` / `outlet.total_budget` are computed backend-side to
  // include Room Revenue + Room Budget (see routes/dashboard.py).
  const totalSales = (outlet.total_revenue != null)
    ? outlet.total_revenue
    : ((outlet.net_sales || 0) + (outlet.room_revenue || 0));
  const totalBudget = (outlet.total_budget != null)
    ? outlet.total_budget
    : ((outlet.budget || 0) + (outlet.room_revenue_budget || 0));
  const variancePositive = (outlet.variance ?? 0) >= 0;
  const channels = [
    { label: 'Dine-in', value: outlet.channel_dine_in || 0 },
    { label: 'Swiggy', value: outlet.channel_swiggy || 0 },
    { label: 'Zomato', value: outlet.channel_zomato || 0 },
    { label: 'Other', value: outlet.channel_other || 0 },
  ];
  const hasChannels = channels.some(c => c.value > 0);

  return (
    <Card className="border-[#E2E8DF] shadow-sm metric-card" data-testid={`outlet-card-${outlet.outlet_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-[color:var(--color-text-primary)] text-sm">{outlet.outlet_name}</h4>
          <Badge
            className={variancePositive
              ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20'
              : 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20'
            }
          >
            {variancePositive ? '+' : ''}{outlet.variance_pct}%
          </Badge>
        </div>
        <p className="text-2xl font-bold text-[color:var(--color-text-primary)]">{fmt(totalSales)}</p>
        <p className="text-xs text-[color:var(--color-text-muted)] mt-0.5">Budget: {fmt(totalBudget)}</p>

        {/* LY Same Date & Same Day (Weekday) */}
        <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg">
              <p className="text-[10px] uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] font-medium">
                LY Same Date{outlet.ly_same_date_day ? ` (${outlet.ly_same_date_day.slice(0,3)})` : ''}
              </p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)] mt-0.5">
                {outlet.last_year_net_sales != null ? fmt(outlet.last_year_net_sales) : '—'}
              </p>
              {outlet.last_year_net_sales != null && outlet.last_year_net_sales > 0 && (
                <p className={`text-[10px] font-medium mt-0.5 ${outlet.net_sales >= outlet.last_year_net_sales ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                  data-testid={`yoy-same-date-${outlet.outlet_id}`}>
                  {outlet.net_sales >= outlet.last_year_net_sales ? '+' : ''}{((outlet.net_sales - outlet.last_year_net_sales) / outlet.last_year_net_sales * 100).toFixed(1)}% YoY
                </p>
              )}
            </div>
            <div className="p-2 bg-[color:var(--color-primary)]/5 rounded-lg">
              <p className="text-[10px] uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] font-medium">
                LY Same Day{outlet.ly_same_day_name ? ` (${outlet.ly_same_day_name.slice(0,3)})` : ''}
              </p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)] mt-0.5">
                {outlet.ly_same_day_sales != null ? fmt(outlet.ly_same_day_sales) : '—'}
              </p>
              {outlet.ly_same_day_sales != null && outlet.ly_same_day_sales > 0 && (
                <p className={`text-[10px] font-medium mt-0.5 ${outlet.net_sales >= outlet.ly_same_day_sales ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}
                  data-testid={`yoy-same-day-${outlet.outlet_id}`}>
                  {outlet.net_sales >= outlet.ly_same_day_sales ? '+' : ''}{((outlet.net_sales - outlet.ly_same_day_sales) / outlet.ly_same_day_sales * 100).toFixed(1)}% YoY
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Food Sale / Beverage Sale */}
        <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className="text-xs text-[color:var(--color-text-muted)]">Food Sale</p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)]">{fmt(outlet.food_sale)}</p>
            </div>
            <div>
              <p className="text-xs text-[color:var(--color-text-muted)]">Beverage Sale</p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)]">{fmt(outlet.beverage_sale)}</p>
            </div>
          </div>
        </div>

        {/* Cost percentages + NPS */}
        <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-[color:var(--color-text-muted)]">Food Cost %</p>
              <p className={`text-sm font-semibold ${outlet.food_cost_pct != null && outlet.food_cost_pct > 35 ? 'text-[#C45A41]' : 'text-[color:var(--color-text-primary)]'}`} data-testid="food-cost-metric">
                {outlet.food_cost_pct != null ? `${outlet.food_cost_pct}%` : <span className="text-[color:var(--color-text-muted)] text-xs font-normal">Pending</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-[color:var(--color-text-muted)]">Bev Cost %</p>
              <p className={`text-sm font-semibold ${outlet.beverage_cost_pct != null && outlet.beverage_cost_pct > 30 ? 'text-[#C45A41]' : 'text-[color:var(--color-text-primary)]'}`} data-testid="beverage-cost-metric">
                {outlet.beverage_cost_pct != null ? `${outlet.beverage_cost_pct}%` : <span className="text-[color:var(--color-text-muted)] text-xs font-normal">Pending</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-[color:var(--color-text-muted)]">NPS</p>
              <p className="text-sm font-semibold text-[color:var(--color-text-primary)]" data-testid="nps-day-metric">{outlet.nps_day > 0 ? outlet.nps_day : '—'}</p>
            </div>
          </div>
        </div>

        {/* APC Metrics */}
        {(outlet.number_of_covers > 0) && (
          <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
            <p className="text-xs text-[color:var(--color-text-muted)] font-medium mb-2">APC (Covers: {outlet.number_of_covers})</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">Food APC</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]" data-testid="food-apc-metric">{fmt(outlet.food_apc)}</p>
              </div>
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">Bev APC</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]" data-testid="bev-apc-metric">{fmt(outlet.beverage_apc)}</p>
              </div>
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">Total APC</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]" data-testid="total-apc-metric">{fmt(outlet.total_apc)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Channel Split */}
        {hasChannels && (
          <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
            <p className="text-xs text-[color:var(--color-text-muted)] font-medium mb-2">Channel Breakup</p>
            <div className="grid grid-cols-4 gap-1 text-center">
              {channels.map(ch => (
                <div key={ch.label}>
                  <p className="text-[10px] text-[color:var(--color-text-muted)]">{ch.label}</p>
                  <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">{fmt(ch.value)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rooms (only if enabled) */}
        {outlet.rooms_enabled && (outlet.room_revenue > 0 || outlet.rooms_sold > 0) && (
          <div className="mt-3 pt-3 border-t border-[#E2E8DF]" data-testid={`rooms-card-${outlet.outlet_id}`}>
            <p className="text-xs text-[color:var(--color-text-muted)] font-medium mb-2 flex items-center gap-1">
              <BedDouble className="w-3.5 h-3.5" /> Rooms
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">Sold</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">{outlet.rooms_sold}</p>
              </div>
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">Occupancy</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">
                  {outlet.rooms_available > 0 ? `${outlet.occupancy_pct}%` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">Revenue</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">{fmt(outlet.room_revenue)}</p>
              </div>
              <div>
                <p className="text-[10px] text-[color:var(--color-text-muted)]">ARR</p>
                <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">
                  {outlet.rooms_sold > 0 ? fmt(outlet.arr) : '—'}
                </p>
              </div>
            </div>
            {(outlet.room_revenue_budget > 0 || outlet.room_revenue_last_year > 0) && (
              <div className="grid grid-cols-2 gap-2 mt-2 text-center">
                {outlet.room_revenue_budget > 0 && (
                  <div>
                    <p className="text-[10px] text-[color:var(--color-text-muted)]">Budget</p>
                    <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">{fmt(outlet.room_revenue_budget)}</p>
                  </div>
                )}
                {outlet.room_revenue_last_year > 0 && (
                  <div>
                    <p className="text-[10px] text-[color:var(--color-text-muted)]">Last Year</p>
                    <p className="text-xs font-semibold text-[color:var(--color-text-primary)]">{fmt(outlet.room_revenue_last_year)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Section-wise Status */}
        {outlet.section_submissions && Object.keys(outlet.section_submissions).length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E2E8DF]">
            <p className="text-xs text-[color:var(--color-text-muted)] font-medium mb-2">Section Status</p>
            <div className="space-y-1">
              {[
                { key: 'sales_data', label: 'Sales Data' },
                { key: 'kitchen_inventory', label: 'Kitchen Inventory' },
                ...(outlet.alcohol_enabled === false ? [] : [{ key: 'bar_inventory', label: 'Bar Inventory' }]),
                { key: 'other_issues', label: 'Store Issues' },
                { key: 'customer_experience', label: 'Customer Exp.' },
                { key: 'notes', label: 'Notes' }
              ].map(sec => {
                const ss = outlet.section_submissions[sec.key];
                return (
                  <div key={sec.key} className="flex items-center justify-between" data-testid={`section-status-${sec.key}`}>
                    <span className="text-[10px] text-[color:var(--color-text-muted)]">{sec.label}</span>
                    <div className="flex items-center gap-1.5">
                      {ss?.locked ? (
                        <>
                          <span className="text-[10px] text-[#2D6A4F] font-medium">Submitted</span>
                          {ss.edit_count > 0 && (
                            <span className="text-[9px] text-[#D4A017] bg-[#D4A017]/10 px-1 rounded">
                              Edited {ss.edit_count}x
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-[#C45A41]">Pending</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================================================
   EXECUTIVE OVERVIEW REDESIGN — Feb 2026
   These components consume the SAME data the classic Overview
   already fetches (portfolio_totals, daily_trend, cost_efficiency).
   No new backend aggregation. Projection is a small client-side
   run-rate calc on the existing daily_trend series.
   ============================================================ */

// ---------- helpers ----------
const compactINR = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const signedINR = (v) => {
  const n = Number(v || 0);
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${compactINR(Math.abs(n)).replace('₹', '₹')}`;
};

// Run-rate projection over the currently-open MTD period.
// completed  – rows with numeric total_net_sales > 0 (real reporting)
// missing    – budget-only rows extending into the future part of month
// window     – latest 7 completed observations when available
// Returns { projectedTotal, seriesForChart, hasProjection }
function computeMtdProjection({ dailyTrend = [], daysInMonth, year, month }) {
  const rows = Array.isArray(dailyTrend) ? dailyTrend : [];
  if (rows.length === 0 || !daysInMonth) return { projectedTotal: null, seriesForChart: [], hasProjection: false };

  const completed = rows.filter(r => Number(r.total_net_sales) > 0);
  if (completed.length < 3) {
    // Emit a chart-friendly series with just actual + budget (no projection line)
    const series = [];
    for (let d = 1; d <= daysInMonth; d += 1) {
      const dt = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const match = rows.find(r => r.date === dt);
      series.push({
        date: dt,
        day: d,
        actual: match && Number(match.total_net_sales) > 0 ? Number(match.total_net_sales) : null,
        budget: match ? Number(match.cumulative_budget || 0) : null,
        projected: null,
      });
    }
    return { projectedTotal: null, seriesForChart: series, hasProjection: false };
  }

  // Prefer recent-7 window; fall back to all completed observations.
  const window = completed.slice(-Math.min(7, completed.length));
  const runRate = window.reduce((s, r) => s + Number(r.total_net_sales || 0), 0) / window.length;

  // Compose the chart series over the full month:
  //   day <= lastCompletedDay → actual (solid), projected: null after last actual
  //   day > lastCompletedDay  → projected: runRate * pending days from lastActualCumulative
  //   budget continues throughout via daily_trend.cumulative_budget when present.
  const byDate = new Map(rows.map(r => [r.date, r]));
  const series = [];
  let cumulativeActual = 0;
  let lastActualDay = null;
  let lastActualCum = 0;
  for (let d = 1; d <= daysInMonth; d += 1) {
    const dt = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const row = byDate.get(dt);
    const dayActual = row && Number(row.total_net_sales) > 0 ? Number(row.total_net_sales) : null;
    if (dayActual != null) {
      cumulativeActual += dayActual;
      lastActualDay = d;
      lastActualCum = cumulativeActual;
    }
    series.push({
      date: dt,
      day: d,
      actual: dayActual != null ? cumulativeActual : null,
      budget: row ? Number(row.cumulative_budget || 0) : null,
      projected: null,
    });
  }
  // Fill projection from lastActualDay+1 through daysInMonth (dashed extension).
  // The projection SERIES also emits a duplicated value at lastActualDay so the
  // dashed line starts EXACTLY on the final actual point (no visual gap).
  if (lastActualDay != null && lastActualDay < daysInMonth) {
    const idxLast = series.findIndex(p => p.day === lastActualDay);
    if (idxLast >= 0) series[idxLast].projected = lastActualCum;
    let running = lastActualCum;
    for (let d = lastActualDay + 1; d <= daysInMonth; d += 1) {
      running += runRate;
      const idx = series.findIndex(p => p.day === d);
      if (idx >= 0) series[idx].projected = running;
    }
    return {
      projectedTotal: running,
      seriesForChart: series,
      hasProjection: true,
      lastActualDay,
    };
  }
  return { projectedTotal: lastActualCum, seriesForChart: series, hasProjection: false, lastActualDay };
}

// ---------- Executive Pulse ----------
// Presentation-only metrics mapper. Reads canonical fields already computed
// by /dashboard/daily, /dashboard/mtd, /dashboard/ytd, /insights/five-minute
// and normalizes them into a single shape consumed by ExecutivePulse,
// OutletPulseCard, OutletBudgetPerformanceChart, RevenueIntelligencePanel,
// and PortfolioSignals. NO new calculations — same-KPI-same-scope-same-period
// must equal the values shown in the Detailed Comparison table.
//
// `kind`: 'daily' | 'mtd' | 'ytd'
// `pt`: portfolio_totals (backend canonical)
// `outlets`: per-outlet decorated rows (backend canonical)
// `costEfficiency`: rows from /insights/five-minute cost_efficiency block
// `lyPulse` (optional): a `/dashboard/daily?date={data_through}` payload
//    used when the active period is NOT daily, so LY Same Date / LY Same Day
//    can still anchor to the latest completed comparison day.
function buildOverviewMetrics({ kind = 'daily', pt = null, outlets = [], costEfficiency = [], lyPulse = null } = {}) {
  const rows = (outlets || []).filter(countsTowardPortfolio);
  const daily = kind === 'daily';

  // Revenue basis — CANONICAL (F&B + Room). Comes straight from backend.
  const revenue = Number(pt?.total_revenue || 0);
  const fbSales = Number(pt?.fb_sales || 0);
  const roomRev = Number(pt?.room_revenue || 0);
  const budget = Number(pt?.budget || 0);
  const budgetFb = Number(pt?.budget_fb || 0);
  const budgetRoom = Number(pt?.budget_room || 0);
  const variance_amt = Number(pt?.variance_amt || 0);
  const variance_pct = pt?.variance_pct != null ? Number(pt.variance_pct) : null;
  const yoy_pct = pt?.yoy_pct != null ? Number(pt.yoy_pct) : null;
  const covers = Number(pt?.covers || 0);
  const apc = pt?.apc != null ? Number(pt.apc) : null;
  const fc_pct = pt?.food_cost_pct != null ? Number(pt.food_cost_pct) : null;
  const bc_pct = pt?.beverage_cost_pct != null ? Number(pt.beverage_cost_pct) : null;

  // Cost targets (from /insights/five-minute) — the SAME rows the detailed
  // Cost & Efficiency block already reads. NO parallel calculation.
  const ce = costEfficiency || [];
  const fc_target = ce.find(c => c.metric === 'Food Cost %')?.budget ?? null;
  const bc_target = ce.find(c => c.metric === 'Beverage Cost %')?.budget ?? null;
  const elecRow = ce.find(c => c.metric === 'Electricity Cost') || null;
  const gasRow = ce.find(c => c.metric === 'Gas Cost') || null;
  const energyActual = (Number(elecRow?.actual || 0) + Number(gasRow?.actual || 0)) || null;
  const energyBudget = (elecRow?.budget != null || gasRow?.budget != null)
    ? (Number(elecRow?.budget || 0) + Number(gasRow?.budget || 0)) : null;

  // ─── LY Same Date / LY Same Day ───
  // These are DAILY benchmark metrics. On Daily scope: read from portfolio's
  // outlet rows directly. On MTD/YTD/Range scope: `lyPulse` is a
  // `/dashboard/daily?date={data_through}` payload that lets us continue to
  // reuse the existing backend LY-date logic (never recreated in frontend).
  const lyPool = daily
    ? rows.filter(o => o.has_entry)
    : ((lyPulse?.outlets || []).filter(o => countsTowardPortfolio(o) && o.has_entry));

  const currentFb = daily
    ? fbSales
    : lyPool.reduce((s, o) => s + Number(o.net_sales || 0), 0);

  // LY Same Date — comparison denominator only counts outlets that actually
  // report an LY benchmark, so % comparison is like-for-like.
  const sameDatePool = lyPool.filter(o => Number(o.last_year_net_sales || 0) > 0);
  const lySameDate = sameDatePool.reduce((s, o) => s + Number(o.last_year_net_sales || 0), 0);
  const currentFbSameDatePool = sameDatePool.reduce((s, o) => s + Number(o.net_sales || 0), 0);
  const lySameDatePct = lySameDate > 0
    ? ((currentFbSameDatePool - lySameDate) / lySameDate) * 100 : null;
  const lySameDateLabel = daily
    ? (rows.find(o => o.ly_same_date_day)?.ly_same_date_day || null)
    : (lyPulse?.outlets?.find(o => o.ly_same_date_day)?.ly_same_date_day || null);

  const sameDayPool = lyPool.filter(o => Number(o.ly_same_day_sales || 0) > 0);
  const lySameDay = sameDayPool.reduce((s, o) => s + Number(o.ly_same_day_sales || 0), 0);
  const currentFbSameDayPool = sameDayPool.reduce((s, o) => s + Number(o.net_sales || 0), 0);
  const lySameDayPct = lySameDay > 0
    ? ((currentFbSameDayPool - lySameDay) / lySameDay) * 100 : null;
  const lySameDayName = daily
    ? (rows.find(o => o.ly_same_day_name)?.ly_same_day_name || null)
    : (lyPulse?.outlets?.find(o => o.ly_same_day_name)?.ly_same_day_name || null);
  const lySameDayDate = daily
    ? (rows.find(o => o.ly_same_day_date)?.ly_same_day_date || null)
    : (lyPulse?.outlets?.find(o => o.ly_same_day_date)?.ly_same_day_date || null);
  const lyAnchorDate = daily
    ? null
    : (lyPulse?.date || null);

  // Coverage — comparable outlets / reporting outlets.
  const reporting = lyPool.length;
  const sameDateCoverage = sameDatePool.length;
  const sameDayCoverage = sameDayPool.length;

  // Full-Month Budget — canonical from backend (MTD only). Never derived.
  const fullMonthBudget = pt?.full_month_budget != null ? Number(pt.full_month_budget) : null;
  const fullMonthBudgetAvailable = !!pt?.full_month_budget_available;

  return {
    kind,
    revenue, fbSales, roomRev, budget, budgetFb, budgetRoom,
    variance_amt, variance_pct, yoy_pct,
    covers, apc, fc_pct, bc_pct, fc_target, bc_target,
    energyActual, energyBudget, elecRow, gasRow,
    // LY block
    currentFb,
    lySameDate, lySameDatePct, lySameDateLabel, lySameDateCoverage: sameDateCoverage,
    lySameDay, lySameDayPct, lySameDayName, lySameDayDate, lySameDayCoverage: sameDayCoverage,
    lyAnchorDate,
    lyPoolReporting: reporting,
    // MTD full-month
    fullMonthBudget, fullMonthBudgetAvailable,
    // Outlets included in portfolio
    outletsIncluded: rows.length,
  };
}

function ExecCard({ children, className = '', testId, size = 'md', variant = 'kpi', interactive = false }) {
  const pads = size === 'lg' ? 'p-6 lg:p-7' : 'p-4 lg:p-5';
  // Level A → surface-hero (Total Revenue, Revenue Intelligence, Rooms).
  // Level B → surface-kpi is the default; achieved via existing
  // `bg-white rounded-2xl border` classes + global CSS rule that lifts
  // every ExecCard uniformly. No extra class needed.
  // Level C → surface-support for filters / table wrappers.
  const variantCls = variant === 'hero'
    ? 'surface-hero'
    : (variant === 'support' ? 'surface-support' : '');
  const isHero = variant === 'hero';
  // Hero cards need extra top padding to clear the 3px accent + tint bleed.
  const heroTopPad = isHero ? 'pt-6 lg:pt-7' : '';
  const baseCls = isHero
    // Hero uses .surface-hero exclusively (owns its border/shadow/bg)
    ? `relative ${variantCls} ${pads} ${heroTopPad}`
    // KPI (default) uses the existing bg-white/rounded-2xl/border baseline
    // which is globally lifted by index.css .rounded-2xl.border.bg-white
    : `relative bg-white rounded-2xl border ${pads}`;
  const inter = interactive ? 'is-interactive cursor-pointer' : '';
  return (
    <div
      data-testid={testId}
      className={`${baseCls} ${variantCls === 'surface-support' ? variantCls : ''} ${inter} ${className}`}>
      {children}
    </div>
  );
}

function ExecutivePulse({ metrics, hasRooms, testIdPrefix = 'exec' }) {
  const m = metrics || {};
  const revenue = Number(m.revenue || 0);
  const budget = Number(m.budget || 0);
  const varAmt = Number(m.variance_amt || 0);
  const varPct = m.variance_pct != null ? Number(m.variance_pct) : null;
  const fb = Number(m.fbSales || 0);
  const rooms = Number(m.roomRev || 0);
  const yoy = m.yoy_pct != null ? Number(m.yoy_pct) : null;
  const fcActual = m.fc_pct;
  const bcActual = m.bc_pct;
  const covers = Number(m.covers || 0);
  const apc = m.apc;
  const fcTarget = m.fc_target;
  const bcTarget = m.bc_target;
  const energyActual = m.energyActual;
  const energyBudget = m.energyBudget;
  const energyVar = energyActual != null && energyBudget ? (energyActual - energyBudget) : null;
  const pctToBudget = budget > 0 ? Math.min(150, (revenue / budget) * 100) : 0;
  const varUp = varAmt >= 0;

  // LY blocks (Daily anchor: current day; MTD/YTD anchor: latest completed day)
  const lySameDate = m.lySameDate;
  const lySameDatePct = m.lySameDatePct;
  const lySameDay = m.lySameDay;
  const lySameDayPct = m.lySameDayPct;
  const lySameDayName = m.lySameDayName;
  const lyAnchorDate = m.lyAnchorDate; // present on MTD/YTD/Range scopes

  const LyCard = ({ label, contextLabel, lyVal, lyPct, coverage, reporting, testId }) => {
    const hasVal = Number(lyVal || 0) > 0;
    return (
      <ExecCard testId={testId} className="lg:col-span-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md truncate max-w-full">{label}</div>
            {contextLabel && <div className="text-[10px] text-[#9CA3AF] mt-0.5 truncate">{contextLabel}</div>}
          </div>
          {hasVal && lyPct != null && (
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap
              ${lyPct >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {lyPct >= 0 ? '+' : ''}{lyPct.toFixed(1)}%
            </span>
          )}
        </div>
        <div className="text-[28px] lg:text-[30px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-2">
          {hasVal ? compactINR(lyVal) : <span className="text-[#9CA3AF]">—</span>}
        </div>
        <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2">
          {hasVal
            ? <>Current F&amp;B <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(m.currentFb)}</span></>
            : <span className="italic text-[#9CA3AF]">Comparison unavailable</span>}
        </div>
        {reporting > 0 && coverage < reporting && (
          <div className="text-[10px] text-[#9A7818] mt-1" data-testid={`${testId}-coverage`}>
            Comparison available {coverage}/{reporting} outlets
          </div>
        )}
      </ExecCard>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4 mb-4 lg:mb-5" data-testid={`${testIdPrefix}-pulse`}>
      {/* Dominant Revenue card — spans 6 cols on desktop */}
      <ExecCard variant="hero" size="lg" testId={`${testIdPrefix}-revenue-card`} className="lg:col-span-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Total Revenue</div>
            <div className="text-[36px] lg:text-[44px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-1.5"
              data-testid={`${testIdPrefix}-revenue-value`}>
              {compactINR(revenue)}
            </div>
          </div>
          <div className={`flex items-center gap-1 text-[13px] font-semibold px-2.5 py-1 rounded-full
            ${varUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
            data-testid={`${testIdPrefix}-revenue-variance`}>
            {varUp ? <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} /> : <ArrowDownRight className="w-3.5 h-3.5" strokeWidth={2.5} />}
            {pctFmt(varPct, { signed: true })}
          </div>
        </div>
        <div className="text-[12px] text-[color:var(--color-text-muted)] mb-2">
          Budget <span className="tabular-nums text-[color:var(--color-text-primary)] font-medium" data-testid={`${testIdPrefix}-budget-value`}>{compactINR(budget)}</span>
          <span className="mx-2 text-[#C7D0CB]">·</span>
          <span className={`tabular-nums font-medium ${varUp ? 'text-emerald-700' : 'text-rose-700'}`}>
            {varUp ? '+' : ''}{compactINR(varAmt)}
          </span> vs Budget
        </div>
        <div className="h-2 w-full rounded-full bg-[#EEF2EF] overflow-hidden" title={`${pctToBudget.toFixed(1)}% of budget`}>
          <div className={`h-full ${varUp ? 'bg-emerald-500' : 'bg-rose-500'} transition-all`}
            style={{ width: `${Math.max(2, Math.min(100, pctToBudget))}%` }} />
        </div>
        {hasRooms && (
          <div className="mt-3 pt-3 border-t border-[#EEF2EF] text-[11px] text-[color:var(--color-text-muted)]">
            F&B <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(fb)}</span>
            <span className="mx-2 text-[#C7D0CB]">·</span>
            Rooms <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(rooms)}</span>
          </div>
        )}
      </ExecCard>

      {/* YoY */}
      <ExecCard testId={`${testIdPrefix}-yoy-card`} className="lg:col-span-3">
        <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">YoY Growth</div>
        <div className={`text-[30px] leading-none font-bold tabular-nums mt-2
          ${yoy == null ? 'text-[#9CA3AF]' : (yoy >= 0 ? 'text-emerald-700' : 'text-rose-700')}`}
          data-testid={`${testIdPrefix}-yoy-value`}>
          {yoy == null ? '—' : pctFmt(yoy, { signed: true })}
        </div>
        <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2">
          {yoy == null ? 'LY benchmark unavailable' : (yoy >= 0 ? 'Growing vs last year' : 'Down vs last year')}
        </div>
      </ExecCard>

      {/* APC */}
      <ExecCard testId={`${testIdPrefix}-apc-card`} className="lg:col-span-3">
        <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">APC</div>
        <div className="text-[30px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-2"
          data-testid={`${testIdPrefix}-apc-value`}>
          {apc != null && apc > 0 ? compactINR(apc) : '—'}
        </div>
        <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2 flex items-center gap-1">
          <Users className="w-3 h-3" strokeWidth={2} />
          <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{covers.toLocaleString('en-IN')}</span> Covers
        </div>
      </ExecCard>

      {/* Food Cost */}
      <ExecCard testId={`${testIdPrefix}-fc-card`} className="lg:col-span-3">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Food Cost</div>
          <span className="icon-tile icon-tile--sm"><ChefHat strokeWidth={2.25} /></span>
        </div>
        <div className="text-[30px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-2"
          data-testid={`${testIdPrefix}-fc-value`}>
          {fcActual != null ? pctFmt(fcActual) : '—'}
        </div>
        <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2">
          Target <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{fcTarget != null ? pctFmt(fcTarget) : '—'}</span>
          {fcActual != null && fcTarget != null && (
            <span className={`ml-2 font-medium ${fcActual <= fcTarget ? 'text-emerald-700' : 'text-rose-700'}`}>
              {fcActual <= fcTarget ? `${(fcTarget - fcActual).toFixed(1)} pts better` : `${(fcActual - fcTarget).toFixed(1)} pts over`}
            </span>
          )}
        </div>
      </ExecCard>

      {/* Beverage Cost */}
      <ExecCard testId={`${testIdPrefix}-bc-card`} className="lg:col-span-3">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Beverage Cost</div>
          <span className="icon-tile icon-tile--sm"><Wine strokeWidth={2.25} /></span>
        </div>
        <div className="text-[30px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-2"
          data-testid={`${testIdPrefix}-bc-value`}>
          {bcActual != null ? pctFmt(bcActual) : '—'}
        </div>
        <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2">
          Target <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{bcTarget != null ? pctFmt(bcTarget) : '—'}</span>
          {bcActual != null && bcTarget != null && (
            <span className={`ml-2 font-medium ${bcActual <= bcTarget ? 'text-emerald-700' : 'text-rose-700'}`}>
              {bcActual <= bcTarget ? `${(bcTarget - bcActual).toFixed(1)} pts better` : `${(bcActual - bcTarget).toFixed(1)} pts over`}
            </span>
          )}
        </div>
      </ExecCard>

      {/* LY Same Date */}
      <LyCard
        label={`LY Same Date${m.lySameDateLabel ? ' · ' + m.lySameDateLabel.slice(0, 3) : ''}`}
        contextLabel={lyAnchorDate ? `Benchmark: ${lyAnchorDate}` : null}
        lyVal={lySameDate}
        lyPct={lySameDatePct}
        coverage={m.lySameDateCoverage || 0}
        reporting={m.lyPoolReporting || 0}
        testId={`${testIdPrefix}-ly-same-date`}
      />

      {/* LY Same Day */}
      <LyCard
        label={`LY Same Day${lySameDayName ? ' · ' + lySameDayName.slice(0, 3).toUpperCase() : ''}`}
        contextLabel={m.lySameDayDate ? `${m.lySameDayDate}` : null}
        lyVal={lySameDay}
        lyPct={lySameDayPct}
        coverage={m.lySameDayCoverage || 0}
        reporting={m.lyPoolReporting || 0}
        testId={`${testIdPrefix}-ly-same-day`}
      />

      {/* Energy */}
      <ExecCard testId={`${testIdPrefix}-energy-card`} className="lg:col-span-4">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Energy Cost</div>
          <span className="icon-tile icon-tile--sm"><Zap strokeWidth={2.25} /></span>
        </div>
        <div className="flex items-baseline gap-3 mt-2 flex-wrap">
          <div className="text-[28px] lg:text-[30px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums"
            data-testid={`${testIdPrefix}-energy-value`}>
            {energyActual != null ? compactINR(energyActual) : '—'}
          </div>
          {energyBudget != null && energyVar != null && (
            <div className={`text-[12px] font-semibold ${energyVar > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
              {energyVar > 0 ? '+' : ''}{compactINR(energyVar)}
              <span className="ml-1 text-[11px] font-medium">
                {energyBudget > 0 ? `(${((energyVar / energyBudget) * 100).toFixed(0)}% ${energyVar > 0 ? 'over' : 'under'})` : ''}
              </span>
            </div>
          )}
        </div>
        <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2">
          Budget <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{energyBudget != null ? compactINR(energyBudget) : '—'}</span>
        </div>
      </ExecCard>
    </div>
  );
}

// ---------- Revenue Trajectory ----------
function RevenueTrajectoryChart({ series, hasProjection, budget, projectedTotal, actualCumThroughToday, testId }) {
  const ready = Array.isArray(series) && series.length > 0;
  const currency = (v) => v == null ? '' : `₹${Math.round(v).toLocaleString('en-IN')}`;
  const projVar = projectedTotal != null && budget > 0
    ? ((projectedTotal - budget) / budget) * 100 : null;
  return (
    <ExecCard testId={testId} className="mb-4 lg:mb-5" size="lg">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A8B82] font-semibold flex items-center gap-1.5">
            <Activity className="w-3 h-3" strokeWidth={2.5} /> Revenue Trajectory
          </div>
          <div className="text-[13px] text-[color:var(--color-text-muted)] mt-1">Actual · Budget · Projected</div>
        </div>
        <div className="text-right">
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Projected Period Close</div>
          <div className="text-[26px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-1.5">
            {hasProjection && projectedTotal != null ? compactINR(projectedTotal) : (actualCumThroughToday ? compactINR(actualCumThroughToday) : '—')}
          </div>
          {hasProjection && projVar != null && (
            <div className={`text-[12px] font-semibold mt-1 ${projVar >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
              data-testid={`${testId}-projected-variance`}>
              Budget <span className="text-[color:var(--color-text-muted)] font-normal">{compactINR(budget)}</span>
              <span className="mx-1">·</span>
              Projected {projVar >= 0 ? '+' : ''}{projVar.toFixed(1)}%
            </div>
          )}
          {!hasProjection && (
            <div className="text-[11px] text-[#9CA3AF] italic mt-1" data-testid={`${testId}-projection-unavailable`}>
              Projection unavailable — need 3+ reported days
            </div>
          )}
        </div>
      </div>
      <div className="h-[260px] lg:h-[300px]">
        {ready ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
              <defs>
                <linearGradient id="actualFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#EEF2EF" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: '#7A8B82', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => compactINR(v).replace('₹', '')}
                tick={{ fill: '#7A8B82', fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const row = payload[0]?.payload || {};
                  const varDay = (row.actual != null && row.budget != null)
                    ? row.actual - row.budget : null;
                  return (
                    <div className="bg-white border border-[#E2E8DF] rounded-lg shadow-lg p-2.5 text-[11px]">
                      <div className="font-semibold text-[color:var(--color-text-primary)] mb-1">Day {label}</div>
                      {row.actual != null && <div>Actual: <span className="tabular-nums font-medium">{currency(row.actual)}</span></div>}
                      {row.budget != null && <div>Budget: <span className="tabular-nums font-medium">{currency(row.budget)}</span></div>}
                      {row.projected != null && <div className="text-[#8B7A00]">Projected: <span className="tabular-nums font-medium">{currency(row.projected)}</span></div>}
                      {varDay != null && (
                        <div className={`mt-1 font-medium ${varDay >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          Variance: {varDay >= 0 ? '+' : ''}{currency(varDay)}
                        </div>
                      )}
                    </div>
                  );
                }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconSize={10} />
              <Area type="monotone" dataKey="actual" name="Actual" stroke="var(--color-primary)" strokeWidth={2.5}
                fill="url(#actualFill)" connectNulls={false} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="budget" name="Budget" stroke="#829C7A" strokeWidth={1.5}
                strokeDasharray="6 4" dot={false} activeDot={false} />
              <Line type="monotone" dataKey="projected" name="Projected" stroke="#C7A600" strokeWidth={2}
                strokeDasharray="2 3" dot={false} activeDot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-[12px] text-[#9CA3AF]">
            No trajectory data available
          </div>
        )}
      </div>
    </ExecCard>
  );
}

// ---------- Outlet Pulse Grid ----------
function OutletPulseGrid({ outlets, kind, testId }) {
  const rows = (outlets || []).filter(countsTowardPortfolio);
  if (!rows.length) {
    return <ExecCard testId={testId} className="text-center py-10 text-[13px] text-[#9CA3AF]">No outlets in scope</ExecCard>;
  }
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[13px] font-semibold text-[color:var(--color-text-primary)] uppercase tracking-[0.1em]">Outlet Pulse</h2>
        <span className="text-[11px] text-[#7A8B82]">{rows.length} outlets</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4 mb-4 lg:mb-5">
        {rows.map((o) => <OutletPulseCard key={o.outlet_id || o.outlet_name} outlet={o} kind={kind} />)}
      </div>
    </div>
  );
}

function OutletPulseCard({ outlet, kind }) {
  const daily = kind === 'daily';

  // ── Canonical field mapping — MIRROR of OutletComparisonTable.pick(o) ──
  // Any drift here will break reconciliation with the Detailed Comparison.
  const fbSales = daily ? Number(outlet.net_sales || 0) : Number(outlet.total_sales || 0);
  const roomRev = daily ? Number(outlet.room_revenue || 0) : Number(outlet.total_room_revenue || 0);
  const revenue = outlet.total_revenue != null ? Number(outlet.total_revenue) : (fbSales + roomRev);
  const fbBudget = daily ? Number(outlet.budget || 0) : Number(outlet.total_budget || 0);
  const roomBudget = daily
    ? Number(outlet.room_revenue_budget || 0)
    : Number(outlet.total_room_budget || 0);
  const budget = fbBudget + roomBudget;
  const varAmt = revenue - budget;
  const varPct = budget > 0 ? (varAmt / budget) * 100 : null;
  const varUp = varAmt >= 0;

  // FC/BC/APC — mirror OutletComparisonTable adapter (daily → *_pct; mtd → avg_*_pct).
  const fcPct = daily ? outlet.food_cost_pct : outlet.avg_food_cost_pct;
  const bcPct = daily ? outlet.beverage_cost_pct : outlet.avg_beverage_cost_pct;
  const apc = outlet.total_apc;
  const covers = daily ? outlet.number_of_covers : outlet.total_covers;

  // YoY — like-for-like:
  //   Daily : F&B  vs LY Same-Day F&B  (outlet.ly_same_day_sales)
  //   MTD   : Total (F&B + Rooms) vs LY (F&B + Rooms) using ly_mtd_excel + total_room_ly
  let yoyPct = null;
  if (daily) {
    const ly = Number(outlet.ly_same_day_sales || 0);
    if (ly > 0) yoyPct = ((fbSales - ly) / ly) * 100;
  } else {
    const lyFb = Number(outlet.ly_mtd_excel || 0);
    const lyRoom = Number(outlet.total_room_ly || 0);
    const lyTotal = lyFb + lyRoom;
    if (lyTotal > 0) yoyPct = ((revenue - lyTotal) / lyTotal) * 100;
  }

  const elec = Number(outlet.electricity_cost || 0);
  const gas = Number(outlet.gas_cost || 0);
  const energyOutlet = (elec + gas) || null;

  // Rooms detail — mirror OutletComparisonTable adapter
  const roomsEnabled = !!outlet.rooms_enabled;
  const occ = daily ? outlet.occupancy_pct : outlet.avg_occupancy_pct;
  const arr = daily ? outlet.arr : outlet.avg_arr;
  const hasRooms = roomsEnabled && (roomRev > 0 || (occ != null && occ > 0));

  const em = <span className="text-[#9CA3AF]">—</span>;
  const pctBar = budget > 0 ? Math.max(2, Math.min(100, (revenue / budget) * 100)) : 0;
  const perfBadge = varPct == null ? { text: 'No budget', cls: 'bg-[#F3F5F2] text-[color:var(--color-text-muted)]' }
                  : varPct >= 5   ? { text: 'Above budget', cls: 'bg-emerald-50 text-emerald-700' }
                  : varPct >= -5  ? { text: 'On track',      cls: 'bg-[#EFF3EE] text-[#4A7059]' }
                  :                 { text: 'Below budget', cls: 'bg-rose-50 text-rose-700' };
  const tid = `outlet-pulse-${outlet.outlet_id || outlet.outlet_name}`;

  return (
    <ExecCard testId={tid} className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-[color:var(--color-text-primary)] truncate">{outlet.outlet_name}</div>
          {outlet.outlet_code && <div className="text-[10px] text-[#9CA3AF] font-mono mt-0.5">{outlet.outlet_code}</div>}
        </div>
        <span className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${perfBadge.cls}`}>
          {perfBadge.text}
        </span>
      </div>
      <div className="text-[26px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums"
        data-testid={`${tid}-revenue`}>{compactINR(revenue)}</div>
      <div className="flex items-center gap-3 mt-1 text-[11px] flex-wrap">
        <span className={`font-medium ${varUp ? 'text-emerald-700' : 'text-rose-700'}`}
          data-testid={`${tid}-variance`}>
          {varPct == null ? '—' : `${varUp ? '+' : ''}${varPct.toFixed(1)}%`}
        </span>
        <span className="text-[color:var(--color-text-muted)]">
          {varPct == null ? 'No budget' :
            <>{varUp ? '+' : ''}<span className="tabular-nums">{compactINR(varAmt)}</span> vs Budget <span className="tabular-nums">{compactINR(budget)}</span></>}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[#EEF2EF] overflow-hidden mt-2 mb-3">
        <div className={`h-full ${varUp ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${pctBar}%` }} />
      </div>

      <div className="grid grid-cols-4 gap-2 text-[11px] text-[color:var(--color-text-muted)]">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">YoY</div>
          <div className={`tabular-nums font-semibold text-[13px] ${yoyPct == null ? 'text-[#9CA3AF]' : (yoyPct >= 0 ? 'text-emerald-700' : 'text-rose-700')}`}
            data-testid={`${tid}-yoy`}>
            {yoyPct == null ? '—' : pctFmt(yoyPct, { signed: true })}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">FC</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]" data-testid={`${tid}-fc`}>
            {fcPct != null ? pctFmt(fcPct) : em}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">BC</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]" data-testid={`${tid}-bc`}>
            {bcPct != null ? pctFmt(bcPct) : em}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">APC</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]" data-testid={`${tid}-apc`}>
            {apc != null && apc > 0 ? compactINR(apc) : em}
          </div>
        </div>
      </div>

      <div className="text-[11px] text-[color:var(--color-text-muted)] mt-3 pt-3 border-t border-[#EEF2EF] flex flex-wrap gap-x-3 gap-y-1">
        <span>
          <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">
            {covers ? Number(covers).toLocaleString('en-IN') : '—'}
          </span> Covers
        </span>
        {energyOutlet != null && <>
          <span className="text-[#C7D0CB]">·</span>
          <span>Energy <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(energyOutlet)}</span></span>
        </>}
      </div>

      {hasRooms && (
        <div className="mt-2 pt-2 border-t border-[#EEF2EF] grid grid-cols-3 gap-2 text-[11px]" data-testid={`${tid}-rooms`}>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Rooms</div>
            <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]">{compactINR(roomRev)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">OCC</div>
            <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]">{occ != null && occ > 0 ? pctFmt(occ) : em}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">ARR</div>
            <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]">{arr != null && arr > 0 ? compactINR(arr) : em}</div>
          </div>
        </div>
      )}
    </ExecCard>
  );
}

// ---------- Outlet Budget Performance Chart (horizontal bar) ----------
// Field mapping MIRRORS OutletComparisonTable.pick(o). Any drift breaks
// side-by-side reconciliation with the Detailed Comparison table.
function OutletBudgetPerformanceChart({ outlets, kind, testId }) {
  const rows = (outlets || []).filter(countsTowardPortfolio).map(o => {
    const daily = kind === 'daily';
    const fbSales = daily ? Number(o.net_sales || 0) : Number(o.total_sales || 0);
    const roomRev = daily ? Number(o.room_revenue || 0) : Number(o.total_room_revenue || 0);
    const revenue = o.total_revenue != null ? Number(o.total_revenue) : (fbSales + roomRev);
    const fbBudget = daily ? Number(o.budget || 0) : Number(o.total_budget || 0);
    const roomBudget = daily
      ? Number(o.room_revenue_budget || 0)
      : Number(o.total_room_budget || 0);
    const budget = fbBudget + roomBudget;
    return {
      name: o.outlet_name,
      Actual: Math.round(revenue),
      Budget: Math.round(budget),
      varPct: budget > 0 ? ((revenue - budget) / budget) * 100 : null,
      varAmt: Math.round(revenue - budget),
    };
  }).sort((a, b) => (b.Actual) - (a.Actual));
  return (
    <ExecCard testId={testId} className="h-full">
      <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md mb-1">Actual vs Budget</div>
      <div className="text-[13px] text-[color:var(--color-text-muted)] mb-3">By outlet</div>
      <div style={{ height: Math.max(220, rows.length * 52) }}>
        {rows.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 88, left: 0, bottom: 4 }} barGap={2} barCategoryGap="26%">
              <CartesianGrid stroke="#EEF2EF" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => compactINR(v).replace('₹','')} tick={{ fill: '#7A8B82', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#5C6B64', fontSize: 11 }} width={110} axisLine={false} tickLine={false} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const r = payload[0].payload;
                return (
                  <div className="bg-white border border-[#E2E8DF] rounded-lg shadow-lg p-2.5 text-[11px]">
                    <div className="font-semibold mb-1 text-[color:var(--color-text-primary)]">{r.name}</div>
                    <div>Actual: <span className="tabular-nums font-medium">{compactINR(r.Actual)}</span></div>
                    <div>Budget: <span className="tabular-nums font-medium">{compactINR(r.Budget)}</span></div>
                    {r.varPct != null && (
                      <div className={`font-medium mt-0.5 ${r.varPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        Variance {r.varAmt >= 0 ? '+' : ''}{compactINR(r.varAmt)} · {r.varPct >= 0 ? '+' : ''}{r.varPct.toFixed(1)}%
                      </div>
                    )}
                  </div>
                );
              }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconSize={10} />
              <Bar dataKey="Budget" name="Budget" fill="#D6DED8" radius={[0, 3, 3, 0]} maxBarSize={14} />
              <Bar dataKey="Actual" name="Actual" radius={[0, 3, 3, 0]} maxBarSize={14}
                label={({ x, y, width, height, index }) => {
                  const r = rows[index];
                  if (!r || r.varPct == null) return null;
                  const positive = r.varPct >= 0;
                  return (
                    <text x={x + width + 6} y={y + height / 2 + 3}
                      fill={positive ? '#047857' : '#B91C1C'}
                      fontSize={10.5} fontWeight={600}>
                      {positive ? '+' : ''}{r.varPct.toFixed(1)}%
                    </text>
                  );
                }}>
                {rows.map((r, i) => (
                  <Cell key={i} fill={r.varPct == null ? '#829C7A' : (r.varPct >= 0 ? 'var(--color-primary)' : '#C45A41')} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="h-full flex items-center justify-center text-[12px] text-[#9CA3AF]">No outlet data</div>}
      </div>
    </ExecCard>
  );
}

// ---------- Cost & Efficiency Panel ----------
function CostRow({ label, actual, target, unit = '%', over_ok_msg }) {
  const hasA = actual != null && !isNaN(actual);
  const hasT = target != null && !isNaN(target);
  const isPct = unit === '%';
  const actualStr = hasA ? (isPct ? pctFmt(actual) : compactINR(actual)) : '—';
  const targetStr = hasT ? (isPct ? pctFmt(target) : compactINR(target)) : '—';
  const diff = hasA && hasT ? (actual - target) : null;
  const isBad = diff != null && diff > 0;
  const barPct = hasA && hasT && target > 0 ? Math.max(4, Math.min(100, (actual / target) * 100)) : 0;
  return (
    <div className="p-4 rounded-xl border border-[#EEF2EF] bg-[#FAFCFA]" data-testid={`cost-row-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">{label}</div>
          <div className="text-[24px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-1.5">{actualStr}</div>
        </div>
        {diff != null && (
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap
            ${isBad ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {isBad ? 'Over target' : 'On target'}
          </span>
        )}
      </div>
      <div className="text-[11px] text-[color:var(--color-text-muted)] mt-2 flex items-center justify-between">
        <span>Target <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{targetStr}</span></span>
        {diff != null && (
          <span className={`tabular-nums font-medium ${isBad ? 'text-rose-700' : 'text-emerald-700'}`}>
            {isBad ? `+${(isPct ? diff.toFixed(1)+' pts' : compactINR(diff))} over` : `${(isPct ? Math.abs(diff).toFixed(1)+' pts' : compactINR(Math.abs(diff)))} better`}
          </span>
        )}
      </div>
      <div className="h-2 rounded-full bg-white border border-[#EEF2EF] overflow-hidden mt-2 relative">
        <div className={`h-full ${isBad ? 'bg-rose-500' : 'bg-emerald-500'}`} style={{ width: `${barPct}%` }} />
        {hasT && <div className="absolute top-0 bottom-0 w-[2px] bg-[#0F1A16]/70" style={{ left: `${Math.min(100, 100)}%` }} />}
      </div>
    </div>
  );
}

// ---------- Utilities & Energy Performance ----------
// Two combined cards: Electricity + Gas. Each card shows Cost (hero),
// Units (secondary), Budget/status and a bottom progress bar aligned
// across cards via flex layout. FC/BC live only in Executive Pulse.
// Data sources unchanged:
//   • cost_efficiency rows from /insights/five-minute
//   • Consumption sums from per-outlet MTD/Daily rows
function UtilCombinedCard({ label, costActual, costBudget, unitsActual, uom, testId }) {
  const hasCost = costActual != null && !isNaN(costActual);
  const hasBudget = costBudget != null && !isNaN(costBudget) && Number(costBudget) > 0;
  const hasUnits = unitsActual != null && !isNaN(unitsActual) && Number(unitsActual) > 0;
  const diff = hasCost && hasBudget ? (Number(costActual) - Number(costBudget)) : null;
  const varPct = hasCost && hasBudget ? ((Number(costActual) - Number(costBudget)) / Number(costBudget) * 100) : null;
  const isBad = diff != null && diff > 0;
  const barPct = hasCost && hasBudget ? Math.max(4, Math.min(140, (Number(costActual) / Number(costBudget)) * 100)) : 0;
  const badge = diff == null ? null : (isBad ? 'Over target' : 'On target');

  return (
    <div className="p-4 rounded-xl border border-[#EEF2EF] bg-[#FAFCFA] flex flex-col h-full min-h-[168px]"
      data-testid={testId}>
      {/* Header row — title left, badge right; will never overlap. */}
      <div className="flex items-start justify-between gap-2">
        <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md truncate max-w-full">
          {label}
        </div>
        {badge && (
          <span className={`shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full whitespace-nowrap
            ${isBad ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {badge}
          </span>
        )}
      </div>

      {/* Hero: Cost value — dominant. */}
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <div className="text-[26px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums"
          data-testid={`${testId}-cost`}>
          {hasCost ? compactINR(costActual) : '—'}
        </div>
        {varPct != null && (
          <span className={`text-[11px] font-semibold tabular-nums ${varPct >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}
            data-testid={`${testId}-varpct`}>
            {varPct >= 0 ? '+' : ''}{varPct.toFixed(1)}%
          </span>
        )}
      </div>

      {/* Secondary: Units — muted, quieter. */}
      <div className="mt-1.5 text-[12px] text-[color:var(--color-text-muted)]" data-testid={`${testId}-units`}>
        {hasUnits ? (
          <>
            <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">
              {Math.round(Number(unitsActual)).toLocaleString('en-IN')}
            </span>
            <span className="ml-1 text-[10px] text-[#9CA3AF] font-normal uppercase tracking-wider">{uom}</span>
          </>
        ) : (
          <span className="italic text-[#9CA3AF]">Units not reported</span>
        )}
      </div>

      {/* Budget line — muted, above the bottom-anchored bar. */}
      <div className="mt-1.5 text-[11px] text-[color:var(--color-text-muted)]">
        {hasBudget
          ? <>Budget <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]" data-testid={`${testId}-budget`}>{compactINR(costBudget)}</span></>
          : <span className="italic text-[#9CA3AF]" data-testid={`${testId}-budget-unset`}>Budget not configured</span>}
      </div>

      {/* Spacer pushes the progress bar to the card bottom — cards align. */}
      <div className="mt-auto pt-3">
        <div className="h-2 rounded-full bg-white border border-[#EEF2EF] overflow-hidden relative">
          <div className={`h-full ${isBad ? 'bg-rose-500' : 'bg-emerald-500'} transition-all`}
            style={{ width: `${Math.min(100, barPct)}%` }} />
        </div>
      </div>
    </div>
  );
}

function UtilitiesEnergyPanel({ costEfficiency = [], outlets = [], testId }) {
  const elec = costEfficiency.find(r => r.metric === 'Electricity Cost');
  const gas = costEfficiency.find(r => r.metric === 'Gas Cost');
  // Consumption sums — mirror per-outlet fields used by Detailed Comparison.
  const pool = (outlets || []).filter(countsTowardPortfolio);
  const elecKwh = pool.reduce((s, o) => s + Number(o.electricity_consumption || 0), 0);
  const gasUnits = pool.reduce((s, o) => s + Number(o.gas_consumption || 0), 0);

  return (
    <ExecCard testId={testId} className="h-full">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Utilities &amp; Energy Performance</div>
          <div className="text-[13px] text-[color:var(--color-text-muted)] mt-0.5">Combined cost + consumption</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UtilCombinedCard
          label="Electricity"
          costActual={elec?.actual}
          costBudget={elec?.budget}
          unitsActual={elecKwh > 0 ? elecKwh : null}
          uom="kWh"
          testId={`${testId}-electricity`}
        />
        <UtilCombinedCard
          label="Gas"
          costActual={gas?.actual}
          costBudget={gas?.budget}
          unitsActual={gasUnits > 0 ? gasUnits : null}
          uom="LTR"
          testId={`${testId}-gas`}
        />
      </div>
    </ExecCard>
  );
}

// ---------- Revenue Intelligence Panel ----------
// Two permanent sub-panels: Current Month (MTD) + Current Year (YTD).
// Rendered regardless of the active period tab (Daily/MTD/YTD/Range).
// Data source: existing /dashboard/mtd + /dashboard/ytd payloads passed
// via props. Projection is a small client-side run-rate calc on
// daily_trend (7 most recent completed observations, or all completed
// if <7). Full-Month Budget denominator is the backend-canonical
// `full_month_budget` from /dashboard/mtd (never extrapolated).

function _mtdProjectionSeries({ dailyTrend, dataThrough, year, month, daysInMonth }) {
  const rows = Array.isArray(dailyTrend) ? dailyTrend : [];
  // Completed reporting boundary — use `data_through` (backend-authoritative),
  // NOT `total_net_sales > 0`. A legitimately completed day can have ₹0 sales.
  const completedRows = dataThrough
    ? rows.filter(r => r.date <= dataThrough)
    : rows;
  const daily_values = completedRows.map(r => Number(r.total_net_sales || 0));
  const n = daily_values.length;

  // Run-rate over most recent 7 completed observations
  let runRate = null;
  if (n >= 7) runRate = daily_values.slice(-7).reduce((s, v) => s + v, 0) / 7;
  else if (n >= 3) runRate = daily_values.reduce((s, v) => s + v, 0) / n;

  // Build per-day series across the entire month
  const byDate = new Map(rows.map(r => [r.date, r]));
  const series = [];
  let cumActual = 0;
  let lastActualDay = null;
  let lastActualCum = 0;
  for (let d = 1; d <= daysInMonth; d += 1) {
    const dt = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const row = byDate.get(dt);
    const isCompleted = dataThrough ? dt <= dataThrough : !!row;
    const dayActual = row ? Number(row.total_net_sales || 0) : 0;
    if (isCompleted && row) {
      cumActual += dayActual;
      lastActualDay = d;
      lastActualCum = cumActual;
    }
    series.push({
      date: dt,
      day: d,
      daily: (isCompleted && row) ? dayActual : null,
      actualCum: (isCompleted && row) ? cumActual : null,
      budgetCum: row ? Number(row.cumulative_budget || 0) : null,
      projectedCum: null,
    });
  }

  // Emit projection dashed line from last actual day onwards.
  let projectedTotal = null;
  if (runRate != null && lastActualDay != null) {
    // Anchor projection at last actual point (avoid visual gap)
    const idxLast = series.findIndex(p => p.day === lastActualDay);
    if (idxLast >= 0) series[idxLast].projectedCum = lastActualCum;
    let running = lastActualCum;
    for (let d = lastActualDay + 1; d <= daysInMonth; d += 1) {
      running += runRate;
      const idx = series.findIndex(p => p.day === d);
      if (idx >= 0) series[idx].projectedCum = running;
    }
    projectedTotal = running;
  }

  return {
    series,
    runRate,
    projectedTotal,
    lastActualDay,
    hasProjection: runRate != null && lastActualDay != null && lastActualDay < daysInMonth,
    completedDays: n,
    avgDaily: n > 0 ? daily_values.reduce((s, v) => s + v, 0) / n : 0,
  };
}

function CurrentMonthPanel({ mtdData, selectedYear, selectedMonth, testId = 'ri-current-month' }) {  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const pt = mtdData?.portfolio_totals || null;
  const mtdActual = Number(pt?.total_revenue || 0);
  const mtdBudgetToDate = Number(pt?.budget || 0);
  const mtdVar = mtdActual - mtdBudgetToDate;
  const mtdVarPct = mtdBudgetToDate > 0 ? (mtdVar / mtdBudgetToDate) * 100 : null;
  const fullMonthBudget = pt?.full_month_budget != null ? Number(pt.full_month_budget) : null;
  const fullMonthBudgetAvailable = !!pt?.full_month_budget_available;

  const proj = _mtdProjectionSeries({
    dailyTrend: mtdData?.daily_trend || [],
    dataThrough: mtdData?.data_through || null,
    year: selectedYear, month: selectedMonth, daysInMonth,
  });
  const projectedClose = proj.projectedTotal;
  const projAchPct = (projectedClose != null && fullMonthBudget && fullMonthBudget > 0)
    ? ((projectedClose - fullMonthBudget) / fullMonthBudget) * 100
    : null;

  // Days remaining — from `data_through`, not calendar today
  let daysElapsed = 0;
  if (mtdData?.data_through) {
    const dt = mtdData.data_through;
    daysElapsed = parseInt(dt.slice(8, 10), 10);
  }
  const daysRemaining = Math.max(0, daysInMonth - daysElapsed);
  const requiredDaily = (fullMonthBudget && daysRemaining > 0)
    ? Math.max(0, (fullMonthBudget - mtdActual) / daysRemaining)
    : null;

  const monthLabel = new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <ExecCard variant="hero" testId={testId} className="h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Current Month · MTD</div>
          <div className="text-[13px] text-[color:var(--color-text-muted)] mt-0.5">{monthLabel}</div>
        </div>
        <div className="text-right">
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Projected Close</div>
          <div className="text-[22px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-1"
            data-testid={`${testId}-projected`}>
            {projectedClose != null ? compactINR(projectedClose) : '—'}
          </div>
          {projAchPct != null && (
            <div className={`text-[11px] font-semibold mt-1 ${projAchPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
              data-testid={`${testId}-projected-ach`}>
              {projAchPct >= 0 ? '+' : ''}{projAchPct.toFixed(1)}% vs Full-Month Budget
            </div>
          )}
          {projectedClose != null && !fullMonthBudgetAvailable && (
            <div className="text-[11px] text-[#9A7818] mt-1 italic" data-testid={`${testId}-fmb-unavailable`}>
              Full-month budget unavailable
            </div>
          )}
          {projectedClose == null && (
            <div className="text-[11px] text-[#9CA3AF] italic mt-1" data-testid={`${testId}-proj-unavailable`}>
              Projection unavailable — need 3+ reported days
            </div>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">MTD Actual</div>
          <div className="text-[15px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-mtd-actual`}>{compactINR(mtdActual)}</div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Budget to Date</div>
          <div className="text-[15px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-btd`}>{compactINR(mtdBudgetToDate)}</div>
          {mtdVarPct != null && (
            <div className={`text-[10px] font-semibold ${mtdVarPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              {mtdVarPct >= 0 ? '+' : ''}{mtdVarPct.toFixed(1)}%
            </div>
          )}
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Full-Month Budget</div>
          <div className="text-[15px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-fmb`}>
            {fullMonthBudgetAvailable ? compactINR(fullMonthBudget) : <span className="text-[#9CA3AF] font-normal italic text-[13px]">Unavailable</span>}
          </div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Variance ₹</div>
          <div className={`text-[15px] font-bold tabular-nums ${mtdVar >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            {mtdVar >= 0 ? '+' : ''}{compactINR(mtdVar)}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-[260px] h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={proj.series} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id={`${testId}-fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.18} />
                <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#EEF2EF" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: '#7A8B82', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tickFormatter={(v) => compactINR(v).replace('₹', '')}
              tick={{ fill: '#7A8B82', fontSize: 10 }} axisLine={false} tickLine={false} width={48} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const row = payload[0]?.payload || {};
                return (
                  <div className="bg-white border border-[#E2E8DF] rounded-lg shadow-lg p-2.5 text-[11px]">
                    <div className="font-semibold text-[color:var(--color-text-primary)] mb-1">Day {label}</div>
                    {row.daily != null && <div>Daily: <span className="tabular-nums font-medium">{compactINR(row.daily)}</span></div>}
                    {row.actualCum != null && <div>Cum Actual: <span className="tabular-nums font-medium">{compactINR(row.actualCum)}</span></div>}
                    {row.budgetCum != null && <div>Cum Budget: <span className="tabular-nums font-medium">{compactINR(row.budgetCum)}</span></div>}
                    {row.projectedCum != null && <div className="text-[#8B7A00]">Projected: <span className="tabular-nums font-medium">{compactINR(row.projectedCum)}</span></div>}
                  </div>
                );
              }} />
            <Legend wrapperStyle={{ fontSize: 10, paddingTop: 2 }} iconSize={8} />
            <Bar yAxisId="left" dataKey="daily" name="Daily Revenue" fill="#829C7A" maxBarSize={12} opacity={0.55} />
            <Area yAxisId="left" type="monotone" dataKey="actualCum" name="Cumulative Actual"
              stroke="var(--color-primary)" strokeWidth={2.5} fill={`url(#${testId}-fill)`}
              connectNulls={false} dot={false} activeDot={{ r: 4 }} />
            <Line yAxisId="left" type="monotone" dataKey="budgetCum" name="Budget Pace"
              stroke="#829C7A" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
            <Line yAxisId="left" type="monotone" dataKey="projectedCum" name="Run-Rate Projection"
              stroke="#C7A600" strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-3 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Avg Daily</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]">{proj.completedDays > 0 ? compactINR(proj.avgDaily) : '—'}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Run Rate (7d)</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]">{proj.runRate != null ? compactINR(proj.runRate) : '—'}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Required Daily</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]"
            data-testid={`${testId}-required-daily`}>
            {requiredDaily != null ? compactINR(requiredDaily) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Days Remaining</div>
          <div className="tabular-nums font-semibold text-[13px] text-[color:var(--color-text-primary)]">{daysRemaining}</div>
        </div>
      </div>
    </ExecCard>
  );
}

function CurrentYearPanel({ ytdData, testId = 'ri-current-year' }) {
  const pt = ytdData?.portfolio_totals || null;
  const ytdActual = Number(pt?.total_revenue || 0);
  const ytdBudget = Number(pt?.budget || 0);
  const ytdVar = ytdActual - ytdBudget;
  const ytdVarPct = ytdBudget > 0 ? (ytdVar / ytdBudget) * 100 : null;

  // Build a monthly aggregate from per-outlet ytd data (if provided).
  // The existing /dashboard/ytd payload doesn't expose a monthly series,
  // so we simply present headline stats + Actual vs Budget summary card
  // without fabricating a monthly time series.
  const outlets = pt ? Number(pt.outlets_reporting || 0) : 0;
  const totalOutlets = pt ? Number(pt.outlets_total || 0) : 0;

  return (
    <ExecCard variant="hero" testId={testId} className="h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">Current Year · YTD</div>
          <div className="text-[13px] text-[color:var(--color-text-muted)] mt-0.5">
            {ytdData?.fy_start && ytdData?.fy_end ? `${ytdData.fy_start} → ${ytdData.fy_end}` : 'Financial Year'}
          </div>
        </div>
        {ytdVarPct != null && (
          <span className={`text-[13px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap
            ${ytdVar >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
            data-testid={`${testId}-varpct`}>
            {ytdVarPct >= 0 ? '+' : ''}{ytdVarPct.toFixed(1)}% vs Budget
          </span>
        )}
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">YTD Actual</div>
          <div className="text-[20px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-actual`}>{compactINR(ytdActual)}</div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">YTD Budget</div>
          <div className="text-[20px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-budget`}>{compactINR(ytdBudget)}</div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Variance ₹</div>
          <div className={`text-[15px] font-bold tabular-nums ${ytdVar >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            {ytdVar >= 0 ? '+' : ''}{compactINR(ytdVar)}
          </div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Outlets Reporting</div>
          <div className="text-[15px] font-bold text-[color:var(--color-text-primary)] tabular-nums">{outlets} / {totalOutlets}</div>
        </div>
      </div>

      {/* Actual vs Budget composed comparison (single-row bar, avoids fake series) */}
      <div className="flex-1 min-h-[260px] h-[300px] flex flex-col">
        <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md mb-2">Actual vs Budget · YTD</div>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={[
              { name: 'Actual', value: ytdActual, color: 'var(--color-primary)' },
              { name: 'Budget', value: ytdBudget, color: '#D6DED8' },
            ]}
            layout="vertical"
            margin={{ top: 8, right: 40, left: 0, bottom: 4 }}
            barCategoryGap="30%">
            <CartesianGrid stroke="#EEF2EF" horizontal={false} />
            <XAxis type="number" tickFormatter={(v) => compactINR(v).replace('₹', '')}
              tick={{ fill: '#7A8B82', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#5C6B64', fontSize: 11 }}
              axisLine={false} tickLine={false} width={70} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const r = payload[0].payload;
                return (
                  <div className="bg-white border border-[#E2E8DF] rounded-lg shadow-lg p-2.5 text-[11px]">
                    <div className="font-semibold text-[color:var(--color-text-primary)] mb-1">{r.name}</div>
                    <div className="tabular-nums font-medium">{compactINR(r.value)}</div>
                  </div>
                );
              }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={30}
              label={({ x, y, width, height, value }) => (
                <text x={x + width + 6} y={y + height / 2 + 3}
                  fill="#0F1A16" fontSize={11} fontWeight={600} className="tabular-nums">
                  {compactINR(value)}
                </text>
              )}>
              <Cell fill="var(--color-primary)" />
              <Cell fill="#D6DED8" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Meta */}
      <div className="mt-3 text-[10px] text-[#9CA3AF] uppercase tracking-wider">
        Includes F&amp;B + Rooms across all included outlets
      </div>
    </ExecCard>
  );
}

function RevenueIntelligencePanel({ mtdData, ytdData, selectedYear, selectedMonth, testId = 'revenue-intelligence' }) {
  // Both panels are ALWAYS rendered so the intelligence area never appears
  // empty when the active period tab is Daily / Range / etc.
  return (
    <div className="mb-4 lg:mb-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A8B82] font-semibold mb-2 flex items-center gap-1.5">
        <Activity className="w-3 h-3" strokeWidth={2.5} /> Revenue Intelligence
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4">
        <CurrentMonthPanel
          mtdData={mtdData}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          testId={`${testId}-month`}
        />
        <CurrentYearPanel
          ytdData={ytdData}
          testId={`${testId}-year`}
        />
      </div>
    </div>
  );
}

// ---------- Rooms & Accommodation Performance ----------
// Portfolio-level MTD + YTD hotel KPIs. Rendered only when the current
// scope includes at least one rooms_enabled outlet. Uses ONLY existing
// canonical room fields already emitted by /dashboard/mtd and /dashboard/ytd
// (total_room_revenue, total_room_budget, total_room_ly, total_rooms_sold,
// total_rooms_available, avg_occupancy_pct, avg_arr). Portfolio OCC/ARR are
// aggregated using the same canonical formula backend uses per-outlet:
//   OCC = Σ rooms_sold / Σ rooms_available × 100
//   ARR = Σ room_revenue / Σ rooms_sold
// This mirrors backend line 657-658 (MTD) & 985-986 (YTD) exactly — no
// simple average of already-averaged outlet OCC/ARR.

function _aggregateRoomKPIs(outlets, kind = 'mtd') {
  const pool = (outlets || []).filter(o => o.rooms_enabled && countsTowardPortfolio(o));
  if (!pool.length) return null;
  const daily = kind === 'daily';
  const revKey = daily ? 'room_revenue' : 'total_room_revenue';
  const budKey = daily ? 'room_revenue_budget' : 'total_room_budget';
  const lyKey = daily ? 'room_revenue_last_year' : 'total_room_ly';
  const soldKey = daily ? 'rooms_sold' : 'total_rooms_sold';
  const availKey = daily ? 'rooms_available' : 'total_rooms_available';
  const sum = (k) => pool.reduce((s, o) => s + Number(o[k] || 0), 0);
  const revenue = sum(revKey);
  const budget = sum(budKey);
  const ly = sum(lyKey);
  const rSold = sum(soldKey);
  const rAvail = sum(availKey);
  const occ = rAvail > 0 ? (rSold / rAvail * 100) : null;
  const arr = rSold > 0 ? (revenue / rSold) : null;
  const varAmt = revenue - budget;
  const varPct = budget > 0 ? (varAmt / budget * 100) : null;
  const yoyPct = ly > 0 ? ((revenue - ly) / ly * 100) : null;
  return {
    revenue, budget, ly, rSold, rAvail, occ, arr,
    varAmt, varPct, yoyPct,
    hotelOutlets: pool.length,
  };
}

function RoomsPeriodCard({ title, contextLabel, kpi, testId }) {
  if (!kpi) return (
    <ExecCard testId={testId} className="h-full flex flex-col">
      <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">{title}</div>
      {contextLabel && <div className="text-[13px] text-[color:var(--color-text-muted)] mt-0.5">{contextLabel}</div>}
      <div className="flex-1 flex items-center justify-center text-[12px] text-[#9CA3AF] italic mt-4">
        No hotel outlets in current scope
      </div>
    </ExecCard>
  );
  const varUp = kpi.varAmt >= 0;
  const pctBar = kpi.budget > 0 ? Math.max(4, Math.min(100, (kpi.revenue / kpi.budget) * 100)) : 0;

  return (
    <ExecCard variant="hero" testId={testId} className="h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="inline-flex items-center w-fit text-[10px] uppercase tracking-[0.14em] font-semibold text-[color:var(--color-primary-hover)] bg-[color:var(--color-primary-tint)] px-2 py-0.5 rounded-md">{title}</div>
          {contextLabel && <div className="text-[13px] text-[color:var(--color-text-muted)] mt-0.5">{contextLabel}</div>}
        </div>
        {kpi.varPct != null && (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap
            ${varUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
            data-testid={`${testId}-varpct`}>
            {varUp ? '+' : ''}{kpi.varPct.toFixed(1)}% vs Budget
          </span>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-wider text-[#7A8B82]">Room Revenue</div>
      <div className="text-[28px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums mt-1"
        data-testid={`${testId}-revenue`}>
        {compactINR(kpi.revenue)}
      </div>
      <div className="text-[12px] text-[color:var(--color-text-muted)] mt-2">
        Budget <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]" data-testid={`${testId}-budget`}>{compactINR(kpi.budget)}</span>
        <span className="mx-2 text-[#C7D0CB]">·</span>
        <span className={`tabular-nums font-medium ${varUp ? 'text-emerald-700' : 'text-rose-700'}`}>
          {varUp ? '+' : ''}{compactINR(kpi.varAmt)}
        </span>
      </div>

      <div className="h-2 w-full rounded-full bg-[#EEF2EF] overflow-hidden mt-2">
        <div className={`h-full ${varUp ? 'bg-emerald-500' : 'bg-rose-500'}`}
          style={{ width: `${pctBar}%` }} />
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4 text-[11px]">
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Occupancy</div>
          <div className="text-[16px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-occ`}>
            {kpi.occ != null ? pctFmt(kpi.occ) : '—'}
          </div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">ARR</div>
          <div className="text-[16px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-arr`}>
            {kpi.arr != null ? compactINR(kpi.arr) : '—'}
          </div>
        </div>
        <div className="rounded-lg border border-[#EEF2EF] bg-[#FAFCFA] px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-[#7A8B82]">Rooms Sold</div>
          <div className="text-[16px] font-bold text-[color:var(--color-text-primary)] tabular-nums" data-testid={`${testId}-sold`}>
            {kpi.rSold ? Number(kpi.rSold).toLocaleString('en-IN') : '—'}
          </div>
        </div>
      </div>

      {kpi.ly > 0 && (
        <div className="mt-auto pt-3 text-[11px] text-[color:var(--color-text-muted)] border-t border-[#EEF2EF] flex items-center justify-between mt-4">
          <span>
            LY Rooms <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]" data-testid={`${testId}-ly`}>{compactINR(kpi.ly)}</span>
          </span>
          {kpi.yoyPct != null && (
            <span className={`font-semibold tabular-nums ${kpi.yoyPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
              data-testid={`${testId}-yoy`}>
              {kpi.yoyPct >= 0 ? '+' : ''}{kpi.yoyPct.toFixed(1)}% YoY
            </span>
          )}
        </div>
      )}
    </ExecCard>
  );
}

function RoomsAccommodationPanel({ mtdData, ytdData, selectedYear, selectedMonth, testId = 'rooms-accommodation' }) {
  const mtdOutlets = mtdData?.outlets || [];
  const ytdOutlets = ytdData?.outlets || [];
  const hasHotelInScope =
    mtdOutlets.some(o => o.rooms_enabled && countsTowardPortfolio(o)) ||
    ytdOutlets.some(o => o.rooms_enabled && countsTowardPortfolio(o));
  if (!hasHotelInScope) return null;

  const mtdKpi = _aggregateRoomKPIs(mtdOutlets, 'mtd');
  const ytdKpi = _aggregateRoomKPIs(ytdOutlets, 'mtd');
  const monthLabel = new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="mb-4 lg:mb-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A8B82] font-semibold mb-2 flex items-center gap-1.5">
        <BedDouble className="w-3 h-3" strokeWidth={2.5} /> Rooms &amp; Accommodation Performance
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4">
        <RoomsPeriodCard
          title="Current Month · MTD"
          contextLabel={monthLabel}
          kpi={mtdKpi}
          testId={`${testId}-mtd`}
        />
        <RoomsPeriodCard
          title="Current Year · YTD"
          contextLabel={ytdData?.fy_start && ytdData?.fy_end ? `${ytdData.fy_start} → ${ytdData.fy_end}` : 'Financial Year'}
          kpi={ytdKpi}
          testId={`${testId}-ytd`}
        />
      </div>
    </div>
  );
}



// ---------- Portfolio Signals ----------
function PortfolioSignals({ outlets, kind, portfolioTotals, costEfficiency, testId }) {
  const rows = (outlets || []).filter(countsTowardPortfolio);
  const daily = kind === 'daily';
  const revOf = (o) => daily
    ? (o.total_revenue != null ? o.total_revenue : ((o.total_sales || 0) + (o.total_room_revenue || 0)))
    : (o.total_revenue != null ? o.total_revenue : ((o.net_sales || 0) + (o.room_revenue || 0)));
  const bdgOf = (o) => daily
    ? ((o.budget || 0) + (o.room_budget || o.total_room_budget || 0))
    : ((o.budget || 0) + (o.room_revenue_budget || 0));
  const varPctOf = (o) => bdgOf(o) > 0 ? ((revOf(o) - bdgOf(o)) / bdgOf(o)) * 100 : null;
  const fcOf = (o) => daily ? o.avg_food_cost_pct : o.food_cost_pct;
  const apcOf = (o) => o.total_apc || o.apc;

  const topPerf   = rows.slice().sort((a, b) => (varPctOf(b) ?? -Infinity) - (varPctOf(a) ?? -Infinity))[0];
  const needsAttn = rows.slice().sort((a, b) => (varPctOf(a) ?? Infinity)  - (varPctOf(b) ?? Infinity))[0];
  const highApc   = rows.slice().filter(o => apcOf(o) != null).sort((a, b) => apcOf(b) - apcOf(a))[0];
  const bestFc    = rows.slice().filter(o => fcOf(o) != null && fcOf(o) > 0).sort((a, b) => fcOf(a) - fcOf(b))[0];
  const highYoy   = rows.slice().filter(o => o.yoy_pct != null).sort((a, b) => (b.yoy_pct) - (a.yoy_pct))[0];
  const energyRow = (costEfficiency || []).find(c => (c.metric === 'Electricity Cost' && c.status === 'over')
                    || (c.metric === 'Gas Cost' && c.status === 'over'));

  const signals = [];
  if (topPerf && varPctOf(topPerf) != null)   signals.push({ icon: Award, key: 'top', label: 'Top Performer', cls: 'text-emerald-700 bg-emerald-50', name: topPerf.outlet_name, value: pctFmt(varPctOf(topPerf), { signed: true }), note: 'vs Budget' });
  if (needsAttn && varPctOf(needsAttn) != null && varPctOf(needsAttn) < -5)
                                              signals.push({ icon: AlertTriangle, key: 'attn', label: 'Needs Attention', cls: 'text-rose-700 bg-rose-50', name: needsAttn.outlet_name, value: pctFmt(varPctOf(needsAttn), { signed: true }), note: 'below Budget' });
  if (highApc)                                signals.push({ icon: Sparkles, key: 'apc', label: 'Highest APC', cls: 'text-[#4A7059] bg-[#EFF3EE]', name: highApc.outlet_name, value: compactINR(apcOf(highApc)), note: 'per cover' });
  if (bestFc)                                 signals.push({ icon: ChefHat, key: 'fc', label: 'Best Food Cost', cls: 'text-emerald-700 bg-emerald-50', name: bestFc.outlet_name, value: pctFmt(fcOf(bestFc)), note: 'lowest FC' });
  if (highYoy && highYoy.yoy_pct != null)     signals.push({ icon: TrendingUp, key: 'yoy', label: 'Highest YoY', cls: 'text-[#4A7059] bg-[#EFF3EE]', name: highYoy.outlet_name, value: pctFmt(highYoy.yoy_pct, { signed: true }), note: 'YoY' });
  if (energyRow)                              signals.push({ icon: Zap, key: 'energy', label: 'Energy Alert', cls: 'text-rose-700 bg-rose-50', name: energyRow.metric, value: compactINR(energyRow.actual), note: `Target ${compactINR(energyRow.budget)}` });

  if (!signals.length) return null;
  return (
    <ExecCard testId={testId} className="mb-4 lg:mb-5">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A8B82] font-semibold mb-3 flex items-center gap-1.5">
        <Flame className="w-3 h-3" strokeWidth={2.5} /> Portfolio Signals
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {signals.map(s => (
          <div key={s.key} className={`rounded-xl p-3 flex items-start gap-3 ${s.cls}`} data-testid={`${testId}-${s.key}`}>
            <div className="w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
              <s.icon className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-wider font-semibold opacity-80">{s.label}</div>
              <div className="text-[13px] font-semibold truncate">{s.name}</div>
              <div className="text-[12px] tabular-nums">
                <span className="font-bold">{s.value}</span> <span className="opacity-70">· {s.note}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ExecCard>
  );
}

// ---------- Detailed outlet comparison (always expanded per UX brief) ----------
function DetailedComparisonSection({ outlets, kind, testId }) {
  return (
    <div className="mb-4" data-testid={testId}>
      <div
        className="w-full flex items-center justify-between px-4 h-11 rounded-md bg-[color:var(--color-primary)] text-white"
        data-testid={`${testId}-header`}>
        <div className="text-[14px] font-semibold text-white leading-none truncate">Detailed Outlet Comparison</div>
        <div className="text-[11px] text-white/85 shrink-0 pl-3">FC · BC · NPS · Checklist · Utilities · Channels</div>
      </div>
      <div className="mt-3">
        <OutletComparisonTable outlets={outlets} kind={kind} testId={`${testId}-table`} />
      </div>
    </div>
  );
}

// ---------- Total Revenue drilldown modal (Feb 2026) ----------------
// Reuses the CANONICAL outlet-level fields already loaded by the
// dashboard — never a second API. Actual + Target only (spec §5).
function TotalRevenueDrilldownModal({ open, onClose, kind, outlets, periodLabel, heroActual, heroTarget }) {
  // Canonical mapping — MIRROR of OutletComparisonTable/buildOverviewMetrics
  // so the modal reconciles bit-identically with the hero card.
  const rows = (outlets || [])
    .filter(o => (kind === 'daily' ? o.has_entry !== false : true) && o.hierarchy !== 'sub' && o.include_in_totals !== false)
    .map(o => {
      const daily = kind === 'daily';
      const fbSales = daily ? Number(o.net_sales || 0) : Number(o.total_sales || 0);
      const roomRev = daily ? Number(o.room_revenue || 0) : Number(o.total_room_revenue || 0);
      const revenue = o.total_revenue != null ? Number(o.total_revenue) : (fbSales + roomRev);
      const fbBudget = daily ? Number(o.budget || 0) : Number(o.total_budget || 0);
      const roomBudget = daily
        ? Number(o.room_revenue_budget || 0)
        : Number(o.total_room_budget || 0);
      return { id: o.id || o.outlet_id, name: o.name || o.outlet_name, actual: revenue, target: fbBudget + roomBudget };
    })
    .sort((a, b) => b.actual - a.actual);

  const totActual = rows.reduce((s, r) => s + r.actual, 0);
  const totTarget = rows.reduce((s, r) => s + r.target, 0);

  const title = kind === 'daily'
    ? `Outlet Sales — ${periodLabel}`
    : `Outlet Sales — MTD · ${periodLabel}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-white p-0 gap-0"
        data-testid="total-revenue-drilldown-modal">
        <DialogHeader className="bg-[color:var(--color-primary)] px-6 py-4 rounded-t-lg">
          <DialogTitle className="text-white text-[15px] font-semibold leading-tight">{title}</DialogTitle>
        </DialogHeader>
        <div className="bg-white px-6 py-5">
        {rows.length === 0 ? (
          <p className="text-sm text-[#5C6B64] italic py-6 text-center">No outlets in current scope</p>
        ) : (
          <table className="w-full text-sm" data-testid="trd-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-[#5C6B64] border-b border-[#E2E8DF]">
                <th className="text-left py-2 font-medium">Outlet</th>
                <th className="text-right py-2 font-medium">Actual</th>
                <th className="text-right py-2 font-medium">Target</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-[#F0F4F1]" data-testid={`trd-row-${r.id}`}>
                  <td className="py-2 text-[color:var(--color-text-primary)]">{r.name}</td>
                  <td className="py-2 text-right tabular-nums">{compactINR(r.actual)}</td>
                  <td className="py-2 text-right tabular-nums">{compactINR(r.target)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#1A2421] bg-[#F9F9F8]" data-testid="trd-total-row">
                <td className="py-2.5 font-semibold text-[color:var(--color-text-primary)]">Total</td>
                <td className="py-2.5 text-right tabular-nums font-bold" data-testid="trd-total-actual">{compactINR(totActual)}</td>
                <td className="py-2.5 text-right tabular-nums font-bold" data-testid="trd-total-target">{compactINR(totTarget)}</td>
              </tr>
            </tbody>
          </table>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── V2 adapter helpers — TRANSFORMS ONLY (no calculations added). ───
// Every value passed to the V2 components must originate from the existing
// metrics returned by buildOverviewMetrics(), _aggregateRoomKPIs(),
// _mtdProjectionSeries(), or the raw portfolio_totals payloads.

function _buildFinancialSnapshotRowsV2(metrics) {
  if (!metrics) return [];
  const m = metrics;
  const tone = (actual, target, lowerIsBetter = false) => {
    if (actual == null || target == null) return 'neutral';
    if (lowerIsBetter) return actual <= target ? 'good' : 'bad';
    return actual >= target ? 'good' : 'bad';
  };
  const variance = (actual, target) => {
    if (actual == null || target == null) return null;
    const d = actual - target;
    if (d === 0) return '0.0 pts';
    return `${d > 0 ? '+' : ''}${d.toFixed(1)} pts`;
  };
  const rows = [];
  rows.push({
    label: 'APC',
    currentText: m.apc != null && m.apc > 0 ? `₹${Math.round(m.apc).toLocaleString('en-IN')}` : '—',
    targetText: '—',
    variance: null,
    status: m.apc != null && m.apc > 0 ? 'Tracked' : 'No data',
    statusTone: m.apc != null && m.apc > 0 ? 'neutral' : 'neutral',
  });
  rows.push({
    label: 'Food Cost %',
    currentText: m.fc_pct != null ? `${m.fc_pct.toFixed(1)}%` : '—',
    targetText: m.fc_target != null ? `${m.fc_target.toFixed(1)}%` : '—',
    variance: variance(m.fc_pct, m.fc_target),
    status: m.fc_pct == null || m.fc_target == null
      ? 'No target'
      : (m.fc_pct <= m.fc_target ? 'On target' : 'Over target'),
    statusTone: m.fc_pct == null || m.fc_target == null
      ? 'neutral'
      : tone(m.fc_pct, m.fc_target, true),
  });
  rows.push({
    label: 'Beverage Cost %',
    currentText: m.bc_pct != null ? `${m.bc_pct.toFixed(1)}%` : '—',
    targetText: m.bc_target != null ? `${m.bc_target.toFixed(1)}%` : '—',
    variance: variance(m.bc_pct, m.bc_target),
    status: m.bc_pct == null || m.bc_target == null
      ? 'No target'
      : (m.bc_pct <= m.bc_target ? 'On target' : 'Over target'),
    statusTone: m.bc_pct == null || m.bc_target == null
      ? 'neutral'
      : tone(m.bc_pct, m.bc_target, true),
  });
  rows.push({
    label: 'Energy Cost',
    currentText: m.energyActual != null ? _compactINR_v2(m.energyActual) : '—',
    targetText: m.energyBudget != null ? _compactINR_v2(m.energyBudget) : '—',
    variance: m.energyActual != null && m.energyBudget != null
      ? `${m.energyActual - m.energyBudget >= 0 ? '+' : ''}${_compactINR_v2(m.energyActual - m.energyBudget)}`
      : null,
    status: m.energyActual == null || m.energyBudget == null
      ? 'No budget'
      : (m.energyActual <= m.energyBudget ? 'Within budget' : 'Over budget'),
    statusTone: m.energyActual == null || m.energyBudget == null
      ? 'neutral'
      : (m.energyActual <= m.energyBudget ? 'good' : 'bad'),
  });
  return rows;
}

function _compactINR_v2(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const s = v < 0 ? '-' : '';
  if (abs >= 1e7) return `${s}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${s}₹${(abs / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${s}₹${(abs / 1e3).toFixed(1)}K`;
  return `${s}₹${abs.toFixed(0)}`;
}

function _outletPulseRowsV2(outlets, kind) {
  return (outlets || []).filter(countsTowardPortfolio).map((o) => {
    const daily = kind === 'daily';
    const fbSales = daily ? Number(o.net_sales || 0) : Number(o.total_sales || 0);
    const roomRev = daily ? Number(o.room_revenue || 0) : Number(o.total_room_revenue || 0);
    const revenue = o.total_revenue != null ? Number(o.total_revenue) : (fbSales + roomRev);
    const fbBudget = daily ? Number(o.budget || 0) : Number(o.total_budget || 0);
    const roomBudget = daily ? Number(o.room_revenue_budget || 0) : Number(o.total_room_budget || 0);
    const budget = fbBudget + roomBudget;
    const varAmt = revenue - budget;
    const varPct = budget > 0 ? (varAmt / budget) * 100 : null;
    const fc = daily ? o.food_cost_pct : o.avg_food_cost_pct;
    const bc = daily ? o.beverage_cost_pct : o.avg_beverage_cost_pct;
    const energyOutlet = (Number(o.electricity_cost || 0) + Number(o.gas_cost || 0)) || null;
    const statusTone = varPct == null ? 'neutral'
                     : varPct >= 5   ? 'good'
                     : varPct >= -5  ? 'ok'
                     :                 'bad';
    return {
      key: o.outlet_id || o.outlet_name,
      name: o.outlet_name,
      code: o.outlet_code || null,
      revenue, varAmt, varPct,
      apc: o.total_apc,
      fc, bc,
      energy: energyOutlet,
      statusTone,
    };
  });
}

function _utilitiesRowsV2(costEfficiency) {
  const list = (costEfficiency || []).filter(
    (r) => r && ['Electricity Cost', 'Gas Cost', 'Water Cost'].includes(r.metric));
  if (!list.length) return { rows: [], chart: [] };
  const rows = list.map((r) => {
    const actual = Number(r.actual || 0);
    const budget = r.budget != null ? Number(r.budget) : null;
    const varAmt = budget != null ? actual - budget : null;
    const varPct = budget != null && budget > 0 ? (varAmt / budget) * 100 : null;
    return {
      label: r.metric,
      currentText: _compactINR_v2(actual),
      targetText: budget != null ? _compactINR_v2(budget) : '—',
      variance: varAmt != null
        ? `${varAmt >= 0 ? '+' : ''}${_compactINR_v2(varAmt)}${varPct != null ? ` (${varPct.toFixed(0)}%)` : ''}`
        : null,
      status: budget == null ? 'No budget' : (varAmt <= 0 ? 'Within budget' : 'Over budget'),
      statusTone: budget == null ? 'neutral' : (varAmt <= 0 ? 'good' : 'bad'),
    };
  });
  const chart = list.map((r) => ({
    label: r.metric.replace(' Cost', ''),
    actual: Number(r.actual || 0),
    budget: r.budget != null ? Number(r.budget) : 0,
  }));
  return { rows, chart };
}

// Build the compact V2 chart series from the same _mtdProjectionSeries output.
function _revenueChartV2({ mtdData, selectedYear, selectedMonth }) {
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  return _mtdProjectionSeries({
    dailyTrend: mtdData?.daily_trend || [],
    dataThrough: mtdData?.data_through || null,
    year: selectedYear, month: selectedMonth, daysInMonth,
  });
}


export default function AdminDashboard({ viewerMode = 'admin', initialView } = {}) {
  const { getHeaders, API, user: authUser } = useAuth();
  const isManagerView = viewerMode === 'manager';
  // Manager assigned outlets — server-enforced scope. Frontend simply hides
  // scope selectors and pre-locks outletIds to the manager's assignment.
  const managerAssigned = useMemo(
    () => (authUser?.assigned_outlets || []).map((o) => o.id).filter(Boolean),
    [authUser],
  );
  const [tab, setTab] = useState('daily');
  // Total Revenue drilldown state (Feb 2026)
  const [trdOpen, setTrdOpen] = useState(false);
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const [selectedDate, setSelectedDate] = useState(yesterday);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [mtdData, setMtdData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [rangeData, setRangeData] = useState(null);
  // Per-view loading state (Part A perf).
  //
  // Previously ONE `loading` boolean was shared by loadDaily/MTD/YTD/Range
  // AND by the eager MTD+YTD prefetch fired from Daily. That meant Daily
  // waited for the slowest of THREE endpoints (daily, mtd, ytd) before
  // its own section could render. It also meant switching tabs re-blanked
  // the whole page.
  //
  // New model:
  //   dailyLoading  — /dashboard/daily
  //   mtdLoading    — /dashboard/mtd
  //   ytdLoading    — /dashboard/ytd
  //   rangeLoading  — /dashboard/range
  //   contextLoading — non-blocking MTD/YTD prefetch used ONLY by
  //     RevenueIntelligencePanel + RoomsAccommodationPanel on the Daily
  //     tab. The primary Daily section renders as soon as `dailyData`
  //     arrives; those secondary panels show their own skeleton until
  //     `contextLoading` completes.
  //
  // `loading` (the composite view spinner) is derived from whichever
  // loader owns the currently active tab. This keeps the pre-existing
  // spinner + skeleton pattern intact without a design change.
  const [dailyLoading, setDailyLoading] = useState(true);
  const [mtdLoading,   setMtdLoading]   = useState(false);
  const [ytdLoading,   setYtdLoading]   = useState(false);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  // Lightweight cross-period context — MTD + YTD headlines Daily needs
  // (portfolio_totals, daily_trend, rooms_kpi) WITHOUT the multi-KB
  // per-outlet rows. Populated only on the Daily tab.
  const [execContext, setExecContext] = useState(null);
  const execContextScopeRef = useRef(null);
  const execContextAbortRef = useRef(null);
  // Scope-key cache refs — remember the scope each cached payload belongs
  // to. Same scope + same tab = no refetch. Changed scope = invalidate
  // before render so we never display stale numbers.
  const mtdScopeRef = useRef(null);
  const ytdScopeRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [rangePreset, setRangePreset] = useState('');
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [rangeStartOpen, setRangeStartOpen] = useState(false);
  const [rangeEndOpen, setRangeEndOpen] = useState(false);
  const [portfolioId, setPortfolioId] = useState('');
  const [outletIds, setOutletIds] = useState(() => (isManagerView ? managerAssigned : []));
  // Sales-Race generalisation — pending draft for the multi-select; commit
  // on explicit Apply so checkbox ticks don't fire a fetch storm. Date /
  // tab changes continue to auto-fetch (single-value triggers, no storm).
  const [pendingPortfolioId, setPendingPortfolioId] = useState('');
  const [pendingOutletIds, setPendingOutletIds] = useState(() => (isManagerView ? managerAssigned : []));
  const scopeDirty = (
    pendingPortfolioId !== portfolioId ||
    pendingOutletIds.length !== outletIds.length ||
    pendingOutletIds.some(id => !outletIds.includes(id))
  );
  const applyScope = useCallback(() => {
    setPortfolioId(pendingPortfolioId);
    setOutletIds(pendingOutletIds);
  }, [pendingPortfolioId, pendingOutletIds]);
  const resetScope = useCallback(() => {
    setPendingPortfolioId(portfolioId);
    setPendingOutletIds(outletIds);
  }, [portfolioId, outletIds]);
  // Per-loader abort + seq-id refs — one each for daily / mtd / ytd / range.
  // Each loader cancels its OWN previous in-flight (cross-tab switching is
  // fine since the inactive tabs' effects don't fire load* anyway).
  const dailySeqRef = useRef(0);  const dailyAbortRef = useRef(null);
  const mtdSeqRef   = useRef(0);  const mtdAbortRef   = useRef(null);
  const ytdSeqRef   = useRef(0);  const ytdAbortRef   = useRef(null);
  const rangeSeqRef = useRef(0);  const rangeAbortRef = useRef(null);
  // First OutletFilter onChange (localStorage hydration) — sync live + pending.
  const hydratedRef = useRef(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  // Tenant-configurable insight thresholds (sales variance / NPS / checklist tags).
  const [insightThresholds, setInsightThresholds] = useState(null);
  // POS/Cost efficiency comes from insights/five-minute; used by
  // ExecutivePulse + UtilitiesEnergyPanel. Refetched when scope or
  // active tab date range changes.
  const [costEfficiency, setCostEfficiency] = useState([]);
  // LY-Pulse: when the active tab is NOT Daily, the LY Same Date / LY Same Day
  // Executive-Pulse cards anchor to the latest completed reporting day. We
  // reuse /dashboard/daily?date={data_through} — same backend logic; no
  // frontend date recreation.
  const [lyPulseData, setLyPulseData] = useState(null);

  // Manager view — keep outletIds locked to the authenticated manager's
  // assignment. Fires whenever auth hydrates.
  useEffect(() => {
    if (!isManagerView) return;
    if (managerAssigned.length === 0) return;
    setPortfolioId('');
    setOutletIds(managerAssigned);
    setPendingPortfolioId('');
    setPendingOutletIds(managerAssigned);
    hydratedRef.current = true;
  }, [isManagerView, managerAssigned]);

  // Fetch insight thresholds once on mount (admin-scoped, may 404 for non-admin → silent fallback)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/insight-thresholds`, { headers: getHeaders() });
        setInsightThresholds(res.data);
      } catch { /* default thresholds will be used */ }
    })();
  }, []); // eslint-disable-line

  const scopeParam = (() => {
    const parts = [];
    if (portfolioId) parts.push(`portfolio_id=${portfolioId}`);
    if (outletIds && outletIds.length > 0) parts.push(`outlet_ids=${outletIds.join(',')}`);
    return parts.length ? `&${parts.join('&')}` : '';
  })();

  // Fetch 7-day trends keyed by current tab's reference date.
  const loadDaily = useCallback(async () => {
    if (dailyAbortRef.current) { try { dailyAbortRef.current.abort(); } catch { /* noop */ } }
    const controller = new AbortController();
    dailyAbortRef.current = controller;
    const mySeq = ++dailySeqRef.current;
    const isLatest = () => mySeq === dailySeqRef.current && !controller.signal.aborted;
    setDailyLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const dailyRes = await axios.get(`${API}/dashboard/daily?date=${dateStr}${scopeParam}`,
        { headers: getHeaders(), signal: controller.signal });
      if (!isLatest()) return;
      setDailyData(dailyRes.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Failed to load daily dashboard:', err);
      if (isLatest()) setDailyData(null);
    } finally {
      if (mySeq === dailySeqRef.current) setDailyLoading(false);
    }
  }, [selectedDate, API, getHeaders, scopeParam]);

  const loadMTD = useCallback(async ({ background = false } = {}) => {
    if (mtdAbortRef.current) { try { mtdAbortRef.current.abort(); } catch { /* noop */ } }
    const controller = new AbortController();
    mtdAbortRef.current = controller;
    const mySeq = ++mtdSeqRef.current;
    const isLatest = () => mySeq === mtdSeqRef.current && !controller.signal.aborted;
    // Background fetches (Daily's eager prefetch for Rooms/RevenueIntel
    // panels) MUST NOT flip mtdLoading — that would block the primary
    // Daily view. Instead they show up on contextLoading.
    if (background) setContextLoading(true); else setMtdLoading(true);
    try {
      const mtdRes = await axios.get(`${API}/dashboard/mtd?year=${selectedYear}&month=${selectedMonth}${scopeParam}`,
        { headers: getHeaders(), signal: controller.signal });
      if (!isLatest()) return;
      setMtdData(mtdRes.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Failed to load MTD dashboard:', err);
      if (isLatest() && !background) setMtdData(null);
    } finally {
      if (mySeq === mtdSeqRef.current) {
        if (background) setContextLoading(false); else setMtdLoading(false);
      }
    }
  }, [selectedMonth, selectedYear, API, getHeaders, scopeParam]);

  const loadYTD = useCallback(async ({ background = false } = {}) => {
    if (ytdAbortRef.current) { try { ytdAbortRef.current.abort(); } catch { /* noop */ } }
    const controller = new AbortController();
    ytdAbortRef.current = controller;
    const mySeq = ++ytdSeqRef.current;
    const isLatest = () => mySeq === ytdSeqRef.current && !controller.signal.aborted;
    if (background) setContextLoading(true); else setYtdLoading(true);
    try {
      const qs = scopeParam.replace(/^&/, '?') || '';
      const res = await axios.get(`${API}/dashboard/ytd${qs}`,
        { headers: getHeaders(), signal: controller.signal });
      if (!isLatest()) return;
      setYtdData(res.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Failed to load YTD dashboard:', err);
      if (isLatest() && !background) setYtdData(null);
    } finally {
      if (mySeq === ytdSeqRef.current) {
        if (background) setContextLoading(false); else setYtdLoading(false);
      }
    }
  }, [API, getHeaders, scopeParam]);

  const loadRange = useCallback(async () => {
    if (!rangeStart || !rangeEnd) return;
    if (rangeAbortRef.current) { try { rangeAbortRef.current.abort(); } catch { /* noop */ } }
    const controller = new AbortController();
    rangeAbortRef.current = controller;
    const mySeq = ++rangeSeqRef.current;
    const isLatest = () => mySeq === rangeSeqRef.current && !controller.signal.aborted;
    setRangeLoading(true);
    try {
      const s = format(rangeStart, 'yyyy-MM-dd');
      const e = format(rangeEnd, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/dashboard/range?start_date=${s}&end_date=${e}${scopeParam}`,
        { headers: getHeaders(), signal: controller.signal });
      if (!isLatest()) return;
      setRangeData(res.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Failed to load range dashboard:', err);
      if (isLatest()) setRangeData(null);
    } finally {
      if (mySeq === rangeSeqRef.current) setRangeLoading(false);
    }
  }, [rangeStart, rangeEnd, API, getHeaders, scopeParam]);

  useEffect(() => {
    if (tab === 'daily') loadDaily();
  }, [tab, loadDaily]);

  useEffect(() => {
    if (tab === 'mtd') loadMTD();
  }, [tab, loadMTD]);

  useEffect(() => {
    if (tab === 'ytd') loadYTD();
  }, [tab, loadYTD]);

  useEffect(() => {
    if (tab === 'range' && rangeStart && rangeEnd) loadRange();
  }, [tab, rangeStart, rangeEnd, loadRange]);

  // ── Executive Context (Part A · lightweight cross-period fetch) ──
  //
  // Daily's Rooms + Revenue-Intelligence + Cumulative-Chart panels need
  // MTD/YTD headlines (portfolio_totals + daily_trend + rooms rollup).
  // Previously we fetched the FULL /dashboard/mtd + /dashboard/ytd
  // payloads for that alone — many KB of per-outlet rows Daily never
  // renders. We now fetch a slim /dashboard/executive-context response
  // that returns ONLY those projected fields, computed by the same
  // canonical helpers used by the full endpoints (zero formula drift).
  //
  // Full MTD / full YTD now fire ONLY when the user actually opens
  // those tabs. Cache-aware — same scope+period is a no-op.
  const execContextScopeKey = `ec::${selectedYear}|${selectedMonth}|${scopeParam}`;
  const mtdScopeKey = `${selectedYear}|${selectedMonth}|${scopeParam}`;
  const ytdScopeKey = `${selectedYear}|${scopeParam}`;
  useEffect(() => {
    // Daily / MTD / Range need the lightweight cross-period context.
    // Daily uses it for MTD + YTD hero + trend; MTD uses it as the
    // canonical YTD source (full /dashboard/ytd payload is loaded ONLY on
    // the actual YTD tab). Range uses it for its context panel.
    // The YTD tab has its own full payload and doesn't need this.
    if (tab !== 'daily' && tab !== 'range' && tab !== 'mtd') return;
    if (execContext && execContextScopeRef.current === execContextScopeKey) return;
    if (execContextAbortRef.current) {
      try { execContextAbortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    execContextAbortRef.current = controller;
    setContextLoading(true);
    (async () => {
      try {
        const qs = `year=${selectedYear}&month=${selectedMonth}${scopeParam}`;
        const res = await axios.get(`${API}/dashboard/executive-context?${qs}`,
          { headers: getHeaders(), signal: controller.signal });
        if (controller.signal.aborted) return;
        setExecContext(res.data);
        execContextScopeRef.current = execContextScopeKey;
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        if (!controller.signal.aborted) {
          console.error('Failed to load executive context:', err);
          setExecContext(null);
        }
      } finally {
        if (!controller.signal.aborted) setContextLoading(false);
      }
    })();
    return () => { try { controller.abort(); } catch { /* noop */ } };
  }, [tab, execContextScopeKey, execContext, API, getHeaders, selectedYear, selectedMonth, scopeParam]);

  // Scope/period-change cache invalidation — never render stale numbers
  // under a changed scope. Applies to all three cache refs.
  useEffect(() => {
    if (mtdScopeRef.current && mtdScopeRef.current !== mtdScopeKey) {
      if (tab === 'mtd') setMtdData(null);
    }
    if (ytdScopeRef.current && ytdScopeRef.current !== ytdScopeKey) {
      if (tab === 'ytd') setYtdData(null);
    }
    if (execContextScopeRef.current && execContextScopeRef.current !== execContextScopeKey) {
      setExecContext(null);
    }
  }, [mtdScopeKey, ytdScopeKey, execContextScopeKey, tab]);

  // Keep the scope-key ref in sync as data lands (MTD + YTD full payloads).
  useEffect(() => { if (mtdData) mtdScopeRef.current = mtdScopeKey; }, [mtdData, mtdScopeKey]);
  useEffect(() => { if (ytdData) ytdScopeRef.current = ytdScopeKey; }, [ytdData, ytdScopeKey]);

  // LY-Pulse fetch — for MTD/YTD/Range tabs, anchor LY Same Date / Same Day
  // Executive Pulse cards to the latest completed reporting day. Reuses the
  // existing /dashboard/daily endpoint (backend LY-date logic is authoritative).
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        let anchorDate = null;
        if (tab === 'mtd') anchorDate = mtdData?.data_through || null;
        else if (tab === 'ytd') anchorDate = ytdData?.fy_end || null;
        else if (tab === 'range') anchorDate = rangeEnd ? format(rangeEnd, 'yyyy-MM-dd') : null;
        else { setLyPulseData(null); return; }
        if (!anchorDate) { setLyPulseData(null); return; }
        const res = await axios.get(
          `${API}/dashboard/daily?date=${anchorDate}${scopeParam}`,
          { headers: getHeaders(), signal: controller.signal });
        if (!cancelled) setLyPulseData(res.data);
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        if (!cancelled) setLyPulseData(null);
      }
    })();
    return () => { cancelled = true; try { controller.abort(); } catch { /* noop */ } };
  }, [tab, mtdData?.data_through, ytdData?.fy_end, rangeEnd, scopeParam, API, getHeaders]);

  // Cost efficiency (Food Cost, Beverage Cost, Electricity, Gas) is
  // sourced from /api/insights/five-minute (existing endpoint). It is
  // refetched whenever the visible period, portfolio, or outlet scope
  // changes so ExecutivePulse and CostEfficiencyPanel stay in sync
  // with the primary Overview.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        let start, end;
        if (tab === 'daily') {
          const s = format(selectedDate, 'yyyy-MM-dd');
          start = s; end = s;
        } else if (tab === 'mtd') {
          const first = new Date(selectedYear, selectedMonth - 1, 1);
          const last  = new Date(selectedYear, selectedMonth, 0);
          start = format(first, 'yyyy-MM-dd');
          end   = format(last, 'yyyy-MM-dd');
        } else if (tab === 'ytd') {
          start = `${selectedYear}-01-01`;
          end   = `${selectedYear}-12-31`;
        } else if (tab === 'range' && rangeStart && rangeEnd) {
          start = format(rangeStart, 'yyyy-MM-dd');
          end   = format(rangeEnd, 'yyyy-MM-dd');
        } else {
          setCostEfficiency([]);
          return;
        }
        const res = await axios.get(
          `${API}/insights/five-minute?start_date=${start}&end_date=${end}${scopeParam}`,
          { headers: getHeaders(), signal: controller.signal });
        setCostEfficiency(res.data?.cost_efficiency || []);
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setCostEfficiency([]);
      }
    })();
    return () => { try { controller.abort(); } catch { /* noop */ } };
  }, [tab, selectedDate, selectedYear, selectedMonth, rangeStart, rangeEnd, scopeParam, API, getHeaders]);

  const applyRangePreset = (preset) => {
    setRangePreset(preset);
    const today = new Date();
    if (preset === 'last7') {
      setRangeStart(subDays(today, 6));
      setRangeEnd(today);
    } else if (preset === 'last30') {
      setRangeStart(subDays(today, 29));
      setRangeEnd(today);
    } else if (preset === 'lastMonth') {
      const lastM = subMonths(today, 1);
      setRangeStart(startOfMonth(lastM));
      setRangeEnd(endOfMonth(lastM));
    } else if (preset === 'custom') {
      // user picks manually
    }
  };

  // Single source of truth — backend-computed portfolio_totals.
  // The frontend NEVER re-aggregates: KPI strip, chart, table, exports all
  // read the SAME numbers from `dailyData.portfolio_totals`. This eliminates
  // any drift between widgets (the long-standing double-counting bug for
  // sub-units).
  //
  // CANONICAL fields (must match every widget): `total_revenue` = F&B + Rooms,
  // `budget` = F&B budget + Room budget, `variance_amt` = total_revenue - budget.
  // Reading `fb_sales` / `budget_fb` here was the root cause of the Hero
  // showing "Above Budget" while the Variance card showed a negative delta.
  const dailyPT = dailyData?.portfolio_totals || null;
  const dailyTotalsPool = (dailyData?.outlets || []).filter(countsTowardPortfolio);
  const totalNetSales = dailyPT ? (dailyPT.total_revenue || 0) : 0;
  const totalBudget = dailyPT ? (dailyPT.budget || 0) : 0;
  const totalVariance = dailyPT ? (dailyPT.variance_amt || 0) : 0;
  const outletsReporting = dailyPT ? (dailyPT.outlets_reporting || 0) : 0;

  // Daily aggregates for Hotel KPIs, Product Mix, Service Split.
  // Same shape the components expect, but values come from backend
  // `portfolio_totals` instead of a parallel reduce.
  const dailyAgg = (() => {
    if (!dailyPT) {
      return {
        totalFood: 0, totalBev: 0, totalFoodCost: 0, totalBevCost: 0,
        hasFcData: false, hasBcData: false,
        totalRoomRev: 0, totalRoomsSold: 0, totalRoomsAvail: 0,
        totalLunchSales: 0, totalDinnerSales: 0,
        totalLunchCovers: 0, totalDinnerCovers: 0,
        totalCovers: 0, grandRevenue: 0,
        totalChannelDineIn: 0, totalChannelSwiggy: 0,
        totalChannelZomato: 0, totalChannelOther: 0,
        foodCostPct: null, bevCostPct: null,
        occupancy: null, arr: null, revpar: null,
        foodPct: null, bevPct: null,
        lunchPct: null, dinnerPct: null,
        apc: null, foodApc: null, bevApc: null,
      };
    }
    // Room rollup still needs a small reduce — backend exposes
    // portfolio room_revenue but not rooms_sold/avail (computed below).
    const rows = (dailyData?.outlets || []).filter(o => o.has_entry && countsTowardPortfolio(o));
    const sumRow = (k) => rows.reduce((s, o) => s + (Number(o[k]) || 0), 0);
    const totalRoomsSold = sumRow('rooms_sold');
    const totalRoomsAvail = sumRow('rooms_available');
    return {
      totalFood: dailyPT.food_sale || 0,
      totalBev: dailyPT.beverage_sale || 0,
      totalFoodCost: dailyPT.food_cost || 0,
      totalBevCost: dailyPT.beverage_cost || 0,
      hasFcData: rows.some(o => o.food_cost != null),
      hasBcData: rows.some(o => o.beverage_cost != null),
      totalRoomRev: dailyPT.room_revenue || 0,
      totalRoomsSold, totalRoomsAvail,
      totalLunchSales: dailyPT.lunch_sales || 0,
      totalDinnerSales: dailyPT.dinner_sales || 0,
      totalLunchCovers: dailyPT.lunch_covers || 0,
      totalDinnerCovers: dailyPT.dinner_covers || 0,
      totalCovers: dailyPT.covers || 0,
      grandRevenue: dailyPT.total_revenue || 0,
      totalChannelDineIn: dailyPT.channel_dine_in || 0,
      totalChannelSwiggy: dailyPT.channel_swiggy || 0,
      totalChannelZomato: dailyPT.channel_zomato || 0,
      totalChannelOther: dailyPT.channel_other || 0,
      foodCostPct: dailyPT.food_cost_pct,
      bevCostPct: dailyPT.beverage_cost_pct,
      occupancy: totalRoomsAvail > 0 ? (totalRoomsSold / totalRoomsAvail * 100) : null,
      arr: totalRoomsSold > 0 ? ((dailyPT.room_revenue || 0) / totalRoomsSold) : null,
      revpar: totalRoomsAvail > 0 ? ((dailyPT.room_revenue || 0) / totalRoomsAvail) : null,
      foodPct: dailyPT.food_pct_mix,
      bevPct: dailyPT.bev_pct_mix,
      lunchPct: (dailyPT.lunch_sales + dailyPT.dinner_sales) > 0
        ? (dailyPT.lunch_sales / (dailyPT.lunch_sales + dailyPT.dinner_sales) * 100) : null,
      dinnerPct: (dailyPT.lunch_sales + dailyPT.dinner_sales) > 0
        ? (dailyPT.dinner_sales / (dailyPT.lunch_sales + dailyPT.dinner_sales) * 100) : null,
      apc: dailyPT.apc,
      foodApc: dailyPT.covers > 0 ? (dailyPT.food_sale / dailyPT.covers) : null,
      bevApc: dailyPT.covers > 0 ? (dailyPT.beverage_sale / dailyPT.covers) : null,
    };
  })();
  const hasDailyRooms = dailyAgg.totalRoomRev > 0 || dailyAgg.totalRoomsSold > 0;

  // ── Reconciliation guard (dev-only) ──
  // Verifies that a KPI never differs between Executive Pulse, Outlet Pulse
  // sums, Outlet Budget chart and Detailed Comparison portfolio totals. Runs
  // in the browser console; NO user-visible UI. Tolerance: ₹1 for money,
  // matched rounding for percentages.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const check = (label, ptSource, outletsList, kind) => {
      if (!ptSource || !outletsList?.length) return;
      const includedOutlets = outletsList.filter(countsTowardPortfolio);
      const daily = kind === 'daily';
      const sumRev = includedOutlets.reduce((s, o) => {
        const fb = daily ? Number(o.net_sales || 0) : Number(o.total_sales || 0);
        const room = daily ? Number(o.room_revenue || 0) : Number(o.total_room_revenue || 0);
        return s + (o.total_revenue != null ? Number(o.total_revenue) : (fb + room));
      }, 0);
      const sumBudget = includedOutlets.reduce((s, o) => {
        const fbB = daily ? Number(o.budget || 0) : Number(o.total_budget || 0);
        const roomB = daily ? Number(o.room_revenue_budget || 0) : Number(o.total_room_budget || 0);
        return s + fbB + roomB;
      }, 0);
      const revDelta = Math.abs(sumRev - Number(ptSource.total_revenue || 0));
      const budDelta = Math.abs(sumBudget - Number(ptSource.budget || 0));
      if (revDelta > 1) console.warn(`[Recon:${label}] Revenue drift ₹${revDelta.toFixed(2)} — outlet sum=${sumRev.toFixed(2)} vs pt.total_revenue=${ptSource.total_revenue}`);
      if (budDelta > 1) console.warn(`[Recon:${label}] Budget drift ₹${budDelta.toFixed(2)} — outlet sum=${sumBudget.toFixed(2)} vs pt.budget=${ptSource.budget}`);
    };
    if (tab === 'daily') check('daily', dailyData?.portfolio_totals, dailyData?.outlets, 'daily');
    if (tab === 'mtd')   check('mtd',   mtdData?.portfolio_totals,   mtdData?.outlets,   'mtd');
  }, [tab, dailyData, mtdData]);

  const handleExportDaily = () => {
    if (!dailyData?.outlets) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const rows = formatDailyExport(dailyData.outlets, dateStr);
    if (rows.length === 0) { toast.info('No data to export'); return; }
    downloadCSV(rows, `daily-report-${dateStr}`);
    toast.success('Daily report exported');
  };

  const handleExportMTD = () => {
    if (!mtdData?.outlets) return;
    const rows = formatMTDExport(mtdData.outlets, mtdData.month);
    if (rows.length === 0) { toast.info('No data to export'); return; }
    downloadCSV(rows, `mtd-report-${mtdData.month}`);
    toast.success('MTD report exported');
  };

  const handlePrint = () => window.print();

  // iter207 — Top-level view selector: Overview vs Sales Analytics.
  // Overview (default) renders today's existing dashboard untouched.
  // Sales Analytics renders the new reporting layer (no new finance logic).
  // Persist the choice in sessionStorage so a soft reload stays on the same view.
  const [dashboardView, setDashboardView] = useState(() => {
    if (initialView === 'overview' || initialView === 'sales-analytics') {
      return initialView;
    }
    try { return sessionStorage.getItem('dash_view') || 'overview'; } catch { return 'overview'; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('dash_view', dashboardView); } catch (e) { /* noop */ }
  }, [dashboardView]);

  if (dashboardView === 'sales-analytics') {
    return (
      <div data-testid="admin-dashboard-view">
        <div className="mb-3 flex items-center gap-2" data-testid="dashboard-view-selector">
          <Tabs value={dashboardView} onValueChange={setDashboardView}>
            <TabsList className="bg-[#F3F5F2] h-9">
              <TabsTrigger value="overview"
                className="h-7 px-3 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white"
                data-testid="dash-view-overview">Overview</TabsTrigger>
              <TabsTrigger value="sales-analytics"
                className="h-7 px-3 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white"
                data-testid="dash-view-sales-analytics">Sales Analytics</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <SalesAnalyticsView viewerMode={viewerMode} />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-view">
      {/* iter207 — View selector above the existing Overview filter bar */}
      <div className="mb-3 flex items-center gap-2" data-testid="dashboard-view-selector">
        <Tabs value={dashboardView} onValueChange={setDashboardView}>
          <TabsList className="bg-[#F3F5F2] h-9">
            <TabsTrigger value="overview"
              className="h-7 px-3 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white"
              data-testid="dash-view-overview">Overview</TabsTrigger>
            <TabsTrigger value="sales-analytics"
              className="h-7 px-3 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white"
              data-testid="dash-view-sales-analytics">Sales Analytics</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {/* ONE-ROW FILTER BAR — Portfolio/Outlet | Tabs | Context date controls | Exports */}
      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap mb-3" data-testid="dashboard-filter-bar">
        {isManagerView ? (
          <div className="flex items-center gap-2 min-w-0" data-testid="manager-outlet-context">
            {(authUser?.assigned_outlets || []).length > 0 && (
              <span className="text-[11px] uppercase tracking-widest text-[#7A8B82]">Outlet</span>
            )}
            <div className="flex items-center gap-1.5">
              {(authUser?.assigned_outlets || []).map((o) => (
                <span key={o.id}
                      data-testid={`manager-outlet-chip-${o.id}`}
                      className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)] border border-[color:var(--color-primary)]/20">
                  {o.name}
                </span>
              ))}
            </div>
          </div>
        ) : (
        <div className="flex items-center gap-2 min-w-0">
          <OutletFilter
            portfolioId={pendingPortfolioId}
            outletIds={pendingOutletIds}
            onChange={({ portfolioId: pid, outletIds: oids }) => {
              if (!hydratedRef.current) {
                hydratedRef.current = true;
                setPortfolioId(pid || '');
                setOutletIds(oids || []);
              }
              setPendingPortfolioId(pid || '');
              setPendingOutletIds(oids || []);
            }}
          />
          <Button
            size="sm"
            disabled={!scopeDirty}
            onClick={applyScope}
            data-testid="dash-apply-scope"
            className={`h-8 text-xs ${scopeDirty
              ? 'bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
            Apply
          </Button>
          {scopeDirty && (
            <button type="button"
              onClick={resetScope}
              data-testid="dash-reset-scope"
              className="text-[10px] text-slate-500 hover:text-slate-700 underline underline-offset-2">
              reset
            </button>
          )}
        </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="shrink-0">
          <TabsList className="bg-[#F3F5F2] h-9">
            <TabsTrigger value="daily" className="h-7 px-2.5 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="daily-tab">Daily</TabsTrigger>
            <TabsTrigger value="mtd" className="h-7 px-2.5 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="mtd-tab">MTD</TabsTrigger>
            <TabsTrigger value="ytd" className="h-7 px-2.5 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="ytd-tab">YTD</TabsTrigger>
            <TabsTrigger value="range" className="h-7 px-2.5 text-xs data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="range-tab">
              <Filter className="w-3 h-3 mr-1" /> Range
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Tab-specific context controls */}
        {tab === 'daily' && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="outline" size="sm"
              onClick={() => { const y = new Date(); y.setDate(y.getDate() - 1); setSelectedDate(y); }}
              className={`h-9 px-2.5 border-[#E2E8DF] text-[color:var(--color-text-muted)] hover:bg-[#F3F5F2] ${
                format(selectedDate, 'yyyy-MM-dd') === format((() => { const y = new Date(); y.setDate(y.getDate() - 1); return y; })(), 'yyyy-MM-dd')
                  ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : ''
              }`}
              data-testid="yesterday-btn">Yesterday</Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 px-2.5 border-[#E2E8DF] text-[color:var(--color-text-primary)] hover:bg-[#F3F5F2]" data-testid="dashboard-date-picker">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[color:var(--color-text-muted)]" />
                  {format(selectedDate, 'dd MMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={selectedDate}
                  onSelect={d => { if (d) { setSelectedDate(d); setCalendarOpen(false); } }}
                  disabled={d => d > new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {tab === 'mtd' && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[115px] h-9 border-[#E2E8DF]" data-testid="mtd-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[90px] h-9 border-[#E2E8DF]" data-testid="mtd-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Right-aligned action cluster */}
        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          <Button variant="outline" size="sm" onClick={handlePrint}
            className="h-9 px-2.5 border-[#E2E8DF] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-primary)] hover:bg-[#F3F5F2]"
            data-testid="print-button">
            <Printer className="w-3.5 h-3.5 sm:mr-1.5" strokeWidth={1.5} /><span className="hidden sm:inline">Print</span>
          </Button>
          <Button variant="outline" size="sm"
            onClick={tab === 'daily' ? handleExportDaily : handleExportMTD}
            className="h-9 px-2.5 border-[#E2E8DF] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-primary)] hover:bg-[#F3F5F2]"
            data-testid="export-csv-button">
            <Download className="w-3.5 h-3.5 sm:mr-1.5" strokeWidth={1.5} /><span className="hidden sm:inline">CSV</span>
          </Button>
          <Button size="sm" onClick={() => setReportModalOpen(true)}
            className="h-9 px-2.5 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="export-report-button">
            <Download className="w-3.5 h-3.5 sm:mr-1.5" strokeWidth={1.5} /><span className="hidden sm:inline">Report</span>
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsContent value="daily">
          {dailyLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : (() => {
            const pt = dailyData?.portfolio_totals || null;
            const hasRooms = pt ? Number(pt.room_revenue || 0) > 0 : false;
            // Daily reads MTD/YTD headlines from the lightweight
            // `execContext` payload, NOT from full /dashboard/mtd/ytd.
            // Fields available: {portfolio_totals, daily_trend,
            // data_through, rooms_kpi} on each side.
            const mtdCtx = execContext?.mtd || null;
            const ytdCtx = execContext?.ytd || null;
            const metrics = buildOverviewMetrics({
              kind: 'daily',
              pt,
              outlets: dailyData?.outlets || [],
              costEfficiency,
              lyPulse: null, // Daily scope reads LY directly from outlet rows
            });
            return (
            <>
              <OverviewHeader
                title="Yesterday's Performance"
                subtitle={`${format(selectedDate, 'dd MMM yyyy')} • ${outletsReporting}/${dailyTotalsPool.length || 0} outlets reported`}
                testId="daily-overview-header"
              />

              {/* V2 · 3 hero KPI cards + section shell */}
              {(() => {
                const proj = _revenueChartV2({ mtdData: mtdCtx, selectedYear, selectedMonth });
                const mtdPt = mtdCtx?.portfolio_totals || {};
                const ytdPt = ytdCtx?.portfolio_totals || {};
                const ytdActual = Number(ytdPt.total_revenue || 0);
                const ytdBudget = Number(ytdPt.budget || 0);
                const spark = (proj.series || []).map((p) => p.actualCum).filter((v) => v != null);
                return (
                  <HeroKpiRow
                    testIdPrefix="daily-exec-hero"
                    totalRevenue={metrics.revenue}
                    totalBudget={metrics.budget}
                    totalVarAmt={metrics.variance_amt}
                    totalVarPct={metrics.variance_pct}
                    fbSales={metrics.fbSales} roomRev={metrics.roomRev} hasRooms={hasRooms}
                    revenueSparkline={spark.length ? spark : null}
                    projectedClose={proj.projectedTotal}
                    projectedBudget={mtdPt.full_month_budget}
                    projectedVarPct={(proj.projectedTotal != null && mtdPt.full_month_budget)
                      ? ((proj.projectedTotal - mtdPt.full_month_budget) / mtdPt.full_month_budget) * 100 : null}
                    projectionAvailable={proj.projectedTotal != null}
                    projectionMsg="Projection unavailable — need 3+ reported days"
                    ytdActual={ytdActual} ytdBudget={ytdBudget}
                    ytdVarAmt={ytdActual - ytdBudget}
                    ytdVarPct={ytdBudget > 0 ? ((ytdActual - ytdBudget) / ytdBudget) * 100 : null}
                    ytdSparkline={null}
                    onTotalRevenueClick={() => setTrdOpen(true)}
                  />
                );
              })()}

              {/* Detailed Outlet Comparison — moved directly under Hero (Feb 2026 spec §3) */}
              <DetailedComparisonSection
                outlets={dailyData?.outlets?.slice().sort((a, b) => (b.has_entry ? 1 : 0) - (a.has_entry ? 1 : 0)) || []}
                kind="daily"
                testId="daily-detailed-comparison"
              />

              {/* Total Revenue drilldown modal */}
              <TotalRevenueDrilldownModal
                open={trdOpen}
                onClose={() => setTrdOpen(false)}
                kind="daily"
                outlets={dailyData?.outlets || []}
                periodLabel={format(selectedDate, 'dd MMM yyyy')}
                heroActual={metrics.revenue}
                heroTarget={metrics.budget}
              />

              {/* Revenue Performance — chart + summary table */}
              {(() => {
                const proj = _revenueChartV2({ mtdData: mtdCtx, selectedYear, selectedMonth });
                const mtdPt = mtdCtx?.portfolio_totals || {};
                const ytdPt = ytdCtx?.portfolio_totals || {};
                return (
                  <RevenuePerformanceSection
                    testId="daily-revenue-performance"
                    mtdSeries={proj.series}
                    projectedTotal={proj.projectedTotal}
                    budgetToDate={Number(mtdPt.budget || 0)}
                    mtdActual={Number(mtdPt.total_revenue || 0)}
                    fullMonthBudget={mtdPt.full_month_budget}
                    fullMonthBudgetAvailable={!!mtdPt.full_month_budget_available}
                    ytdActual={Number(ytdPt.total_revenue || 0)}
                    ytdBudget={Number(ytdPt.budget || 0)}
                    periodLabel={format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}
                  />
                );
              })()}

              {/* Financial Snapshot */}
              <FinancialSnapshotTable
                testId="daily-financial-snapshot"
                rows={_buildFinancialSnapshotRowsV2(metrics)}
              />

              {/* Rooms & Accommodation — served from the slim
                  execContext.rooms_kpi rollup (computed by the same
                  canonical sum-formula server-side; no per-outlet
                  rows shipped to Daily). */}
              <RoomsAccommodationSection
                testId="daily-rooms-section"
                mtdKpi={mtdCtx?.rooms_kpi || null}
                ytdKpi={ytdCtx?.rooms_kpi || null}
              />

              {/* Outlet Pulse table */}
              <OutletPulseTable
                testId="daily-outlet-pulse-table"
                rows={_outletPulseRowsV2(dailyData?.outlets || [], 'daily')}
              />

              {/* Utilities · Energy */}
              {(() => {
                const u = _utilitiesRowsV2(costEfficiency);
                return (
                  <UtilitiesEnergySection
                    testId="daily-utilities-energy-v2"
                    rows={u.rows}
                    chartData={u.chart}
                  />
                );
              })()}

              <MixDoughnutSection
                foodPct={dailyAgg.foodPct}
                bevPct={dailyAgg.bevPct}
                foodSale={dailyAgg.totalFood}
                bevSale={dailyAgg.totalBev}
                lunchPct={dailyAgg.lunchPct}
                dinnerPct={dailyAgg.dinnerPct}
                lunchSales={dailyAgg.totalLunchSales}
                dinnerSales={dailyAgg.totalDinnerSales}
                channelDineIn={dailyAgg.totalChannelDineIn}
                channelSwiggy={dailyAgg.totalChannelSwiggy}
                channelZomato={dailyAgg.totalChannelZomato}
                channelOther={dailyAgg.totalChannelOther}
                testId="daily-mix-doughnut"
              />

              <PortfolioSignals
                outlets={dailyData?.outlets || []}
                kind="daily"
                portfolioTotals={pt}
                costEfficiency={costEfficiency}
                testId="daily-portfolio-signals"
              />

              {/* Detailed Outlet Comparison moved to top (see spec §3) */}

              {dailyData?.alerts?.length > 0 && (
                <Card className="border-[#E2E8DF] shadow-sm mt-3">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-[color:var(--color-text-primary)]">
                      <AlertTriangle className="w-4 h-4 text-[#C45A41]" strokeWidth={1.5} />
                      Alerts ({dailyData.alerts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {dailyData.alerts.map((alert, i) => (
                        <AlertCard key={`${alert.type}-${alert.outlet}-${i}`} alert={alert} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
            );
          })()}
        </TabsContent>

        <TabsContent value="mtd">
          {mtdLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : (() => {
            const os = mtdData?.outlets || [];
            const pt = mtdData?.portfolio_totals || null;
            // Single source of truth — backend portfolio_totals. Sub-units
            // are filtered server-side (counts_toward_portfolio).
            const totRoomRev = pt ? pt.room_revenue : 0;
            const totPool = os.filter(countsTowardPortfolio);
            const reporting = pt ? (pt.outlets_reporting || 0) : 0;
            const totFood = pt ? pt.food_sale : 0;
            const totBev = pt ? pt.beverage_sale : 0;
            const totLunch = pt ? pt.lunch_sales : 0;
            const totDinner = pt ? pt.dinner_sales : 0;
            const totChDineIn = pt ? pt.channel_dine_in : 0;
            const totChSwiggy = pt ? pt.channel_swiggy : 0;
            const totChZomato = pt ? pt.channel_zomato : 0;
            const totChOther = pt ? pt.channel_other : 0;
            const foodPct = pt ? pt.food_pct_mix : null;
            const bevPct = pt ? pt.bev_pct_mix : null;
            const lunchPct = (totLunch + totDinner) > 0 ? (totLunch / (totLunch + totDinner) * 100) : null;
            const dinnerPct = (totLunch + totDinner) > 0 ? (totDinner / (totLunch + totDinner) * 100) : null;
            const hasRooms = totRoomRev > 0;

            // Canonical metrics mapper — LY cards anchored to lyPulseData (latest completed day)
            const metrics = buildOverviewMetrics({
              kind: 'mtd',
              pt,
              outlets: os,
              costEfficiency,
              lyPulse: lyPulseData,
            });

            return (
              <>
                <OverviewHeader
                  title="MTD Performance"
                  subtitle={(() => {
                    const monthName = format(new Date(selectedYear, selectedMonth - 1, 1), 'MMM yyyy');
                    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
                    return `1–${lastDay} ${monthName} • ${reporting}/${totPool.length} outlets reported`;
                  })()}
                  testId="mtd-overview-header"
                />

                {/* V2 · 3 hero KPI cards */}
                {(() => {
                  const proj = _revenueChartV2({ mtdData, selectedYear, selectedMonth });
                  // Canonical YTD source for MTD view = executive-context
                  // (Daily and MTD share the same lightweight YTD context;
                  // full /dashboard/ytd payload is fetched ONLY on the YTD tab).
                  const ytdPt = execContext?.ytd?.portfolio_totals || null;
                  const ytdActual = ytdPt ? Number(ytdPt.total_revenue || 0) : null;
                  const ytdBudget = ytdPt ? Number(ytdPt.budget || 0) : null;
                  const spark = (proj.series || []).map((p) => p.actualCum).filter((v) => v != null);
                  return (
                    <HeroKpiRow
                      testIdPrefix="mtd-exec-hero"
                      totalRevenue={metrics.revenue}
                      totalBudget={metrics.budget}
                      totalVarAmt={metrics.variance_amt}
                      totalVarPct={metrics.variance_pct}
                      fbSales={metrics.fbSales} roomRev={metrics.roomRev} hasRooms={hasRooms}
                      revenueSparkline={spark.length ? spark : null}
                      projectedClose={proj.projectedTotal}
                      projectedBudget={metrics.fullMonthBudget}
                      projectedVarPct={(proj.projectedTotal != null && metrics.fullMonthBudget)
                        ? ((proj.projectedTotal - metrics.fullMonthBudget) / metrics.fullMonthBudget) * 100 : null}
                      projectionAvailable={proj.projectedTotal != null}
                      projectionMsg="Projection unavailable — need 3+ reported days"
                      ytdActual={ytdActual}
                      ytdBudget={ytdBudget}
                      ytdVarAmt={(ytdActual != null && ytdBudget != null) ? ytdActual - ytdBudget : null}
                      ytdVarPct={(ytdBudget && ytdBudget > 0) ? ((ytdActual - ytdBudget) / ytdBudget) * 100 : null}
                      ytdSparkline={null}
                      onTotalRevenueClick={() => setTrdOpen(true)}
                    />
                  );
                })()}

                {/* Detailed Outlet Comparison — moved directly under Hero (Feb 2026 spec §3) */}
                <DetailedComparisonSection
                  outlets={os}
                  kind="mtd"
                  testId="mtd-detailed-comparison"
                />

                {/* Total Revenue drilldown modal */}
                <TotalRevenueDrilldownModal
                  open={trdOpen}
                  onClose={() => setTrdOpen(false)}
                  kind="mtd"
                  outlets={os}
                  periodLabel={format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}
                  heroActual={metrics.revenue}
                  heroTarget={metrics.budget}
                />

                {mtdData?.data_through && (
                  <p className="text-[11px] text-[#9CA3AF] mb-3 pl-1" data-testid="mtd-data-through">
                    Data through <span className="text-[color:var(--color-text-muted)] font-medium">{mtdData.data_through}</span> (excludes current day)
                  </p>
                )}

                {/* Revenue Performance */}
                {(() => {
                  const proj = _revenueChartV2({ mtdData, selectedYear, selectedMonth });
                  const mtdPt = mtdData?.portfolio_totals || {};
                  // Canonical YTD source for MTD view = executive-context.ytd
                  // (same source Daily uses). Falls back to full ytdData ONLY
                  // if it happens to be loaded (user visited YTD tab earlier).
                  const ytdPt = execContext?.ytd?.portfolio_totals || ytdData?.portfolio_totals || null;
                  return (
                    <RevenuePerformanceSection
                      testId="mtd-revenue-performance"
                      mtdSeries={proj.series}
                      projectedTotal={proj.projectedTotal}
                      budgetToDate={Number(mtdPt.budget || 0)}
                      mtdActual={Number(mtdPt.total_revenue || 0)}
                      fullMonthBudget={mtdPt.full_month_budget}
                      fullMonthBudgetAvailable={!!mtdPt.full_month_budget_available}
                      ytdActual={ytdPt ? Number(ytdPt.total_revenue || 0) : null}
                      ytdBudget={ytdPt ? Number(ytdPt.budget || 0) : null}
                      periodLabel={format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}
                    />
                  );
                })()}

                <FinancialSnapshotTable
                  testId="mtd-financial-snapshot"
                  rows={_buildFinancialSnapshotRowsV2(metrics)}
                />

                <RoomsAccommodationSection
                  testId="mtd-rooms-section"
                  mtdKpi={_aggregateRoomKPIs(mtdData?.outlets || [], 'mtd')}
                  ytdKpi={_aggregateRoomKPIs(ytdData?.outlets || [], 'mtd')}
                />

                <OutletPulseTable
                  testId="mtd-outlet-pulse-table"
                  rows={_outletPulseRowsV2(os, 'mtd')}
                />

                {(() => {
                  const u = _utilitiesRowsV2(costEfficiency);
                  return (
                    <UtilitiesEnergySection
                      testId="mtd-utilities-energy-v2"
                      rows={u.rows}
                      chartData={u.chart}
                    />
                  );
                })()}

                <MixDoughnutSection
                  foodPct={foodPct} bevPct={bevPct}
                  foodSale={totFood} bevSale={totBev}
                  lunchPct={lunchPct} dinnerPct={dinnerPct}
                  lunchSales={totLunch} dinnerSales={totDinner}
                  channelDineIn={totChDineIn}
                  channelSwiggy={totChSwiggy}
                  channelZomato={totChZomato}
                  channelOther={totChOther}
                  testId="mtd-mix-doughnut"
                />

                <PortfolioSignals
                  outlets={os}
                  kind="mtd"
                  portfolioTotals={pt}
                  costEfficiency={costEfficiency}
                  testId="mtd-portfolio-signals"
                />

                {/* Detailed Outlet Comparison moved to top (see spec §3) */}
              </>
            );
          })()}
        </TabsContent>
        {/* YTD Tab */}
        <TabsContent value="ytd">
          {ytdLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : ytdData ? (() => {
            const os = ytdData.outlets || [];
            const pt = ytdData.portfolio_totals || null;
            const totSales = pt ? pt.fb_sales : 0;
            const totBudget = pt ? pt.budget_fb : 0;
            const totRoomRev = pt ? pt.room_revenue : 0;
            const totRoomBudget = pt ? pt.budget_room : 0;
            const grandRevenue = pt ? pt.total_revenue : 0;
            const grandBudget = pt ? pt.budget : 0;
            const variance = pt ? pt.variance_amt : 0;
            const ytdTotPool = os.filter(countsTowardPortfolio);
            const reporting = pt ? (pt.outlets_reporting || 0) : 0;
            const totFood = pt ? pt.food_sale : 0;
            const totBev = pt ? pt.beverage_sale : 0;
            const totCovers = pt ? pt.covers : 0;
            const foodPct = pt ? pt.food_pct_mix : null;
            const bevPct = pt ? pt.bev_pct_mix : null;
            const foodApc = totCovers > 0 ? (totFood / totCovers) : null;
            const bevApc = totCovers > 0 ? (totBev / totCovers) : null;
            const hasRooms = totRoomRev > 0;
            return (
              <>
                {/* FY scope badge */}
                <div className="flex items-center mb-3">
                  <Badge variant="outline" className="border-[#E2E8DF] text-[color:var(--color-text-muted)]" data-testid="ytd-fy-badge">
                    FY: {ytdData.fy_start} to {ytdData.fy_end}
                  </Badge>
                </div>

                {/* KPI Row */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <MetricCard
                    title={hasRooms ? 'Total Revenue (incl. Rooms)' : 'YTD Sales'}
                    value={fmt(grandRevenue)}
                    subtitle={hasRooms ? `F&B ${fmt(totSales)} • Rooms ${fmt(totRoomRev)}` : undefined}
                    icon={DollarSign}
                    trend={variance >= 0 ? 'up' : 'down'}
                    testId="ytd-grand-total-card" />
                  <MetricCard title="YTD Budget" value={fmt(grandBudget)} icon={Target} testId="ytd-total-budget-card" />
                  <MetricCard title="Variance"
                    value={`${variance >= 0 ? '+' : ''}${fmt(variance)}`}
                    icon={variance >= 0 ? TrendingUp : TrendingDown}
                    trend={variance >= 0 ? 'up' : 'down'}
                    testId="ytd-total-variance-card" />
                  <MetricCard title="Outlets Reporting"
                    value={`${reporting} / ${ytdTotPool.length}`}
                    icon={CalendarIcon}
                    testId="ytd-outlets-reporting-card" />
                </div>

                {/* Hidden legacy testid for backward-compat */}
                <span className="sr-only" data-testid="ytd-grand-total">{fmt(grandRevenue)}</span>

                {/* 1) PRIMARY: All Outlets at a Glance */}
                <Card className="border-[#E2E8DF] shadow-sm mb-4 overflow-hidden">
                  <CardContent className="p-0">
                    <YtdOutletTable ytdData={ytdData} />
                  </CardContent>
                </Card>

                {/* 2) Product Mix + Service Split */}
                <MixSplitTable
                  foodPct={foodPct} bevPct={bevPct}
                  foodSale={totFood} bevSale={totBev}
                  foodApc={foodApc} bevApc={bevApc}
                  lunchPct={null} dinnerPct={null}
                  lunchSales={0} dinnerSales={0}
                  lunchCovers={0} dinnerCovers={0}
                  testId="ytd-mix-split-table"
                />

                {/* 3) Individual Outlet Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {os.map(o => (
                    <AggregateOutletCard
                      key={o.outlet_id}
                      outlet={o}
                      sales={o.ytd_sales}
                      budget={o.ytd_budget}
                      foodCostPct={o.avg_food_cost_pct}
                      bevCostPct={o.avg_beverage_cost_pct}
                      covers={o.total_covers || 0}
                      totalApc={o.total_apc}
                      roomRevenue={o.total_room_revenue}
                      occupancy={o.avg_occupancy_pct}
                      arr={o.avg_arr}
                      daysReported={o.days_reported}
                      label="YTD"
                      testId={`ytd-outlet-card-${o.outlet_id}`} />
                  ))}
                </div>

                {/* 4) Data Health intentionally removed from YTD tab — see InsightStripCards in Daily/MTD for the new executive-grade insight blocks. */}
              </>
            );
          })() : (
            <p className="text-center text-[color:var(--color-text-muted)] py-10">No YTD data available</p>
          )}
        </TabsContent>

        {/* Range Tab */}
        <TabsContent value="range">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex gap-2">
              {[
                { key: 'last7', label: 'Last 7 Days' },
                { key: 'last30', label: 'Last 30 Days' },
                { key: 'lastMonth', label: 'Last Month' },
                { key: 'custom', label: 'Custom' },
              ].map(p => (
                <Button key={p.key} variant={rangePreset === p.key ? 'default' : 'outline'} size="sm"
                  onClick={() => applyRangePreset(p.key)}
                  className={rangePreset === p.key
                    ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]'
                    : 'border-[#E2E8DF] text-[color:var(--color-text-muted)] hover:bg-[#F3F5F2]'}
                  data-testid={`range-preset-${p.key}`}>
                  {p.label}
                </Button>
              ))}
            </div>
            {rangePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <Popover open={rangeStartOpen} onOpenChange={setRangeStartOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[#E2E8DF] text-[color:var(--color-text-primary)]" data-testid="range-start-picker">
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[color:var(--color-text-muted)]" />
                      {rangeStart ? format(rangeStart, 'dd MMM yyyy') : 'Start'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={rangeStart}
                      onSelect={d => { if (d) { setRangeStart(d); setRangeStartOpen(false); } }}
                      disabled={d => d > new Date()} />
                  </PopoverContent>
                </Popover>
                <span className="text-[color:var(--color-text-muted)] text-sm">to</span>
                <Popover open={rangeEndOpen} onOpenChange={setRangeEndOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[#E2E8DF] text-[color:var(--color-text-primary)]" data-testid="range-end-picker">
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[color:var(--color-text-muted)]" />
                      {rangeEnd ? format(rangeEnd, 'dd MMM yyyy') : 'End'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={rangeEnd}
                      onSelect={d => { if (d) { setRangeEnd(d); setRangeEndOpen(false); } }}
                      disabled={d => d > new Date()} />
                  </PopoverContent>
                </Popover>
              </div>
            )}
            {rangeStart && rangeEnd && (
              <Badge variant="outline" className="border-[#E2E8DF] text-[color:var(--color-text-muted)]" data-testid="range-label">
                {format(rangeStart, 'dd MMM')} - {format(rangeEnd, 'dd MMM yyyy')}
              </Badge>
            )}
          </div>

          {!rangeStart || !rangeEnd ? (
            <p className="text-center text-[color:var(--color-text-muted)] py-10">Select a date range to view data</p>
          ) : rangeLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : rangeData ? (
            <>
              <Card className="border-[#E2E8DF] shadow-sm mb-6 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-[color:var(--color-text-primary)]">
                      <thead>
                        <tr className="text-xs uppercase tracking-[0.05em] text-[color:var(--color-text-muted)] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                          <th className="px-4 py-3 font-medium">Outlet</th>
                          <th className="px-4 py-3 font-medium text-right">Total Sales</th>
                          <th className="px-4 py-3 font-medium text-right">Budget</th>
                          <th className="px-4 py-3 font-medium text-right">Variance</th>
                          <th className="px-4 py-3 font-medium text-right">Avg Daily</th>
                          <th className="px-4 py-3 font-medium text-right">FC %</th>
                          <th className="px-4 py-3 font-medium text-right">BC %</th>
                          <th className="px-4 py-3 font-medium text-right">Covers</th>
                          <th className="px-4 py-3 font-medium text-right">Total APC</th>
                          <th className="px-4 py-3 font-medium text-right">Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rangeData.outlets?.map((o, i) => {
                          const variance = o.total_sales - o.total_budget;
                          const variancePct = o.total_budget > 0 ? (variance / o.total_budget * 100).toFixed(1) : '0.0';
                          return (
                          <tr key={o.outlet_id} className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                            data-testid={`range-row-${o.outlet_id}`}>
                            <td className="px-4 py-3 font-medium">{o.outlet_name}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmt(o.total_sales)}</td>
                            <td className="px-4 py-3 text-right">{fmt(o.total_budget)}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-medium ${variance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                                {variance >= 0 ? '+' : ''}{fmt(variance)} ({variancePct}%)
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">{fmt(o.avg_daily_sales)}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={o.food_cost_pct != null && o.food_cost_pct > 35 ? 'text-[#C45A41] font-medium' : ''}>{o.food_cost_pct != null ? `${o.food_cost_pct}%` : 'Pending'}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={o.beverage_cost_pct != null && o.beverage_cost_pct > 30 ? 'text-[#C45A41] font-medium' : ''}>{o.beverage_cost_pct != null ? `${o.beverage_cost_pct}%` : 'Pending'}</span>
                            </td>
                            <td className="px-4 py-3 text-right">{o.total_covers > 0 ? o.total_covers : '—'}</td>
                            <td className="px-4 py-3 text-right">{o.total_apc > 0 ? fmt(o.total_apc) : '—'}</td>
                            <td className="px-4 py-3 text-right">{o.days_reported}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {rangeData.daily_trend?.length > 0 && (
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-[color:var(--color-text-primary)]">Daily Sales Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={rangeData.daily_trend.map(d => ({
                        date: d.date.slice(5),
                        sales: d.total_net_sales
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DF" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#5C6B64' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#5C6B64' }} />
                        <Tooltip contentStyle={{ border: '1px solid #E2E8DF', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(value) => [fmt(value), 'Net Sales']} />
                        <Line type="monotone" dataKey="sales" stroke={CHART_COLORS[0]} strokeWidth={2}
                          dot={{ fill: CHART_COLORS[0], r: 3 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <p className="text-center text-[color:var(--color-text-muted)] py-10">No data for selected range</p>
          )}
        </TabsContent>

      </Tabs>

      <ReportExportModal open={reportModalOpen} onClose={() => setReportModalOpen(false)} portfolioId={portfolioId} />
    </div>
  );
}
